import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Form from 'react-bootstrap/Form';
import Navbar from 'react-bootstrap/Navbar';
import Container from 'react-bootstrap/Container';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import Modal from 'react-bootstrap/Modal';
import DataTable from './components/DataTable';
import AnalyticsDataTable from './components/AnalyticsDataTable';
import FootballDataTable from './components/FootballDataTable';
import BasketballDataTable from './components/BasketballDataTable';
import MobileBasketballDataTable from './components/MobileBasketballDataTable';
import NHLDataTable from './components/NHLDataTable';
import NCAAFDataTable from './components/NCAAFDataTable';
import NCAABDataTable from './components/NCAABDataTable';
import H2HDataTable from './components/H2HDataTable';
import NFLDataTable from './components/NFLDataTable';
import MobileNFLDataTable from './components/MobileNFLDataTable';
import SoccerDataTable from './components/SoccerDataTable';
import BestBetsModal from './components/BestBetsModal';
import BestBetsPanel from './components/BestBetsPanel';
import GamePanel from './components/GamePanel';
import PlayerModal from './components/PlayerModal';
import NFLPlayerModal from './components/NFLPlayerModal';
import NHLPlayerModal from './components/NHLPlayerModal';
import NBAPlayerModal from './components/NBAPlayerModal';
import WNBAPlayerModal from './components/WNBAPlayerModal';
import SoccerPlayerModal from './components/SoccerPlayerModal';
import DebugOverlay from './components/DebugOverlay';
import SettingsTakeover from './components/SettingsTakeover';
import EdgeBlocks from './components/EdgeBlocks';
import UserMenu from './components/UserMenu';
import AuthWrapper from './components/AuthWrapper';
import LegalDocument from './components/LegalDocument';
import ContactModal from './components/ContactModal';
import BestBetsFaqModal from './components/BestBetsFaqModal';
import BetslipFloatingButton from './components/BetslipFloatingButton';
import ScratchpadFloatingButton from './components/ScratchpadFloatingButton';
import BetslipModal from './components/BetslipModal';
import PlayerContextMenu from './components/PlayerContextMenu';
import ScratchpadPanel from './components/ScratchpadPanel';
import AddToScratchpadLink from './components/AddToScratchpadLink';
import PlayerOddsTable from './components/PlayerOddsTable';
import PlayerOddsChart from './components/PlayerOddsChart';
import MatchupsPanel from './components/MatchupsPanel';
import PlayerStatsChart from './components/PlayerStatsChart';
import AnnouncementsModal from './components/AnnouncementsModal';
import DevPanel from './components/DevPanel';
import TrendsFirstBasketReport from './components/TrendsFirstBasketReport';
import TrendsDoubleDoubleReport from './components/TrendsDoubleDoubleReport';
import TrendsPropReport from './components/TrendsPropReport';
import './utils/apiClient'; // Initialize fetch interceptor
import { WhopCheckoutEmbed } from '@whop/checkout/react';
import { useGamesData } from './hooks/useGamesData';
import { useOddsData } from './hooks/useOddsData';
import { useAnalyticsData } from './hooks/useAnalyticsData';
import { useNcaafCalendar } from './hooks/useNcaafCalendar';
import { useNcaafGames } from './hooks/useNcaafGames';
import { useNflCalendar } from './hooks/useNflCalendar';
import { useNflGames } from './hooks/useNflGames';
import { useAnalyticsGamesData } from './hooks/useAnalyticsGamesData';
import { useDarkMode } from './contexts/DarkModeContext';
import { useBooks } from './contexts/BooksContext';
import { useAuth } from './contexts/AuthContext';
import { AVAILABLE_MARKETS, BOOKMAKERS_LIST, getMarketsForSport, getDefaultMarketForSport, isGameFinished } from './utils/constants';
import { getTodayString, formatPlayerName, getTeamLogoUrl, getBookLogoUrl, getPlayerHeadshotUrl } from './utils/formatters';
import { SPORTS_NAV } from './utils/sportsNav';
import { calculateBestBets, isBinaryMarket } from './utils/oddsHelpers';
import { shouldShowTiers, hasDropdown } from './utils/marketConfig';
import { isDevModeEnabled } from './utils/devMode';
import { getWNBATeamLogoUrl } from './utils/wnbaHelpers';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

// Build playerId -> currentTeam map from /today payload for the visible game.
// Supports object-keyed (`{ ID123: {...} }`) and array roster shapes.
function buildTrendsRosterMap(todayPayload, visibleGame) {
  if (!todayPayload || !visibleGame) return null;
  const games = Array.isArray(todayPayload) ? todayPayload : todayPayload.games;
  if (!Array.isArray(games)) return null;
  const game = games.find(g => (
    g.gameIDmlb === visibleGame.gameIDmlb ||
    g.gameIDwnba === visibleGame.gameIDmlb ||
    g.id === visibleGame.gameIDmlb ||
    (g.away === visibleGame.away && g.home === visibleGame.home)
  ));
  if (!game) return null;
  const map = new Map();
  const addRoster = (roster, teamAbbrev) => {
    if (!roster || !teamAbbrev) return;
    if (Array.isArray(roster)) {
      roster.forEach(p => {
        const id = String(p?.playerId ?? p?.id ?? '');
        if (id) map.set(id, teamAbbrev);
      });
    } else if (typeof roster === 'object') {
      Object.entries(roster).forEach(([key, val]) => {
        const rawId = (typeof val === 'object' && val !== null && (val.playerId ?? val.id)) ||
          (key.startsWith('ID') ? key.slice(2) : key);
        const id = String(rawId);
        if (id) map.set(id, teamAbbrev);
      });
    }
  };
  addRoster(game.awayPlayers ?? game.awayRoster, game.away ?? visibleGame.away);
  addRoster(game.homePlayers ?? game.homeRoster, game.home ?? visibleGame.home);
  return map.size > 0 ? map : null;
}

function App() {
  const isMobile = window.innerWidth <= 768;
  const { isDarkMode } = useDarkMode();
  const { getEnabledBooks, getEnabledBooksForBestBets, enabledBooksForBestBets, bookOrder, recalcEdgeOdds, recalcEdgeBestBets, recalcEdgePlayer, bestBetsMinOdds, bestBetsMaxOdds, bestBetsUseGlobalOdds, bestBetsOddsBySport, getBestBetsOddsRange } = useBooks();
  const { isAuthenticated, user, login, logout, checkAuth, isLoading: authLoading, activeSub, availablePlans, environment, announcements, betslip, gift } = useAuth();

  // Send user data to Meta Pixel when authenticated
  useEffect(() => {
    if (isAuthenticated && user && window.fbq) {
      window.fbq('init', '1266747342086742', {
        em: user.email || undefined,
        external_id: user.username || undefined,
      });
      window.fbq('trackCustom', 'UserIdentified', {
        subscription_status: activeSub?.status || 'none',
        subscription_tier: activeSub?.tier || 'none',
        subscription_valid: activeSub?.valid || false,
      });
    }
  }, [isAuthenticated, user, activeSub]);

  // Check if we're in test mode (environment will be 'test' for internal IPs only)
  const isTestMode = environment === 'test';

  // Check if user has an active subscription
  // Note: past_due users are treated as free tier (no access) until payment is resolved
  const hasActiveSubscription = !!(isAuthenticated && activeSub?.valid && (activeSub?.status === 'active' || activeSub?.status === 'trialing' || activeSub?.status === 'completed' || activeSub?.status === 'drafted'));
  // Unified "can access data" flag: subscription OR gift access. Use this
  // for data-loading gates (odds fetch, "loading games" placeholders, etc.)
  // rather than `hasActiveSubscription`, which is billing-specific.
  const hasAccess = hasActiveSubscription || gift;

  // Redirect users without an active plan to /lets-go (skip if gift access)
  useEffect(() => {
    if (!authLoading && !hasActiveSubscription && !gift && window.location.pathname !== '/lets-go') {
      window.location.href = '/lets-go';
    }
  }, [authLoading, hasActiveSubscription, gift]);

  // Check if betslip feature is enabled for this user
  const isBetslipEnabled = betslip === true;

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImageIndex, setLightboxImageIndex] = useState(0);

  // Checkout state
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null); // 'free' or 'paid'
  const [checkoutStatus, setCheckoutStatus] = useState(null); // null | 'processing' | 'success' | 'timeout'
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showBestBets, setShowBestBets] = useState(false);
  const [showBestBetsPanel, setShowBestBetsPanel] = useState(false);
  const [showBestBetsFaq, setShowBestBetsFaq] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showAnnouncementsModal, setShowAnnouncementsModal] = useState(false);
  const [showBetslipIframe, setShowBetslipIframe] = useState(false);
  const [hasNewAnnouncements, setHasNewAnnouncements] = useState(false);
  const [legalDocument, setLegalDocument] = useState(null); // 'terms' | 'privacy' | 'subscription' | 'disclaimers'
  const [aboutSectionExpanded, setAboutSectionExpanded] = useState(false); // For mobile about section toggle - default collapsed
  const [displayDate] = useState(getTodayString());
  const [selectedDate, setSelectedDate] = useState(getTodayString());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateExplicitlySelected, setDateExplicitlySelected] = useState(false); // Track if user picked a date
  const [selectedSport, setSelectedSport] = useState('nba');
  const [analyticsMode, setAnalyticsMode] = useState(false);
  const [analyticsType, setAnalyticsType] = useState('matchups'); // 'matchups' or 'performance'
  const [globalSelectedYear, setGlobalSelectedYear] = useState('alltime'); // Global year selection for analytics
  const [nbaViewMode, setNbaViewMode] = useState('bestline'); // 'bestline' for Best Line view, 'ou' for O/U view
  const [nflViewMode, setNflViewMode] = useState('ou'); // 'bestline' for Best Line view, 'ou' for O/U view
  const [showIqOverlay, setShowIqOverlay] = useState(false); // IQ overlay for NBA
  const [showBetslipPanel, setShowBetslipPanel] = useState(false); // Betslip panel for NBA
  // When both right-side panels are open, this picks which one is visible.
  // Updated whenever a panel is toggled on so the freshly-opened one wins.
  const [activeRightPanel, setActiveRightPanel] = useState('betslip');
  const [showMatchupsPanel, setShowMatchupsPanel] = useState(false); // Matchups panel for MLB
  const [iqData, setIqData] = useState(null); // Data for IQ overlay
  const [iqLoading, setIqLoading] = useState(false); // Loading state for IQ data
  const [showMobileTrends, setShowMobileTrends] = useState(false); // Mobile trends overlay
  const [trendsYear, setTrendsYear] = useState('2026'); // Season year for trends data
  const [showTrendsExpanded, setShowTrendsExpanded] = useState(false); // Expanded trends iframe panel
  const [showNegCorrOverlay, setShowNegCorrOverlay] = useState(false); // NFL negative-correlation iframe overlay
  const [showAutoParlaysOverlay, setShowAutoParlaysOverlay] = useState(false); // Soccer auto-parlays iframe overlay
  const [showLeadersOverlay, setShowLeadersOverlay] = useState(false); // NBA daily leaders iframe overlay
  const [trendsTodayGames, setTrendsTodayGames] = useState(null); // /today payload — used to map visible-game current rosters for the trends panel
  const [visibleGameId, setVisibleGameId] = useState(null); // Track which game is visible in viewport
  const gameElementsRef = useRef(new Map()); // Map of gameId -> DOM element
  const marketBannerRef = useRef(null); // Ref for market banner to get its bottom position
  const [marketBannerBottom, setMarketBannerBottom] = useState(140); // Default fallback


  // Debug: Log whenever nbaViewMode changes
  useEffect(() => {
    console.log('NBA View Mode changed to:', nbaViewMode);
  }, [nbaViewMode]);

  // Debug: Log whenever nflViewMode changes
  useEffect(() => {
    console.log('NFL View Mode changed to:', nflViewMode);
  }, [nflViewMode]);

  const [masterMarket, setMasterMarket] = useState(null);
  const [masterShowTiers, setMasterShowTiers] = useState(false);
  const [marketTierPreferences, setMarketTierPreferences] = useState(() => {
    // Load saved tier preferences per market from localStorage
    const saved = localStorage.getItem('marketTierPreferences');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Error parsing saved tier preferences:', e);
      }
    }
    return {}; // Default to empty object
  });
  const [gameShowTiers, setGameShowTiers] = useState({}); // Track per-game tier visibility
  const [masterTierFilter, setMasterTierFilter] = useState(null); // Filter to specific tier value
  const [showMobileMenu, setShowMobileMenu] = useState(false); // Mobile menu state
  const [userSelectedMarkets, setUserSelectedMarkets] = useState({}); // Track which games have user-specific selections
  const [gameCollapseStates, setGameCollapseStates] = useState({}); // Track collapse state for each game on mobile
  const [enabledConferences, setEnabledConferences] = useState(new Set()); // Track enabled conferences for NCAAB filtering
  const [enabledLeagues, setEnabledLeagues] = useState(new Set()); // Track enabled leagues for soccer filtering
  const [top25FilterEnabled, setTop25FilterEnabled] = useState(false); // Track Top25 filter for NCAAB
  const [showConferencePopup, setShowConferencePopup] = useState(false); // Mobile conference selector popup
  const [enabledNcaafTimeSlots, setEnabledNcaafTimeSlots] = useState(new Set()); // Selected NCAAF start-time badges (keys like "19:00")
  const ncaafHasInitedTimeSlots = useRef(false); // Tracks whether the NCAAF time-slot filter has been populated once; before then the filter is a no-op.

  // Player modal state
  const [showPlayerModal, setShowPlayerModal] = useState(false);
  const [playerModalData, setPlayerModalData] = useState({
    gameId: null,
    playerId: null,
    playerName: null,
    market: null,
    awayTeam: null,
    homeTeam: null,
    playerTeam: null,
    playerPosition: null
  });

  // Player detail panel state (desktop)
  const [showPlayerPanel, setShowPlayerPanel] = useState(false);
  const [playerPanelData, setPlayerPanelData] = useState({
    gameId: null,
    playerId: null,
    playerName: null,
    market: null,
    awayTeam: null,
    homeTeam: null,
    playerTeam: null,
    playerPosition: null,
    point: null
  });
  const [playerPanelFull, setPlayerPanelFull] = useState(false);
  const [playerPanelView, setPlayerPanelView] = useState('table'); // 'table' | 'chart'
  const [playerPanelChartParams, setPlayerPanelChartParams] = useState(null); // { marketKey, point, isOver }
  const [playerPanelActiveMarketKey, setPlayerPanelActiveMarketKey] = useState(null);
  const [playerPanelLineValue, setPlayerPanelLineValue] = useState(null);
  const [playerPanelHandInfo, setPlayerPanelHandInfo] = useState(null);
  const handlePlayerPanelInfo = useCallback((info) => setPlayerPanelHandInfo(info), []);
  const playerPanelChartRef = useRef(null);
  const [playerPanelGameLogNode, setPlayerPanelGameLogNode] = useState(null);

  // Game panel state (NCAAB game detail panel, desktop)
  const [showGamePanel, setShowGamePanel] = useState(false);
  const [gamePanelGameId, setGamePanelGameId] = useState(null);

  // Betslip state
  const [betslipItems, setBetslipItems] = useState([]);
  const [showBetslipModal, setShowBetslipModal] = useState(false);
  const [clickedCellId, setClickedCellId] = useState(null);
  // Mobile-only visual confirmation toast when a bet is added. Desktop already
  // shows the panel + count on the button so the toast is redundant there.
  const [betslipToast, setBetslipToast] = useState(null); // { message, kind: 'added' | 'dup' } | null
  const betslipToastTimerRef = useRef(null);

  // Player context menu state
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, playerData: null });

  // Scratchpad state — persisted to localStorage AND synced to the API server
  // (GET /api/users/me/scratchpad on auth, PUT on every change). The server
  // applies a 2-day rolling window filter (today + yesterday in Eastern), so
  // older items returned by the GET are silently dropped server-side.
  // Each item is { playerId, name, gameId, teamAbbr, ... }. Dedup'd by
  // playerId+gameId.
  const [scratchpadItems, setScratchpadItems] = useState(() => {
    try {
      const saved = localStorage.getItem('scratchpadItems');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [showScratchpadPanel, setShowScratchpadPanel] = useState(false);
  // Scratchpad-filter toggle — when on, the odds screen for the current sport
  // is restricted to today's scratchpad players. Sport-specific: sports with
  // no items on the scratchpad today fall through to the unfiltered view.
  const [scratchpadFilterEnabled, setScratchpadFilterEnabled] = useState(() => {
    try { return localStorage.getItem('scratchpadFilterEnabled') === '1'; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('scratchpadFilterEnabled', scratchpadFilterEnabled ? '1' : '0'); }
    catch { /* ignore quota errors */ }
  }, [scratchpadFilterEnabled]);
  // Suppresses the PUT effect immediately after we hydrate from the server,
  // so the freshly-loaded items aren't echoed straight back.
  const skipScratchpadSyncRef = useRef(false);
  // Tracks the last set of items the server confirmed it has. Used to compute
  // the add/remove diff so we can merge concurrent server-side changes (from
  // other tabs / devices) without clobbering them. Persisted to localStorage
  // so that after a reload the diff knows which items were *previously* on
  // the server — without that history, items locally re-appearing after the
  // server cleared them would look like fresh local adds and get re-pushed.
  const lastSyncedScratchpadRef = useRef(null);
  if (lastSyncedScratchpadRef.current === null) {
    let initial = [];
    try {
      const saved = localStorage.getItem('scratchpadLastSynced');
      if (saved) {
        initial = JSON.parse(saved);
      } else {
        // First run on the new sync code (legacy users). Seed lastSynced with
        // whatever the user already has in localStorage so that the upcoming
        // load's diff treats those items as "previously known to be on the
        // server" — not as fresh adds to re-push. Items the server actually
        // doesn't have anymore (cleared from another device) will then fall
        // into remoteRemoved and get dropped instead of resurrected.
        try { initial = JSON.parse(localStorage.getItem('scratchpadItems') || '[]'); } catch {}
        try { localStorage.setItem('scratchpadLastSynced', JSON.stringify(initial)); } catch {}
      }
    } catch {}
    lastSyncedScratchpadRef.current = initial;
  }
  const persistLastSyncedScratchpad = (items) => {
    try {
      localStorage.setItem('scratchpadLastSynced', JSON.stringify(items));
    } catch { /* ignore quota errors */ }
  };
  useEffect(() => {
    try {
      localStorage.setItem('scratchpadItems', JSON.stringify(scratchpadItems));
    } catch { /* ignore quota errors */ }
  }, [scratchpadItems]);

  // Hydrate from the server once the user is authenticated. Performs the same
  // 3-way merge as the sync effect using the persisted lastSynced snapshot:
  //   - items in lastSynced but missing from server  → removed elsewhere → drop
  //   - items in local but missing from lastSynced   → unsynced local adds → keep
  //   - items in server but missing from lastSynced  → added elsewhere   → adopt
  // Without lastSynced as a tiebreaker, a "device A cleared everything" event
  // is indistinguishable from "server never had anything", and B's local
  // items would get re-pushed every reload.
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/users/me/scratchpad', { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        if (Array.isArray(data?.items)) {
          const serverItems = data.items;
          const serverUpdatedAtMs = data?.updatedAt ? Date.parse(data.updatedAt) : null;
          let localItems = [];
          try { localItems = JSON.parse(localStorage.getItem('scratchpadItems') || '[]'); } catch {}
          const lastSynced = lastSyncedScratchpadRef.current || [];
          const keyOf = (it) => `${it.playerId}|${it.gameId}`;
          const lastKeys = new Set(lastSynced.map(keyOf));
          const serverKeys = new Set(serverItems.map(keyOf));
          // Removed-by-another-device (per the persisted lastSynced snapshot):
          // items we last saw on the server but aren't there anymore.
          const remoteRemovedKeys = new Set(
            lastSynced.filter(it => !serverKeys.has(keyOf(it))).map(keyOf)
          );
          // Unsynced local adds: in local but not in lastSynced.
          let localAdded = localItems.filter(it => !lastKeys.has(keyOf(it)));
          // Server-updatedAt tiebreaker: if we have no lastSynced history
          // (e.g. very first load after deploy) but the server has a known
          // last-write time, any local item created BEFORE that time and
          // missing from the server must have been removed upstream — not a
          // fresh local add. Drop those to avoid resurrecting deleted items.
          if (serverUpdatedAtMs != null) {
            localAdded = localAdded.filter(it => {
              if (serverKeys.has(keyOf(it))) return true; // server still has it
              const addedAtMs = Number(it.addedAt) || 0;
              return addedAtMs > serverUpdatedAtMs;
            });
          }
          // Merge: server's set, minus what we know was deleted remotely,
          //        plus our unsynced local adds.
          const mergedMap = new Map();
          for (const it of serverItems) mergedMap.set(keyOf(it), it);
          for (const k of remoteRemovedKeys) mergedMap.delete(k);
          for (const it of localAdded) mergedMap.set(keyOf(it), it);
          const merged = Array.from(mergedMap.values());

          lastSyncedScratchpadRef.current = serverItems;
          persistLastSyncedScratchpad(serverItems);

          const mergedKeys = new Set(merged.map(keyOf));
          const serverMatchesMerged =
            serverKeys.size === mergedKeys.size &&
            [...serverKeys].every(k => mergedKeys.has(k));
          if (serverMatchesMerged) {
            // No local-only diff to push — let the upcoming state change skip
            // the next sync round-trip.
            skipScratchpadSyncRef.current = true;
          }
          setScratchpadItems(merged);
        }
      } catch (err) {
        console.warn('[scratchpad] load failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  // Tracks the latest isAuthenticated value so the sync effect (deps:
  // [scratchpadItems]) can gate on auth without having auth in its deps —
  // otherwise the auth flip on refresh would fire the sync in parallel with
  // the load effect, causing redundant GET/PUTs.
  const isAuthenticatedRef = useRef(isAuthenticated);
  useEffect(() => { isAuthenticatedRef.current = isAuthenticated; }, [isAuthenticated]);

  // On every local change: GET the server's latest, compute the diff from our
  // last-synced snapshot, merge our adds/removes onto the server's state, then
  // PUT the merged result. This preserves changes another tab/device made
  // since we last synced rather than overwriting them.
  useEffect(() => {
    if (!isAuthenticatedRef.current) return;
    if (skipScratchpadSyncRef.current) {
      skipScratchpadSyncRef.current = false;
      return;
    }
    const controller = new AbortController();
    const keyOf = (it) => `${it.playerId}|${it.gameId}`;
    (async () => {
      try {
        // 1. Pull latest server state so we don't clobber concurrent edits.
        const getRes = await fetch('/api/users/me/scratchpad', {
          credentials: 'include',
          signal: controller.signal,
        });
        const serverItems = getRes.ok ? ((await getRes.json())?.items || []) : [];
        if (controller.signal.aborted) return;

        // 2. Compute what *this client* added / removed since the last sync.
        const localItems = scratchpadItems;
        const lastSynced = lastSyncedScratchpadRef.current || [];
        const lastKeys = new Set(lastSynced.map(keyOf));
        const localKeys = new Set(localItems.map(keyOf));
        const added = localItems.filter(it => !lastKeys.has(keyOf(it)));
        const removedKeys = new Set(lastSynced.filter(it => !localKeys.has(keyOf(it))).map(keyOf));

        // 3. Apply our diff onto the server's current set, preferring our
        //    own row when keys collide (so re-adds include updated metadata).
        const mergedMap = new Map();
        for (const it of serverItems) {
          if (!removedKeys.has(keyOf(it))) mergedMap.set(keyOf(it), it);
        }
        for (const it of added) {
          mergedMap.set(keyOf(it), it);
        }
        const merged = Array.from(mergedMap.values());

        // 4. Push merged result to the server.
        const putRes = await fetch('/api/users/me/scratchpad', {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: merged }),
          signal: controller.signal,
        });
        if (!putRes.ok || controller.signal.aborted) return;

        // 5. Update last-synced snapshot and, if merge produced items the
        //    local state doesn't know about, sync them in (suppressing the
        //    next PUT echo).
        lastSyncedScratchpadRef.current = merged;
        persistLastSyncedScratchpad(merged);
        const localKeySet = new Set(localItems.map(keyOf));
        const sawNew = merged.some(it => !localKeySet.has(keyOf(it)));
        if (sawNew) {
          skipScratchpadSyncRef.current = true;
          setScratchpadItems(merged);
        }
      } catch (err) {
        if (err?.name !== 'AbortError') {
          console.warn('[scratchpad] sync failed:', err);
        }
      }
    })();
    return () => controller.abort();
  }, [scratchpadItems]);

  // Close sport-specific panels when switching sports
  useEffect(() => {
    setShowPlayerPanel(false);
    setShowGamePanel(false);
    setShowIqOverlay(false);
  }, [selectedSport]);

  // Track unique MLB visit days for promo banner
  useEffect(() => {
    if (selectedSport === 'mlb') {
      const today = new Date().toISOString().slice(0, 10);
      const lastDay = localStorage.getItem('mlb_last_visit_day');
      if (lastDay !== today) {
        const days = parseInt(localStorage.getItem('mlb_visit_days') || '0');
        localStorage.setItem('mlb_visit_days', String(days + 1));
        localStorage.setItem('mlb_last_visit_day', today);
      }
    }
  }, [selectedSport]);

  // Track unique NFL visit days for TUDDY promo banner (same shape as MLB).
  useEffect(() => {
    if (selectedSport === 'nfl') {
      const today = new Date().toISOString().slice(0, 10);
      const lastDay = localStorage.getItem('nfl_last_visit_day');
      if (lastDay !== today) {
        const days = parseInt(localStorage.getItem('nfl_visit_days') || '0');
        localStorage.setItem('nfl_visit_days', String(days + 1));
        localStorage.setItem('nfl_last_visit_day', today);
      }
    }
  }, [selectedSport]);

  // Track unique NBA visit days for BUCKETS promo banner (same shape as MLB).
  useEffect(() => {
    if (selectedSport === 'nba') {
      const today = new Date().toISOString().slice(0, 10);
      const lastDay = localStorage.getItem('nba_last_visit_day');
      if (lastDay !== today) {
        const days = parseInt(localStorage.getItem('nba_visit_days') || '0');
        localStorage.setItem('nba_visit_days', String(days + 1));
        localStorage.setItem('nba_last_visit_day', today);
      }
    }
  }, [selectedSport]);

  // Track unique NHL visit days for SLAPSHOT promo banner (same shape as MLB).
  useEffect(() => {
    if (selectedSport === 'nhl') {
      const today = new Date().toISOString().slice(0, 10);
      const lastDay = localStorage.getItem('nhl_last_visit_day');
      if (lastDay !== today) {
        const days = parseInt(localStorage.getItem('nhl_visit_days') || '0');
        localStorage.setItem('nhl_visit_days', String(days + 1));
        localStorage.setItem('nhl_last_visit_day', today);
      }
    }
  }, [selectedSport]);

  // Disable body scroll when best bets panel is open on desktop
  useEffect(() => {
    if (showBestBetsPanel && !isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [showBestBetsPanel, isMobile]);

  // Get sport-specific markets
  const availableMarkets = getMarketsForSport(selectedSport);
  const defaultMarket = getDefaultMarketForSport(selectedSport);
  
  // Debug NFL markets
  if (selectedSport === 'nfl') {
  }

  // For NBA/WNBA/NHL/NCAAB/Soccer: use null (fetch /next) when user hasn't explicitly selected a date.
  // MLB defaults to a pinned date (10/28/2026) instead of /next.
  // For other sports: always use selectedDate.
  let dateForFetching;
  if (selectedSport === 'mlb' && !dateExplicitlySelected) {
    dateForFetching = '10/28/2026';
  } else if ((selectedSport === 'nba' || selectedSport === 'wnba' || selectedSport === 'nhl' || selectedSport === 'ncaab' || selectedSport === 'soccer') && !dateExplicitlySelected) {
    dateForFetching = null;
  } else {
    dateForFetching = selectedDate;
  }

  // Use custom hooks
  const { games, gamesMetadata, refetchGames } = useGamesData(dateForFetching, selectedSport);
  
  // Separate games data for analytics (doesn't refresh)
  const { analyticsGames, analyticsGamesMetadata } = useAnalyticsGamesData(selectedDate, selectedSport, analyticsMode);
  
  // NCAAF calendar hook (only fetch when NCAAF is selected)
  const {
    calendarData,
    currentWeek,
    selectedWeek,
    setSelectedWeek,
    getCurrentWeekData,
    getSelectedWeekData,
    isLoading: calendarLoading,
    error: calendarError
  } = useNcaafCalendar(selectedSport);

  // NCAAF games hook (fetch games for selected week)
  const {
    games: ncaafGames,
    gamesMetadata: ncaafGamesMetadata,
    isLoading: ncaafGamesLoading,
    error: ncaafGamesError,
    refetchGames: refetchNcaafGames
  } = useNcaafGames(selectedSport, selectedWeek);

  // NFL calendar hook (only fetch when NFL is selected)
  const {
    calendarData: nflCalendarData,
    currentWeek: nflCurrentWeek,
    selectedWeek: nflSelectedWeek,
    setSelectedWeek: setNflSelectedWeek,
    getCurrentWeekData: getNflCurrentWeekData,
    getSelectedWeekData: getNflSelectedWeekData,
    isLoading: nflCalendarLoading,
    error: nflCalendarError
  } = useNflCalendar(selectedSport);

  // Determine if selected NFL week is playoffs (uses isPlayoffs flag from transformed data)
  const nflSelectedWeekData = getNflSelectedWeekData();
  const isNflPlayoffs = nflSelectedWeekData?.isPlayoffs === true;
  // For API calls, use originalValue for playoffs (e.g., "1") or the value itself for regular season
  const nflWeekForApi = isNflPlayoffs ? nflSelectedWeekData?.originalValue : nflSelectedWeek;

  // NFL games hook (fetch games for selected week)
  const {
    games: nflGames,
    gamesMetadata: nflGamesMetadata,
    isLoading: nflGamesLoading,
    error: nflGamesError,
    refetchGames: refetchNflGames
  } = useNflGames(selectedSport, nflWeekForApi, isNflPlayoffs);
  
  // Only fetch odds data when NOT in analytics mode
  const { 
    playersByGameId, 
    selectedMarkets,
    loadingGameIds, 
    handleMarketSelection, 
    getSelectedMarketForGame,
    debugStats
  } = useOddsData(analyticsMode ? [] : (selectedSport === 'ncaaf' ? ncaafGames : selectedSport === 'nfl' ? nflGames : games), selectedSport, hasAccess);

  // Ref to read playersByGameId without re-triggering pitches fetch on polls
  const playersByGameIdRef = useRef(playersByGameId);
  useEffect(() => { playersByGameIdRef.current = playersByGameId; }, [playersByGameId]);

  // Build the set of scratchpad player IDs for *today* in the *current sport*.
  // Null means "filter inactive" — the panel toggle is off, or the current
  // sport has no items on today's scratchpad. Either way the unfiltered
  // playersByGameId flows through to the tables.
  const scratchpadFilterIds = useMemo(() => {
    if (!scratchpadFilterEnabled) return null;
    const now = new Date();
    const todayYmd = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
    const todayIso = now.toISOString().slice(0, 10);
    const sportItems = scratchpadItems.filter(it => {
      if ((it.sport || '').toLowerCase() !== selectedSport) return false;
      const k = it.gameDateKey || (it.addedAt ? new Date(it.addedAt).toISOString().slice(0, 10) : 'older');
      return k === todayYmd || k === todayIso;
    });
    if (sportItems.length === 0) return null;
    const stripPrefix = (id) => String(id).replace(/^(mlb|nba|wnba|nfl|nhl|ncaaf|ncaab|soccer)_/, '');
    return new Set(sportItems.map(it => stripPrefix(it.playerId)));
  }, [scratchpadFilterEnabled, scratchpadItems, selectedSport]);

  // Sport-scoped filtered view of playersByGameId. Player-level rows whose
  // ID isn't on today's scratchpad are removed. Rows without a playerId
  // (game-level h2h / totals / spreads containers) pass through unchanged so
  // the mainline H2H tables aren't accidentally emptied.
  const playersByGameIdFiltered = useMemo(() => {
    if (!scratchpadFilterIds) return playersByGameId;
    const stripPrefix = (id) => String(id).replace(/^(mlb|nba|wnba|nfl|nhl|ncaaf|ncaab|soccer)_/, '');
    const out = {};
    for (const gameId of Object.keys(playersByGameId)) {
      const entry = playersByGameId[gameId];
      if (!entry || !Array.isArray(entry.players)) { out[gameId] = entry; continue; }
      const filtered = entry.players.filter(p => {
        if (!p?.playerId) return true;
        return scratchpadFilterIds.has(stripPrefix(p.playerId));
      });
      out[gameId] = { ...entry, players: filtered };
    }
    return out;
  }, [playersByGameId, scratchpadFilterIds]);

  // Analytics data hook (only fetch when in analytics mode)
  // Pass null for NCAAF/NFL or when not in analytics mode to prevent unnecessary processing
  const { 
    analyticsByGameId, 
    loadingGameIds: analyticsLoadingGameIds
  } = useAnalyticsData(
    analyticsMode && selectedSport !== 'ncaaf' && selectedSport !== 'nfl' ? analyticsGames : null, 
    analyticsType
  );

  // Helper function to get the appropriate games data based on sport and mode
  const getCurrentGamesData = () => {
    if (selectedSport === 'ncaaf') {
      return ncaafGames;
    }
    if (selectedSport === 'nfl') {
      return nflGames;
    }
    return analyticsMode ? analyticsGames : games;
  };

  // Helper function to get the appropriate games metadata
  const getCurrentGamesMetadata = () => {
    if (selectedSport === 'ncaaf') {
      return ncaafGamesMetadata;
    }
    if (selectedSport === 'nfl') {
      return nflGamesMetadata;
    }
    return analyticsMode ? analyticsGamesMetadata : gamesMetadata;
  };

  // Extract a stable "HH:MM" (24-hour) key from an NCAAF game so time-slot
  // badges can be compared regardless of raw gameTime formatting. Handles ISO
  // datetimes, "HH:MM(:SS)" 24-hour strings, and "h:mm(:ss) AM/PM" strings.
  // Returns null when nothing parses.
  //
  // The optional :SS on the AM/PM form is important: without it the regex
  // silently skips the hour on "8:30:15 PM" and matches "30:15 PM", producing
  // nonsense labels like "30:15 PM".
  const getNcaafTimeSlotKey = (game) => {
    if (!game) return null;
    const sources = [game.startTimeEST, game.commence_time, game.startTime, game.gameTime, game.date].filter(Boolean);
    for (const raw of sources) {
      if (typeof raw !== 'string') continue;
      // Try full date-time strings first — Date is the most reliable parser
      // when we have a real timestamp and it also rules out the bare-time
      // regexes below from matching an embedded time-of-day.
      let iso = raw.includes(' ') && !raw.includes('T') ? raw.replace(' ', 'T') : raw;
      const d = new Date(iso);
      if (!isNaN(d.getTime()) && /\d{4}/.test(raw)) {
        return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
      }
      // Bare 24-hour "HH:MM" or "HH:MM:SS"
      const hm24 = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
      if (hm24) return `${hm24[1].padStart(2, '0')}:${hm24[2]}`;
      // "h:mm AM/PM" or "h:mm:ss AM/PM" (possibly with trailing timezone text)
      const hm12 = raw.match(/(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM|am|pm)/);
      if (hm12) {
        let h = parseInt(hm12[1], 10);
        const p = hm12[3].toUpperCase();
        if (p === 'PM' && h !== 12) h += 12;
        if (p === 'AM' && h === 12) h = 0;
        return `${String(h).padStart(2, '0')}:${hm12[2]}`;
      }
    }
    return null;
  };

  // Render "HH:MM" 24-hour key as "h:mm AM/PM" for the badge label.
  const formatTimeSlotLabel = (key) => {
    if (!key) return '';
    const [hStr, m] = key.split(':');
    let h = parseInt(hStr, 10);
    const period = h >= 12 ? 'PM' : 'AM';
    if (h === 0) h = 12;
    else if (h > 12) h -= 12;
    return `${h}:${m} ${period}`;
  };

  // =============================================================================
  // PERFORMANCE OPTIMIZATION: Pre-filter games for NCAAB and Soccer
  // This avoids filtering during render's map callback (which runs on every render)
  // To revert: remove this useMemo and restore filtering logic in the render map
  // =============================================================================
  const filteredGamesForRender = useMemo(() => {
    const currentGames = getCurrentGamesData();
    if (!currentGames || currentGames.length === 0) return [];

    // NCAAB / NCAAF filtering — same conference-badge + Top-25 filter shape.
    // NCAAF additionally intersects with the time-slot badge filter when
    // the user has narrowed it down.
    if (selectedSport === 'ncaab' || selectedSport === 'ncaaf') {
      return currentGames.filter(game => {
        // Conference / Top-25 gate
        if (top25FilterEnabled) {
          const awayIsRanked = game.awayRank && game.awayRank <= 25;
          const homeIsRanked = game.homeRank && game.homeRank <= 25;
          if (!(awayIsRanked || homeIsRanked)) return false;
        } else {
          if (enabledConferences.size === 0) return false;
          const homeConfEnabled = enabledConferences.has(game.homeConferenceName);
          const awayConfEnabled = enabledConferences.has(game.awayConferenceName);
          if (!(homeConfEnabled || awayConfEnabled)) return false;
        }
        // NCAAF time-slot gate. Disabled while the badges UI is hidden — with
        // no UI to reset the filter, dropping games whose gameTime can't be
        // parsed would be a silent regression. Re-enable this whole block when
        // flipping the badges back on above.
        // if (selectedSport === 'ncaaf' && ncaafHasInitedTimeSlots.current) {
        //   const slotKey = getNcaafTimeSlotKey(game);
        //   if (!slotKey || !enabledNcaafTimeSlots.has(slotKey)) return false;
        // }
        return true;
      });
    }

    // Soccer filtering
    if (selectedSport === 'soccer') {
      if (enabledLeagues.size === 0) return [];
      return currentGames.filter(game => enabledLeagues.has(game.leagueName));
    }

    // No filtering for other sports
    return currentGames;
  }, [
    // Dependencies - only re-filter when these change
    selectedSport,
    games,
    analyticsGames,
    ncaafGames,
    nflGames,
    analyticsMode,
    top25FilterEnabled,
    enabledConferences,
    enabledNcaafTimeSlots,
    enabledLeagues
  ]);
  // =============================================================================

  // =============================================================================
  // Measure market banner height for precise overlay positioning
  // =============================================================================
  useEffect(() => {
    const updateBannerBottom = () => {
      if (marketBannerRef.current) {
        const rect = marketBannerRef.current.getBoundingClientRect();
        setMarketBannerBottom(rect.bottom);
      }
    };

    updateBannerBottom();
    window.addEventListener('resize', updateBannerBottom);
    window.addEventListener('scroll', updateBannerBottom, { passive: true });

    // Use ResizeObserver to catch any layout shifts in the banner itself
    let resizeObserver;
    if (marketBannerRef.current && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateBannerBottom);
      resizeObserver.observe(marketBannerRef.current);
    }

    // Update after short delays to catch layout shifts as content loads
    const t1 = setTimeout(updateBannerBottom, 100);
    const t2 = setTimeout(updateBannerBottom, 500);
    const t3 = setTimeout(updateBannerBottom, 1000);

    return () => {
      window.removeEventListener('resize', updateBannerBottom);
      window.removeEventListener('scroll', updateBannerBottom);
      if (resizeObserver) resizeObserver.disconnect();
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [selectedSport, masterMarket, games?.length]); // ResizeObserver handles dynamic size changes automatically

  // =============================================================================
  // Prevent body scrolling when mobile trends overlay is open
  // =============================================================================
  useEffect(() => {
    if (showMobileTrends) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showMobileTrends]);

  // =============================================================================
  // Scroll-based tracking of visible game (NBA Trends + MLB Matchups)
  // Triggers when a game header's top crosses above the trigger line (banner + 500px)
  // =============================================================================
  useEffect(() => {
    const trendsActive = showIqOverlay || showMobileTrends;
    const matchupsActive = showMatchupsPanel && selectedSport === 'mlb';
    if (!(trendsActive && (selectedSport === 'nba' || selectedSport === 'wnba')) && !matchupsActive) {
      return;
    }

    // When best bets panel is open, game selection is driven by clicking game cells — skip scroll tracking
    if (showBestBetsPanel && !isMobile) {
      return;
    }

    const MARKET_BANNER_BOTTOM = marketBannerBottom; // Use measured value
    const TRIGGER_LINE = MARKET_BANNER_BOTTOM + (isMobile ? 300 : 500); // Line below which we look for the active game

    let debounceTimer = null;
    let latestActiveGame = null;

    const handleScroll = () => {
      let activeGame = null;

      // Find the game whose top has scrolled past the trigger line
      // We want the LAST game whose top is above the trigger line (i.e., it has scrolled up past it)
      // This means the game is currently "at" the top of the visible content
      gameElementsRef.current.forEach((element, gameId) => {
        if (!element) return;

        const rect = element.getBoundingClientRect();
        const gameTop = rect.top;

        // If the game's top is above the trigger line AND the game's bottom is still visible
        // (i.e., the game is still on screen), this is a candidate
        if (gameTop <= TRIGGER_LINE && rect.bottom > MARKET_BANNER_BOTTOM) {
          activeGame = gameId;
        }
      });

      // If no game has scrolled past trigger line, use the first game
      if (!activeGame && filteredGamesForRender.length > 0) {
        activeGame = filteredGamesForRender[0].gameIDmlb;
      }

      if (activeGame) {
        latestActiveGame = activeGame;
        // Debounce: wait for scrolling to settle before updating visible game
        // This prevents firing data fetches for every intermediate game during smooth scroll
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          setVisibleGameId(latestActiveGame);
        }, 300);
      }
    };

    // Initial check (immediate, no debounce for first load)
    {
      let activeGame = null;
      gameElementsRef.current.forEach((element, gameId) => {
        if (!element) return;
        const rect = element.getBoundingClientRect();
        if (rect.top <= TRIGGER_LINE && rect.bottom > MARKET_BANNER_BOTTOM) {
          activeGame = gameId;
        }
      });
      if (!activeGame && filteredGamesForRender.length > 0) {
        activeGame = filteredGamesForRender[0].gameIDmlb;
      }
      if (activeGame && activeGame !== visibleGameId) {
        setVisibleGameId(activeGame);
      }
    }

    // Add scroll listener
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      clearTimeout(debounceTimer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [showIqOverlay, showMobileTrends, showMatchupsPanel, selectedSport, visibleGameId, filteredGamesForRender, isMobile, showBestBetsPanel]);

  // =============================================================================
  // IQ Overlay: Fetch summary data once per (sport, market, year).
  // Visible-game filtering is applied client-side in the trends reports so we
  // pick up players who changed teams between seasons.
  // =============================================================================
  const iqCacheRef = useRef(new Map());
  useEffect(() => {
    const trendsActive = showIqOverlay || showMobileTrends;
    if (!trendsActive || !(selectedSport === 'nba' || selectedSport === 'wnba') || !masterMarket) {
      return;
    }

    const cacheKey = `${selectedSport}_${masterMarket}_${trendsYear}`;
    const cached = iqCacheRef.current.get(cacheKey);
    if (cached) {
      setIqData(cached);
      return;
    }

    const fetchIqData = async () => {
      setIqLoading(true);
      try {
        const response = await fetch(`/api/stats/${selectedSport}/summary/all?market=${masterMarket}&year=${trendsYear}`);
        if (response.ok) {
          const data = await response.json();
          iqCacheRef.current.set(cacheKey, data);
          setIqData(data);
        } else {
          console.error('IQ fetch failed:', response.status);
          setIqData(null);
        }
      } catch (error) {
        console.error('IQ fetch error:', error);
        setIqData(null);
      } finally {
        setIqLoading(false);
      }
    };

    fetchIqData();
  }, [showIqOverlay, showMobileTrends, selectedSport, masterMarket, trendsYear]);

  // =============================================================================
  // Trends: Fetch today's games (with rosters) so the small panel can match the
  // large panel's player list. Cached per sport.
  // =============================================================================
  const trendsRosterCacheRef = useRef(new Map());
  useEffect(() => {
    const trendsActive = showIqOverlay || showMobileTrends;
    if (!trendsActive || !(selectedSport === 'nba' || selectedSport === 'wnba')) {
      return;
    }
    const cached = trendsRosterCacheRef.current.get(selectedSport);
    if (cached) {
      setTrendsTodayGames(cached);
      return;
    }
    const fetchToday = async () => {
      try {
        const response = await fetch(`/api/games/${selectedSport}/today`);
        if (response.ok) {
          const data = await response.json();
          trendsRosterCacheRef.current.set(selectedSport, data);
          setTrendsTodayGames(data);
        }
      } catch (error) {
        console.error('Trends roster fetch error:', error);
      }
    };
    fetchToday();
  }, [showIqOverlay, showMobileTrends, selectedSport]);


  // Callback to register game elements for scroll-based visibility tracking
  const registerGameElement = useCallback((gameId, element) => {
    if (element) {
      gameElementsRef.current.set(gameId, element);
    } else {
      gameElementsRef.current.delete(gameId);
    }
  }, []);
  // =============================================================================

  // =============================================================================
  // PERFORMANCE OPTIMIZATION: Memoize best bets calculation
  // This prevents recalculating 1500+ bets on every render (e.g., expanding menus)
  // Only recalculates when actual data changes (polling updates, sport change, etc.)
  // To revert: replace bestBetsData with getBestBets() function calls
  // =============================================================================
  const bestBetsData = useMemo(() => {
    const devModeEnabled = isDevModeEnabled('allbestbets');

    // Get current games based on sport
    let currentGames;
    if (selectedSport === 'ncaaf') {
      currentGames = ncaafGames;
    } else if (selectedSport === 'nfl') {
      currentGames = nflGames;
    } else {
      currentGames = analyticsMode ? analyticsGames : games;
    }

    // Get enabled books for best bets
    const enabledBooks = bookOrder.filter(book => enabledBooksForBestBets[book] !== false);

    // Create getShowTiersForGame function inline to capture current state
    const showTiersForGame = (gameId) => gameShowTiers[gameId] ?? masterShowTiers;

    const bets = calculateBestBets(
      currentGames,
      playersByGameId,
      enabledBooks,
      selectedMarkets,
      availableMarkets,
      masterTierFilter,
      showTiersForGame,
      devModeEnabled,
      recalcEdgeBestBets
    );

    // Optional American-odds range gate from the Settings > Best Bets panel.
    // Bounds are inclusive; either side may be null to skip that side of the
    // check. Bets whose `odds` string doesn't parse are kept (no info to
    // filter on). Range is picked by sport when the global toggle is off.
    const { min: rangeMin, max: rangeMax } = getBestBetsOddsRange(selectedSport);
    if (rangeMin !== null || rangeMax !== null) {
      return bets.filter(bet => {
        const parsed = parseInt(String(bet.odds ?? '').replace('+', ''), 10);
        if (Number.isNaN(parsed)) return true;
        if (rangeMin !== null && parsed < rangeMin) return false;
        if (rangeMax !== null && parsed > rangeMax) return false;
        return true;
      });
    }
    return bets;
  }, [
    selectedSport,
    games,
    ncaafGames,
    nflGames,
    analyticsGames,
    analyticsMode,
    playersByGameId,
    enabledBooksForBestBets,
    bookOrder,
    selectedMarkets,
    availableMarkets,
    masterTierFilter,
    gameShowTiers,
    masterShowTiers,
    recalcEdgeBestBets,
    bestBetsMinOdds,
    bestBetsMaxOdds,
    bestBetsUseGlobalOdds,
    bestBetsOddsBySport
  ]);
  // =============================================================================

  // Desktop panel uses the same dataset as the mobile takeover so the two
  // Best Bets buttons always show the same total.
  const bestBetsPanelData = bestBetsData;

  // Check if best bets data is still loading
  const isBestBetsLoading = loadingGameIds.size > 0;

  // Wrapper for formatPlayerName to include isMobile and starter status
  const formatPlayerNameWrapper = (name, isStarter, sport) => formatPlayerName(name, isMobile, isStarter, sport);
  
  // Get appropriate team logo URL based on sport
  const getTeamLogoUrlForSport = (teamAbbrev) => {
    // This will be called by DataTable without game context
    if (selectedSport === 'wnba') {
      return getWNBATeamLogoUrl(teamAbbrev, isDarkMode);
    }
    return getTeamLogoUrl(teamAbbrev, isDarkMode, selectedSport);
  };
  
  // Create a custom logo getter for each game that includes soccer, NCAAF, and NFL logic
  const createTeamLogoGetter = (game) => {
    return (teamIdentifier) => {
      if (teamIdentifier === 'WAS' || teamIdentifier === 'WSH') {
        /*
        console.log('createTeamLogoGetter Washington debug:', { 
          teamIdentifier, 
          selectedSport, 
          gameAway: game?.away, 
          gameHome: game?.home,
          awayLogo: game?.awayLogo,
          homeLogo: game?.homeLogo
        });
        */
      }
      
      if ((selectedSport === 'soccer' || selectedSport === 'ncaaf' || selectedSport === 'nfl' || selectedSport === 'nba' || selectedSport === 'wnba' || selectedSport === 'ncaab' || selectedSport === 'mlb') && game) {
        // For soccer, NCAAF, NFL, NBA, and WNBA, first try to use logos from game data by matching team abbreviations
        if (teamIdentifier === game.away || teamIdentifier === game.awayTeam) {
          return game.awayLogo || getTeamLogoUrl(teamIdentifier, isDarkMode, selectedSport);
        } else if (teamIdentifier === game.home || teamIdentifier === game.homeTeam) {
          return game.homeLogo || getTeamLogoUrl(teamIdentifier, isDarkMode, selectedSport);
        }
        
        // Try to match by team ID - handle both string and number comparisons
        const teamIdNum = parseInt(teamIdentifier);
        const awayTeamIdNum = parseInt(game.awayTeamId);
        const homeTeamIdNum = parseInt(game.homeTeamId);
        
        if (!isNaN(teamIdNum) && (teamIdNum === awayTeamIdNum)) {
          return game.awayLogo || getTeamLogoUrl(game.away, isDarkMode, selectedSport);
        } else if (!isNaN(teamIdNum) && (teamIdNum === homeTeamIdNum)) {
          return game.homeLogo || getTeamLogoUrl(game.home, isDarkMode, selectedSport);
        }
        
        // If no match found, fallback to default team logo logic
        const logoUrl = getTeamLogoUrl(teamIdentifier, isDarkMode, selectedSport);
        if (teamIdentifier === 'WAS') {
          //console.log('createTeamLogoGetter WAS fallback:', { teamIdentifier, logoUrl });
        }
        return logoUrl;
      }
      return getTeamLogoUrlForSport(teamIdentifier);
    };
  };

  // Compute player panel right offset based on which other panels are open
  const playerPanelRight = useMemo(() => {
    // Treat either right-side panel (Betslip or Scratchpad) as occupying the
    // same 410px slot — the player panel slides left for whichever is open.
    const rightPanelOpen = showBetslipPanel || showScratchpadPanel;
    const trendsOpen = showIqOverlay;
    if (rightPanelOpen && trendsOpen) return '870px';
    if (trendsOpen) return '585px';
    if (rightPanelOpen) return '410px';
    return '5px';
  }, [showBetslipPanel, showScratchpadPanel, showIqOverlay]);

  // Handle master market selection - apply to all games
  const handleMasterMarketSelection = (marketKey) => {
    try {
      console.log('Selecting market:', marketKey);
      setMasterMarket(marketKey);

    // Handle tier filter based on sport and market
    if ((selectedSport === 'nhl' || selectedSport === 'nfl') && marketKey !== 'h2h' && marketKey !== 'totals' && marketKey !== 'spreads') {
      // For NHL and NFL player props, default to 0.5 tier
      setMasterTierFilter(0.5);
    } else if (selectedSport === 'soccer' && (marketKey === 'player_shots' || marketKey === 'player_shots_on_target')) {
      // For soccer shots markets, default to 0.5 tier
      setMasterTierFilter(0.5);
    } else {
      // For other sports/markets, reset tier filter
      setMasterTierFilter(null);
    }
    
    // Get current games based on sport
    const currentGames = getCurrentGamesData();
    
    // Check if there's a saved preference for this market
    const savedPreference = marketTierPreferences[marketKey];
    if (savedPreference !== undefined) {
      // Use the saved preference for this market
      setMasterShowTiers(savedPreference);
      // Apply to all games
      const newGameShowTiers = {};
      currentGames.forEach(game => {
        newGameShowTiers[game.gameIDmlb] = savedPreference;
      });
      setGameShowTiers(newGameShowTiers);
    } else {
      // No saved preference - use default behavior
      // Auto-enable tiers for totals market, disable for others
      const defaultValue = marketKey === 'totals' || marketKey === 'pitcher_strikeouts';
      setMasterShowTiers(defaultValue);
      // Apply to all games
      const newGameShowTiers = {};
      currentGames.forEach(game => {
        newGameShowTiers[game.gameIDmlb] = defaultValue;
      });
      setGameShowTiers(newGameShowTiers);
    }
    
    // Only apply the selected market if we have games loaded
    if (currentGames.length > 0) {
      currentGames.forEach(game => {
        handleMarketSelection(game.gameIDmlb, marketKey);
      });
    }
    } catch (error) {
      console.error('Error in handleMasterMarketSelection:', error);
    }
  };

  // Set default market when sport changes
  useEffect(() => {
    try {
      console.log('Sport changed to:', selectedSport);
      // Reset master market when sport changes
      setMasterMarket(null);
      setMasterTierFilter(null);
      setGameShowTiers({});
      setUserSelectedMarkets({}); // Clear user selections on sport change
      
      // Set sport-specific default market
      const defaultMarket = getDefaultMarketForSport(selectedSport);
      console.log('Default market for', selectedSport, ':', defaultMarket);
      if (defaultMarket) {
        handleMasterMarketSelection(defaultMarket);
      }
    } catch (error) {
      console.error('Error changing sport:', error);
    }
  }, [selectedSport]); // Only depend on sport, not games

  // Apply master market to games when they're loaded
  useEffect(() => {
    const currentGames = getCurrentGamesData();
    if (masterMarket && currentGames.length > 0) {
      // Check if there's a saved preference for this market
      const savedPreference = marketTierPreferences[masterMarket];
      if (savedPreference !== undefined) {
        // Use the saved preference for this market
        setMasterShowTiers(savedPreference);
        const newGameShowTiers = {};
        currentGames.forEach(game => {
          newGameShowTiers[game.gameIDmlb] = savedPreference;
        });
        setGameShowTiers(newGameShowTiers);
      } else {
        // No saved preference - use default behavior
        const defaultValue = masterMarket === 'totals' || masterMarket === 'pitcher_strikeouts';
        setMasterShowTiers(defaultValue);
        const newGameShowTiers = {};
        currentGames.forEach(game => {
          newGameShowTiers[game.gameIDmlb] = defaultValue;
        });
        setGameShowTiers(newGameShowTiers);
      }
      
      currentGames.forEach(game => {
        // Only apply master market if the game doesn't have a user-specific selection
        if (!userSelectedMarkets[game.gameIDmlb]) {
          handleMarketSelection(game.gameIDmlb, masterMarket);
        }
      });
    }
  }, [games, nflGames, ncaafGames, masterMarket]); // Apply when any games load or master market changes

  // Handle master tier toggle - apply to all games
  const handleMasterTierToggle = (show) => {
    setMasterShowTiers(show);
    
    // Save the preference for the current market
    if (masterMarket) {
      const updatedPreferences = {
        ...marketTierPreferences,
        [masterMarket]: show
      };
      setMarketTierPreferences(updatedPreferences);
      localStorage.setItem('marketTierPreferences', JSON.stringify(updatedPreferences));
    }
    
    // Apply to all games
    const newGameShowTiers = {};
    games.forEach(game => {
      newGameShowTiers[game.gameIDmlb] = show;
    });
    setGameShowTiers(newGameShowTiers);
  };

  // Handle game-specific tier toggle
  const handleGameTierToggle = (gameId, show) => {
    setGameShowTiers(prev => ({
      ...prev,
      [gameId]: show
    }));
  };

  // Handle expand/collapse all games (mobile only)
  const handleExpandAll = () => {
    const newCollapseStates = {};
    const currentGames = getCurrentGamesData();
    currentGames.forEach(game => {
      newCollapseStates[game.gameIDmlb] = false; // false = expanded
    });
    setGameCollapseStates(newCollapseStates);
  };

  const handleCollapseAll = () => {
    const newCollapseStates = {};
    const currentGames = getCurrentGamesData();
    currentGames.forEach(game => {
      newCollapseStates[game.gameIDmlb] = true; // true = collapsed
    });
    setGameCollapseStates(newCollapseStates);
  };

  // Handle individual game collapse toggle
  const handleGameCollapseToggle = (gameId, isCollapsed) => {
    setGameCollapseStates(prev => ({
      ...prev,
      [gameId]: isCollapsed
    }));
  };

  // Get whether to show tiers for a specific game
  const getShowTiersForGame = (gameId) => {
    return gameShowTiers[gameId] ?? masterShowTiers;
  };

  // Get available tiers for the selected master market
  const getAvailableTiersForMasterMarket = () => {
    if (!masterMarket) return [];
    
    const tierSet = new Set();
    const currentGames = getCurrentGamesData();
    currentGames.forEach(game => {
      const gameData = playersByGameId[game.gameIDmlb];
      if (gameData?.players) {
        gameData.players.forEach(player => {
          if (player.point !== null && player.point !== undefined) {
            tierSet.add(player.point);
          }
        });
      }
    });
    
    return Array.from(tierSet).sort((a, b) => a - b);
  };

  // Handle master tier filter selection
  const handleMasterTierFilter = (tierValue) => {
    setMasterTierFilter(tierValue === null ? null : parseFloat(tierValue));
  };

  // Handle player name click - open player panel (desktop) or modal (mobile)
  const handlePlayerClick = (gameId, playerId, playerName, market, awayTeam, homeTeam, playerTeam, playerPosition, point = null) => {
    // Determine opposing pitcher from game data
    const clickedGame = filteredGamesForRender.find(g => g.gameIDmlb === gameId);
    // Determine team from: prop, odds data, or game player lists
    const cleanPid = String(playerId).replace(/^mlb_/, '');
    const oddsPlayer = playersByGameIdRef.current?.[gameId]?.players?.find(p => String(p.playerId).replace(/^mlb_/, '') === cleanPid);
    const pTeam = playerTeam || oddsPlayer?.team || oddsPlayer?.teamAbv;
    const isAwayPlayer = pTeam ? (pTeam === clickedGame?.away) : !!clickedGame?.awayPlayers?.[`ID${cleanPid}`];
    const opposingPitcher = isAwayPlayer ? clickedGame?.homeProbablePitcher : clickedGame?.awayProbablePitcher;

    if (!isMobile) {
      // Desktop: open player detail panel
      setPlayerPanelData({
        gameId,
        playerId,
        playerName,
        market,
        awayTeam,
        homeTeam,
        playerTeam,
        playerPosition,
        point,
        opposingPitcher
      });
      setPlayerPanelView('table');
      setPlayerPanelChartParams(null);
      setPlayerPanelActiveMarketKey(null);
      setPlayerPanelHandInfo(null);
      setShowPlayerPanel(true);
      setShowGamePanel(false);
    } else {
      // Mobile: keep existing modal behavior
      setPlayerModalData({
        gameId,
        playerId,
        playerName,
        market,
        awayTeam,
        homeTeam,
        playerTeam,
        playerPosition,
        point,
        opposingPitcher
      });
      setShowPlayerModal(true);
    }
  };

  // Handle date change
  const handleDateChange = (event) => {
    const newDate = event.target.value;
    // Convert from YYYY-MM-DD to MM/DD/YYYY format
    const dateParts = newDate.split('-');
    const formattedDate = `${dateParts[1]}/${dateParts[2]}/${dateParts[0]}`;
    setSelectedDate(formattedDate);
    setDateExplicitlySelected(true); // Mark that user explicitly selected a date
    setShowDatePicker(false);
  };

  // Handle clicking outside date picker to close it
  const handleDatePickerClick = (event) => {
    event.stopPropagation();
  };

  // Format date for input (MM/DD/YYYY -> YYYY-MM-DD)
  const formatDateForInput = (dateString) => {
    const parts = dateString.split('/');
    return `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
  };

  // Navigate to previous date
  const navigateToPreviousDate = () => {
    const currentDate = new Date(selectedDate);
    const previousDate = new Date(currentDate);
    previousDate.setDate(currentDate.getDate() - 1);

    const month = String(previousDate.getMonth() + 1).padStart(2, '0');
    const day = String(previousDate.getDate()).padStart(2, '0');
    const year = previousDate.getFullYear();

    setSelectedDate(`${month}/${day}/${year}`);
    setDateExplicitlySelected(true); // Mark that user navigated to a specific date
  };

  // Navigate to next date
  const navigateToNextDate = () => {
    const currentDate = new Date(selectedDate);
    const nextDate = new Date(currentDate);
    nextDate.setDate(currentDate.getDate() + 1);

    const month = String(nextDate.getMonth() + 1).padStart(2, '0');
    const day = String(nextDate.getDate()).padStart(2, '0');
    const year = nextDate.getFullYear();

    setSelectedDate(`${month}/${day}/${year}`);
    setDateExplicitlySelected(true); // Mark that user navigated to a specific date
  };

  // Reset date selection flag when switching to NBA/NHL/NCAAB/Soccer
  useEffect(() => {
    if (selectedSport === 'nba' || selectedSport === 'nhl' || selectedSport === 'ncaab' || selectedSport === 'soccer') {
      setDateExplicitlySelected(false);
    }
    // Reset enabled conferences and Top25 filter when switching sports
    if (selectedSport !== 'ncaab' && selectedSport !== 'ncaaf') {
      setEnabledConferences(new Set());
      setTop25FilterEnabled(false);
    }
    // Reset NCAAF time-slot filter when leaving NCAAF
    if (selectedSport !== 'ncaaf') {
      setEnabledNcaafTimeSlots(new Set());
      ncaafHasInitedTimeSlots.current = false;
    }
    // Reset enabled leagues when switching sports
    if (selectedSport !== 'soccer') {
      setEnabledLeagues(new Set());
    }
  }, [selectedSport]);

  // Initialize enabled conferences when NCAAB / NCAAF games load
  useEffect(() => {
    if (selectedSport !== 'ncaab' && selectedSport !== 'ncaaf') return;
    const sourceGames = selectedSport === 'ncaaf' ? ncaafGames : games;
    if (!sourceGames || sourceGames.length === 0) return;
    const allConferences = new Set();
    sourceGames.forEach(game => {
      if (game.homeConferenceName) allConferences.add(game.homeConferenceName);
      if (game.awayConferenceName) allConferences.add(game.awayConferenceName);
    });
    // Only initialize if we don't have any conferences enabled yet
    if (enabledConferences.size === 0 && allConferences.size > 0) {
      setEnabledConferences(allConferences);
    }
  }, [selectedSport, games, ncaafGames]);

  // Initialize enabled NCAAF time slots when games load.
  // Uses a stable "HH:MM" 24-hour key so we can compare regardless of how
  // gameTime is formatted on individual games. Sets the init ref so the
  // filter starts applying only after we've seeded a set of slots.
  useEffect(() => {
    if (selectedSport !== 'ncaaf' || !ncaafGames || ncaafGames.length === 0) return;
    const allSlots = new Set();
    ncaafGames.forEach(game => {
      const key = getNcaafTimeSlotKey(game);
      if (key) allSlots.add(key);
    });
    if (!ncaafHasInitedTimeSlots.current && allSlots.size > 0) {
      setEnabledNcaafTimeSlots(allSlots);
      ncaafHasInitedTimeSlots.current = true;
    }
  }, [selectedSport, ncaafGames]);

  // Update enabled leagues when soccer games change (e.g., date change)
  useEffect(() => {
    if (selectedSport === 'soccer' && games && games.length > 0) {
      const allLeagues = new Set();
      games.forEach(game => {
        if (game.leagueName) allLeagues.add(game.leagueName);
      });

      if (enabledLeagues.size === 0) {
        // First load - enable all leagues
        setEnabledLeagues(allLeagues);
      } else {
        // Date changed - keep only leagues that exist in new games, or enable all if none match
        const stillValidLeagues = new Set([...enabledLeagues].filter(league => allLeagues.has(league)));
        if (stillValidLeagues.size === 0) {
          // None of the previously enabled leagues exist in new games - enable all
          setEnabledLeagues(allLeagues);
        } else {
          // Keep only the valid leagues
          setEnabledLeagues(stillValidLeagues);
        }
      }
    }
  }, [selectedSport, games]);

  // Close date picker and mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDatePicker && !event.target.closest('.position-relative')) {
        setShowDatePicker(false);
      }
      if (showMobileMenu && !event.target.closest('.mobile-menu-container')) {
        setShowMobileMenu(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showDatePicker, showMobileMenu]);

  // Handle odds cell click - add to betslip (no book/odds stored - API will find best parlay odds)
  const handleOddsClick = (gameId, rawPlayerId, playerName, market, bookmaker, odds, awayTeam, homeTeam, playerTeam, playerPosition, line, side, teamId) => {
    // Ensure mlb_ prefix on player IDs for betslip API
    const playerId = rawPlayerId && selectedSport === 'mlb' && !String(rawPlayerId).startsWith('mlb_')
      ? `mlb_${rawPlayerId}` : rawPlayerId;

    // For h2h/spreads, use team name instead of playerId
    const isTeamBet = market === 'h2h' || market === 'spreads';
    const name = isTeamBet ? (playerTeam || playerName) : playerName;

    // Check for duplicates - same game, player/team, market, line, and side
    const isDuplicate = betslipItems.some(bet =>
      bet.gameId === gameId &&
      (isTeamBet ? bet.name === name : bet.playerId === playerId) &&
      bet.market === market &&
      bet.line === line &&
      bet.side === side
    );

    // Mobile-only toast helper. Auto-dismisses after ~2s; latest add cancels
    // any pending timer so back-to-back taps replace the message instead of
    // stacking or ping-ponging.
    const flashToast = (payload) => {
      if (!isMobile) return;
      if (betslipToastTimerRef.current) clearTimeout(betslipToastTimerRef.current);
      setBetslipToast(payload);
      betslipToastTimerRef.current = setTimeout(() => {
        setBetslipToast(null);
        betslipToastTimerRef.current = null;
      }, 2000);
    };

    if (isDuplicate) {
      flashToast({ kind: 'dup', message: 'Already in betslip', subtitle: `${name} ${side || ''} ${line ?? ''}`.trim() });
      return;
    }

    // Create bet item without book/odds - API will determine best parlay odds
    const betItem = {
      id: `${gameId}_${isTeamBet ? name : playerId}_${market}_${line}_${side}_${Date.now()}`,
      gameId,
      playerId: isTeamBet ? null : playerId,
      playerName,
      name,
      market,
      line,
      side,
      awayTeam,
      homeTeam,
      team: playerTeam || null,
      teamId: isTeamBet ? (teamId || null) : null,
      sport: selectedSport,
      timestamp: Date.now()
    };

    // Add to betslip
    setBetslipItems(prev => [...prev, betItem]);
    flashToast({ kind: 'added', message: 'Added to betslip', subtitle: `${name} ${side || ''} ${line ?? ''}`.trim() });

    // Trigger cell animation
    const cellId = `${gameId}_${playerId}_${market}_${bookmaker}_${line}_${side}`;
    setClickedCellId(cellId);
    setTimeout(() => setClickedCellId(null), 500);
  };

  // Remove bet from betslip
  const handleRemoveBet = (betId) => {
    setBetslipItems(prev => prev.filter(bet => bet.id !== betId));
  };

  // Clear all bets from betslip
  const handleClearBetslip = () => {
    setBetslipItems([]);
  };

  // Handle right-click on odds cell - show context menu
  const handleOddsContextMenu = (e, gameId, playerId, playerName, market, bookmaker, odds, awayTeam, homeTeam, playerTeam, playerPosition, line, side) => {
    e.preventDefault();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      playerData: { gameId, playerId, playerName, market, bookmaker, odds, awayTeam, homeTeam, playerTeam, playerPosition, line, side }
    });
  };

  // Player-level right-click — used for scratchpad add/remove, no cell-specific data.
  const handlePlayerContextMenu = (e, gameId, playerId, playerName, playerTeam) => {
    e.preventDefault();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      playerData: { gameId, playerId, playerName, playerTeam }
    });
  };

  // Add bet from context menu
  const handleContextMenuAddToBetslip = (data) => {
    handleOddsClick(data.gameId, data.playerId, data.playerName, data.market, data.bookmaker, data.odds, data.awayTeam, data.homeTeam, data.playerTeam, data.playerPosition, data.line, data.side);
  };

  // Scratchpad helpers
  const isInScratchpad = (data) => {
    if (!data) return false;
    return scratchpadItems.some(it => it.playerId === data.playerId && it.gameId === data.gameId);
  };
  const handleToggleScratchpad = (data) => {
    if (!data) return;
    setScratchpadItems(prev => {
      const exists = prev.some(it => it.playerId === data.playerId && it.gameId === data.gameId);
      if (exists) {
        return prev.filter(it => !(it.playerId === data.playerId && it.gameId === data.gameId));
      }
      // Resolve away/home teams: prefer the cell-level data (odds right-click
      // already passes them), otherwise look up the current game by id.
      let awayTeam = data.awayTeam || null;
      let homeTeam = data.homeTeam || null;
      if (!awayTeam || !homeTeam) {
        const game = filteredGamesForRender.find(g => g.gameIDmlb === data.gameId)
          || games.find(g => g.gameIDmlb === data.gameId)
          || nflGames.find(g => g.gameIDmlb === data.gameId)
          || ncaafGames.find(g => g.gameIDmlb === data.gameId);
        if (game) {
          awayTeam = awayTeam || game.away || null;
          homeTeam = homeTeam || game.home || null;
        }
      }
      // Resolve gameDateKey (YYYYMMDD) the same way the rest of the app does:
      // prefer the per-game field if the API attached it, then the current
      // slate's metadata key, then derive from selectedDate (MM/DD/YYYY).
      // The server requires /^\d{8}$/ and silently drops items that fail —
      // so the last-resort fallback guarantees a valid key for every add.
      const game = filteredGamesForRender.find(g => g.gameIDmlb === data.gameId)
        || games.find(g => g.gameIDmlb === data.gameId)
        || nflGames.find(g => g.gameIDmlb === data.gameId)
        || ncaafGames.find(g => g.gameIDmlb === data.gameId);
      const dateMatch = selectedDate?.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      const fallbackGdk = dateMatch ? `${dateMatch[3]}${dateMatch[1]}${dateMatch[2]}` : null;
      const gameDateKey = game?.gameDateKey
        || getCurrentGamesMetadata()?.gameDateKey
        || fallbackGdk
        || null;
      return [
        ...prev,
        {
          playerId: data.playerId,
          name: data.playerName,
          gameId: data.gameId,
          teamAbbr: data.playerTeam || null,
          awayTeam,
          homeTeam,
          sport: selectedSport,
          gameDateKey,
          addedAt: Date.now(),
        },
      ];
    });
  };
  const handleRemoveFromScratchpad = (item) => {
    setScratchpadItems(prev => prev.filter(it => !(it.playerId === item.playerId && it.gameId === item.gameId)));
  };
  const handleClearScratchpadDate = (dateKey) => {
    // dateKey matches the bucket the panel produced (YYYYMMDD gameDateKey,
    // or a YYYY-MM-DD fallback derived from addedAt, or "older").
    setScratchpadItems(prev => prev.filter(it => {
      const itemKey = it.gameDateKey
        || (it.addedAt ? new Date(it.addedAt).toISOString().slice(0, 10) : 'older');
      return itemKey !== dateKey;
    }));
  };

  // Close player modal
  const handleClosePlayerModal = () => {
    setShowPlayerModal(false);
    setPlayerModalData({
      gameId: null,
      playerId: null,
      playerName: null,
      market: null,
      awayTeam: null,
      homeTeam: null
    });
  };

  // Check if returning from checkout
  const returningFromCheckout = React.useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('checkout_complete') || localStorage.getItem('checkout_in_progress') === 'true';
  }, []);

  // Clear checkout flag after detecting it
  React.useEffect(() => {
    if (returningFromCheckout && !authLoading) {
      localStorage.removeItem('checkout_in_progress');
      // Clean up URL parameter
      const url = new URL(window.location.href);
      url.searchParams.delete('checkout_complete');
      window.history.replaceState({}, '', url);
    }
  }, [returningFromCheckout, authLoading]);

  // Check for new announcements
  React.useEffect(() => {
    if (!announcements) {
      setHasNewAnnouncements(false);
      return;
    }

    // Get last seen announcement from localStorage
    const lastSeenAnnouncement = localStorage.getItem('lastSeenAnnouncement');

    // Check if current announcement is different from last seen
    if (lastSeenAnnouncement !== announcements) {
      setHasNewAnnouncements(true);
    } else {
      setHasNewAnnouncements(false);
    }
  }, [announcements]);

  // Handle opening announcements modal
  const handleOpenAnnouncements = () => {
    setShowAnnouncementsModal(true);
    // Mark announcements as seen
    if (announcements) {
      localStorage.setItem('lastSeenAnnouncement', announcements);
      setHasNewAnnouncements(false);
    }
  };

  // Show loading state while checking authentication
  if (authLoading) {
    const isCheckoutReturn = returningFromCheckout;

    return (
      <div className="App" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '40px'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: `4px solid ${isCheckoutReturn ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.3)'}`,
            borderTopColor: '#3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <div style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#fff',
            marginBottom: '8px'
          }}>
            {isCheckoutReturn ? 'Activating your subscription' : 'Loading...'}
          </div>
          <div style={{
            fontSize: '14px',
            color: '#cbd5e1'
          }}>
            {isCheckoutReturn ? 'Please wait while we activate your subscription' : 'Please wait while we load your session'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <AuthWrapper>
      <div className="App">
      {/* Test Mode Banner */}
      {isTestMode && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          backgroundColor: '#f59e0b',
          color: '#000',
          padding: '8px',
          textAlign: 'center',
          fontWeight: 'bold',
          fontSize: '14px',
          zIndex: 9999,
          borderBottom: '2px solid #d97706'
        }}>
          ⚠️ TEST MODE ACTIVE - Using test plan IDs
        </div>
      )}
      <Navbar bg="dark" variant="dark" fixed="top" className="flex-column p-0" style={isTestMode ? { top: '40px' } : {}}>
        <Container className="d-flex justify-content-between align-items-center w-100 px-3">
          <div className="d-flex flex-column align-items-start">
            <div className="d-flex align-items-center gap-3">
              {/* Mobile Menu Icon - moved to left */}
              {isMobile && (
                <div className="mobile-menu-container">
                  <Button
                    variant="outline-light"
                    size="sm"
                    onClick={() => setShowMobileMenu(!showMobileMenu)}
                    style={{ 
                      border: 'none',
                      fontSize: '18px',
                      padding: '4px 8px'
                    }}
                  >
                    ☰
                  </Button>
                </div>
              )}
              <Navbar.Brand className="fw-bold mb-0" style={isMobile ? { marginLeft: '-10px' } : {}}>
                simpleOdds <EdgeBlocks />
                {isMobile && (
                  <span style={{
                    marginLeft: '3px',
                    fontSize: '0.8em',
                    fontWeight: 'normal',
                    color: '#9ca3af',
                    textTransform: 'uppercase'
                  }}>
                    {selectedSport}
                  </span>
                )}
              </Navbar.Brand>
              {/* Desktop Sport Toggle — order/labels driven by SPORTS_NAV. */}
              {!isMobile && (
                <div className="btn-group" role="group">
                  {SPORTS_NAV.map(({ key, label, clearAnalyticsOnSelect }) => (
                    <React.Fragment key={key}>
                      <input
                        type="radio"
                        className="btn-check"
                        name="sport-toggle"
                        id={`${key}-radio`}
                        checked={selectedSport === key}
                        onChange={() => {
                          setSelectedSport(key);
                          if (clearAnalyticsOnSelect && analyticsMode) setAnalyticsMode(false);
                        }}
                      />
                      <label className="btn btn-outline-warning btn-sm" htmlFor={`${key}-radio`}>
                        {label}
                      </label>
                    </React.Fragment>
                  ))}
                </div>
              )}
              
            </div>
            <input
              type="date"
              value={formatDateForInput(selectedDate)}
              onChange={handleDateChange}
              className="bg-dark text-white border-0"
              style={{ 
                fontSize: '12px',
                marginTop: '-2px',
                cursor: 'pointer',
                outline: 'none',
                colorScheme: 'dark'
              }}
            />
          </div>
          <div className="d-flex align-items-center" style={{ gap: '8px' }}>
            {/* Desktop Best Bets Button */}
            {!isMobile && !analyticsMode && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginRight: '8px' }}>
                <Button
                  variant="success"
                  size="sm"
                  onClick={() => setShowBestBetsPanel(!showBestBetsPanel)}
                  className="fw-bold"
                >
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center'
                  }}>
                    <span style={{ marginRight: '4px' }}>⭐</span>
                    Best Bets ({bestBetsPanelData.length})
                    {isDevModeEnabled('allbestbets') && (
                      <span style={{
                        color: '#ff6b6b',
                        fontSize: '16px',
                        marginLeft: '4px'
                      }}>
                        ALL
                      </span>
                    )}
                  </span>
                </Button>
                <button
                  onClick={() => setShowBestBetsFaq(true)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#94a3b8',
                    transition: 'color 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.color = '#ffffff'}
                  onMouseLeave={(e) => e.target.style.color = '#94a3b8'}
                  title="Best Bets FAQ"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                    <line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                </button>
              </div>
            )}
            {/* Announcements Button - HIDDEN FOR NOW */}
            {/* {isAuthenticated && (
              <button
                onClick={handleOpenAnnouncements}
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: 'transparent',
                  border: hasNewAnnouncements ? '2px solid #fbbf24' : '2px solid #94a3b8',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: hasNewAnnouncements
                    ? '0 0 12px rgba(251, 191, 36, 0.5), 0 2px 8px rgba(0, 0, 0, 0.3)'
                    : '0 2px 8px rgba(0, 0, 0, 0.3)',
                  transition: 'all 0.2s ease',
                  padding: 0,
                  position: 'relative',
                  animation: hasNewAnnouncements ? 'pulse-announcement 2s ease-in-out infinite' : 'none'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = hasNewAnnouncements
                    ? '0 0 16px rgba(251, 191, 36, 0.7), 0 4px 12px rgba(0, 0, 0, 0.5)'
                    : '0 4px 12px rgba(0, 0, 0, 0.5)';
                  e.currentTarget.style.borderColor = hasNewAnnouncements ? '#fbbf24' : '#ffffff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = hasNewAnnouncements
                    ? '0 0 12px rgba(251, 191, 36, 0.5), 0 2px 8px rgba(0, 0, 0, 0.3)'
                    : '0 2px 8px rgba(0, 0, 0, 0.3)';
                  e.currentTarget.style.borderColor = hasNewAnnouncements ? '#fbbf24' : '#94a3b8';
                }}
                title={hasNewAnnouncements ? "New Announcements!" : "Announcements"}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={hasNewAnnouncements ? '#fbbf24' : 'white'}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 11l18-5v12L3 13v-2z"/>
                  <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/>
                </svg>
                {hasNewAnnouncements && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '-2px',
                      right: '-2px',
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      backgroundColor: '#fbbf24',
                      border: '2px solid #1f2937',
                      animation: 'pulse-dot 1.5s ease-in-out infinite'
                    }}
                  />
                )}
              </button>
            )} */}
            {/* Contact Us Button - only for authenticated users */}
            {isAuthenticated && (
              <div
                style={{ position: 'relative' }}
                onMouseEnter={(e) => {
                  const tooltip = e.currentTarget.querySelector('.contact-tooltip');
                  if (tooltip) {
                    tooltip.style.opacity = '1';
                    tooltip.style.visibility = 'visible';
                  }
                }}
                onMouseLeave={(e) => {
                  const tooltip = e.currentTarget.querySelector('.contact-tooltip');
                  if (tooltip) {
                    tooltip.style.opacity = '0';
                    tooltip.style.visibility = 'hidden';
                  }
                }}
              >
                <button
                  onClick={() => setShowContactModal(true)}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: 'transparent',
                    border: '2px solid #94a3b8',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                    transition: 'all 0.2s ease',
                    padding: 0
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'scale(1.05)';
                    e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.5)';
                    e.target.style.borderColor = '#ffffff';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'scale(1)';
                    e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.3)';
                    e.target.style.borderColor = '#94a3b8';
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  </svg>
                </button>
                <span
                  className="contact-tooltip"
                  style={{
                    position: 'absolute',
                    bottom: '-32px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: '#1f2937',
                    color: '#f3f4f6',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    whiteSpace: 'nowrap',
                    opacity: 0,
                    visibility: 'hidden',
                    transition: 'opacity 0.15s ease, visibility 0.15s ease',
                    pointerEvents: 'none',
                    zIndex: 1000,
                    border: '1px solid #374151'
                  }}
                >
                  Contact Us
                </span>
              </div>
            )}
            <UserMenu
              onSettingsClick={() => setShowSettings(true)}
              onLoginClick={() => setAboutSectionExpanded(false)}
            />
          </div>
        </Container>
      </Navbar>

      {/* Date Navigation Arrows with Market Controls */}
      <div
        ref={marketBannerRef}
        className="d-flex flex-column"
        style={{
          position: 'fixed',
          top: '55px',
          left: 0,
          right: 0,
          zIndex: 1000,
          backgroundColor: 'rgba(52, 58, 64, 0.95)',
          borderBottom: '1px solid #495057',
          padding: '8px 0'
        }}
      >
        {/* Game badges row - centered (hidden for NCAAF, NCAAB, Soccer). NFL
            now included — badges wrap to multiple rows on a full slate. */}
        {!isMobile && selectedSport !== 'ncaaf' && selectedSport !== 'ncaab' && selectedSport !== 'soccer' && (
          <div className="d-flex justify-content-center mb-2">
            {getCurrentGamesData().length > 0 ? (
              <div className="d-flex flex-wrap justify-content-center gap-1">
                {getCurrentGamesData().map((game) => (
                  <span
                    key={game.gameIDmlb}
                    className={`badge game-badge ${game.status === 'Live' ? 'bg-success text-warning' : isGameFinished(game.status) ? 'bg-light text-dark' : 'bg-info text-dark'}`}
                    style={{ cursor: 'pointer', fontSize: '10px' }}
                    onClick={() => {
                      const el = document.getElementById(`game-${game.gameIDmlb}`);
                      if (el) window.scrollTo({ top: el.offsetTop - 100, behavior: 'smooth' });
                    }}
                  >
                    {game.status === 'Live' && <span className="live-dot-nav"></span>}
                    {`${game.away}@${game.home}`}
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-muted" style={{ fontSize: '12px' }}>
                No games available for this date
              </span>
            )}
          </div>
        )}

        {/* Conference badges for NCAAB / NCAAF */}
        {!isMobile && (selectedSport === 'ncaab' || selectedSport === 'ncaaf') && (
          <div className="d-flex align-items-center mb-2" style={{ position: 'relative' }}>
            <div className="flex-grow-1 d-flex flex-column align-items-center">
            {getCurrentGamesData().length > 0 ? (
              (() => {
                // Extract unique conferences from all games
                const conferences = new Set();
                getCurrentGamesData().forEach(game => {
                  if (game.homeConferenceName) conferences.add(game.homeConferenceName);
                  if (game.awayConferenceName) conferences.add(game.awayConferenceName);
                });
                const sortedConferences = [...conferences].sort();
                const allEnabled = sortedConferences.length > 0 && sortedConferences.every(conf => enabledConferences.has(conf)) && !top25FilterEnabled;

                // Render Top25 badge
                const renderTop25Badge = () => (
                  <span
                    key="top25"
                    className={`badge ${top25FilterEnabled ? 'bg-info text-dark' : 'bg-light text-dark'}`}
                    style={{
                      cursor: 'pointer',
                      fontSize: '10px',
                      opacity: top25FilterEnabled ? 1 : 0.6
                    }}
                    onClick={() => {
                      if (top25FilterEnabled) {
                        // Disable Top25, re-enable all conferences
                        setTop25FilterEnabled(false);
                        setEnabledConferences(new Set(sortedConferences));
                      } else {
                        // Enable Top25, disable all conferences
                        setTop25FilterEnabled(true);
                        setEnabledConferences(new Set());
                      }
                    }}
                  >
                    Top 25
                  </span>
                );

                // Render a single conference badge
                const renderBadge = (conf, isAll = false) => {
                  const isEnabled = isAll ? allEnabled : (enabledConferences.has(conf) && !top25FilterEnabled);
                  return (
                    <span
                      key={conf}
                      className={`badge ${isEnabled ? 'bg-info text-dark' : 'bg-light text-dark'}`}
                      style={{
                        cursor: 'pointer',
                        fontSize: '10px',
                        opacity: isEnabled ? 1 : (top25FilterEnabled ? 0.4 : 0.6)
                      }}
                      onClick={() => {
                        // If Top25 is enabled, disable it and enable only the clicked conference
                        if (top25FilterEnabled) {
                          setTop25FilterEnabled(false);
                          if (isAll) {
                            // Enable all conferences
                            setEnabledConferences(new Set(sortedConferences));
                          } else {
                            // Enable only the clicked conference
                            setEnabledConferences(new Set([conf]));
                          }
                          return;
                        }
                        if (isAll) {
                          // Toggle all conferences
                          setEnabledConferences(prev => {
                            if (allEnabled) {
                              // All are enabled, so disable all
                              return new Set();
                            } else {
                              // Enable all conferences
                              return new Set(sortedConferences);
                            }
                          });
                        } else {
                          setEnabledConferences(prev => {
                            const newSet = new Set(prev);
                            if (newSet.has(conf)) {
                              newSet.delete(conf);
                            } else {
                              newSet.add(conf);
                            }
                            return newSet;
                          });
                        }
                      }}
                    >
                      {conf}
                    </span>
                  );
                };

                // If 10 or more conferences, split into 2 rows with All badge
                if (sortedConferences.length >= 10) {
                  const midpoint = Math.ceil(sortedConferences.length / 2);
                  const row1 = sortedConferences.slice(0, midpoint);
                  const row2 = sortedConferences.slice(midpoint);

                  return (
                    <div className="d-flex flex-column align-items-center gap-1">
                      <div className="d-flex flex-wrap justify-content-center gap-1">
                        {renderTop25Badge()}
                        {renderBadge('All', true)}
                        {row1.map(conf => renderBadge(conf))}
                      </div>
                      <div className="d-flex flex-wrap justify-content-center gap-1">
                        {row2.map(conf => renderBadge(conf))}
                      </div>
                    </div>
                  );
                } else {
                  // Single row for fewer conferences
                  return (
                    <div className="d-flex flex-wrap justify-content-center gap-1">
                      {renderTop25Badge()}
                      {renderBadge('All', true)}
                      {sortedConferences.map(conf => renderBadge(conf))}
                    </div>
                  );
                }
              })()
            ) : (
              <span className="text-muted" style={{ fontSize: '12px' }}>
                No games available
              </span>
            )}
            </div>
            {/* Betslip Button (NCAAB desktop only — NCAAF shares this
                conference-badges row but not the betslip). */}
            {selectedSport === 'ncaab' && (
              <button
                type="button"
                className={`btn btn-sm ${showBetslipPanel ? 'btn-info' : 'btn-outline-info'}`}
                onClick={() => { const next = !showBetslipPanel; setShowBetslipPanel(next); if (next) setActiveRightPanel('betslip'); setShowMobileTrends(false); }}
                style={{ fontSize: '0.75rem', padding: '4px 12px', fontWeight: 'bold', position: 'absolute', right: '12px' }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '4px', verticalAlign: 'text-bottom' }}>
                  <path d="M4 4h16v4.5c-1.1 0-2 .9-2 2s.9 2 2 2V20H4v-7.5c1.1 0 2-.9 2-2s-.9-2-2-2V4z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                </svg>
                Betslip{betslipItems.length > 0 ? ` (${betslipItems.length})` : ''}
              </button>
            )}
          </div>
        )}

        {/* NCAAF time-slot badges (desktop). Composes with the conference
            filter above — a game must match a selected conference AND a
            selected start-time badge. Hidden for now; flip the false to
            true to re-enable. */}
        {false && !isMobile && selectedSport === 'ncaaf' && getCurrentGamesData().length > 0 && (() => {
          const slotSet = new Set();
          getCurrentGamesData().forEach(game => {
            const key = getNcaafTimeSlotKey(game);
            if (key) slotSet.add(key);
          });
          if (slotSet.size <= 1) return null;
          const sortedSlots = [...slotSet].sort();
          const allEnabled = sortedSlots.length > 0 && sortedSlots.every(s => enabledNcaafTimeSlots.has(s));

          const renderSlotBadge = (slot, isAll = false) => {
            const isEnabled = isAll ? allEnabled : enabledNcaafTimeSlots.has(slot);
            return (
              <span
                key={isAll ? '__all__' : slot}
                className={`badge ${isEnabled ? 'bg-info text-dark' : 'bg-light text-dark'}`}
                style={{ cursor: 'pointer', fontSize: '10px', opacity: isEnabled ? 1 : 0.6 }}
                onClick={() => {
                  if (isAll) {
                    setEnabledNcaafTimeSlots(allEnabled ? new Set() : new Set(sortedSlots));
                    return;
                  }
                  setEnabledNcaafTimeSlots(prev => {
                    const next = new Set(prev);
                    if (next.has(slot)) next.delete(slot);
                    else next.add(slot);
                    return next;
                  });
                }}
              >
                {isAll ? 'All' : formatTimeSlotLabel(slot)}
              </span>
            );
          };

          return (
            <div className="d-flex justify-content-center mb-2">
              <div className="d-flex flex-wrap justify-content-center gap-1">
                {renderSlotBadge(null, true)}
                {sortedSlots.map(s => renderSlotBadge(s))}
              </div>
            </div>
          );
        })()}

        {/* Mobile Conference Selector for NCAAB / NCAAF */}
        {isMobile && (selectedSport === 'ncaab' || selectedSport === 'ncaaf') && (
          <div className="d-flex justify-content-center mb-2">
            <button
              onClick={() => setShowConferencePopup(true)}
              style={{
                backgroundColor: '#1a1a2e',
                border: '1px solid #3B82F6',
                borderRadius: '6px',
                padding: '8px 16px',
                color: 'white',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <span>Conferences</span>
              <span style={{
                backgroundColor: '#3B82F6',
                borderRadius: '10px',
                padding: '2px 8px',
                fontSize: '10px'
              }}>
                {top25FilterEnabled ? 'Top 25' : enabledConferences.size === 0 ? 'None' :
                  (() => {
                    const conferences = new Set();
                    getCurrentGamesData().forEach(game => {
                      if (game.homeConferenceName) conferences.add(game.homeConferenceName);
                      if (game.awayConferenceName) conferences.add(game.awayConferenceName);
                    });
                    return enabledConferences.size === conferences.size ? 'All' : enabledConferences.size;
                  })()
                }
              </span>
            </button>
          </div>
        )}

        {/* Conference Selection Popup for Mobile NCAAB */}
        {showConferencePopup && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              zIndex: 9999,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '20px'
            }}
            onClick={() => setShowConferencePopup(false)}
          >
            <div
              style={{
                backgroundColor: '#1a1a2e',
                borderRadius: '12px',
                padding: '20px',
                maxWidth: '350px',
                width: '100%',
                maxHeight: '80vh',
                overflowY: 'auto',
                border: '1px solid #333'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
                borderBottom: '1px solid #333',
                paddingBottom: '12px'
              }}>
                <h5 style={{ margin: 0, color: 'white', fontSize: '16px' }}>Select Conferences</h5>
                <button
                  onClick={() => setShowConferencePopup(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#9ca3af',
                    fontSize: '20px',
                    cursor: 'pointer',
                    padding: '0'
                  }}
                >
                  ×
                </button>
              </div>

              {(() => {
                const conferences = new Set();
                getCurrentGamesData().forEach(game => {
                  if (game.homeConferenceName) conferences.add(game.homeConferenceName);
                  if (game.awayConferenceName) conferences.add(game.awayConferenceName);
                });
                const sortedConferences = [...conferences].sort();
                const allEnabled = sortedConferences.length > 0 && sortedConferences.every(conf => enabledConferences.has(conf)) && !top25FilterEnabled;

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {/* Top 25 Option */}
                    <div
                      onClick={() => {
                        if (top25FilterEnabled) {
                          setTop25FilterEnabled(false);
                          setEnabledConferences(new Set(sortedConferences));
                        } else {
                          setTop25FilterEnabled(true);
                          setEnabledConferences(new Set());
                        }
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px',
                        backgroundColor: top25FilterEnabled ? '#3B82F6' : '#2a2a3e',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        gap: '12px'
                      }}
                    >
                      <span style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '4px',
                        border: '2px solid ' + (top25FilterEnabled ? 'white' : '#666'),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '14px'
                      }}>
                        {top25FilterEnabled && '✓'}
                      </span>
                      <span style={{ color: 'white', fontSize: '14px', fontWeight: '500' }}>Top 25</span>
                    </div>

                    {/* All Option */}
                    <div
                      onClick={() => {
                        setTop25FilterEnabled(false);
                        if (allEnabled) {
                          setEnabledConferences(new Set());
                        } else {
                          setEnabledConferences(new Set(sortedConferences));
                        }
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px',
                        backgroundColor: allEnabled ? '#3B82F6' : '#2a2a3e',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        gap: '12px'
                      }}
                    >
                      <span style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '4px',
                        border: '2px solid ' + (allEnabled ? 'white' : '#666'),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '14px'
                      }}>
                        {allEnabled && '✓'}
                      </span>
                      <span style={{ color: 'white', fontSize: '14px', fontWeight: '500' }}>All Conferences</span>
                    </div>

                    <div style={{ borderTop: '1px solid #333', margin: '8px 0' }} />

                    {/* Individual Conferences */}
                    {sortedConferences.map(conf => {
                      const isEnabled = enabledConferences.has(conf) && !top25FilterEnabled;
                      return (
                        <div
                          key={conf}
                          onClick={() => {
                            if (top25FilterEnabled) {
                              setTop25FilterEnabled(false);
                              setEnabledConferences(new Set([conf]));
                            } else {
                              const newSet = new Set(enabledConferences);
                              if (isEnabled) {
                                newSet.delete(conf);
                              } else {
                                newSet.add(conf);
                              }
                              setEnabledConferences(newSet);
                            }
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '12px',
                            backgroundColor: isEnabled ? '#3B82F6' : '#2a2a3e',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            gap: '12px'
                          }}
                        >
                          <span style={{
                            width: '20px',
                            height: '20px',
                            borderRadius: '4px',
                            border: '2px solid ' + (isEnabled ? 'white' : '#666'),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '14px'
                          }}>
                            {isEnabled && '✓'}
                          </span>
                          <span style={{ color: 'white', fontSize: '14px' }}>{conf}</span>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* League badges for Soccer */}
        {!isMobile && selectedSport === 'soccer' && (
          <div className="d-flex flex-column align-items-center mb-2">
            {getCurrentGamesData().length > 0 ? (
              (() => {
                // Extract unique leagues from all games
                const leagues = new Set();
                getCurrentGamesData().forEach(game => {
                  if (game.leagueName) leagues.add(game.leagueName);
                });
                const sortedLeagues = [...leagues].sort();
                const allEnabled = sortedLeagues.length > 0 && sortedLeagues.every(league => enabledLeagues.has(league));

                // Render a single league badge
                const renderBadge = (league, isAll = false) => {
                  const isEnabled = isAll ? allEnabled : enabledLeagues.has(league);
                  return (
                    <span
                      key={league}
                      className={`badge ${isEnabled ? 'bg-info text-dark' : 'bg-light text-dark'}`}
                      style={{
                        cursor: 'pointer',
                        fontSize: '10px',
                        opacity: isEnabled ? 1 : 0.6
                      }}
                      onClick={() => {
                        if (isAll) {
                          // Toggle all leagues
                          setEnabledLeagues(prev => {
                            if (allEnabled) {
                              return new Set();
                            } else {
                              return new Set(sortedLeagues);
                            }
                          });
                        } else {
                          setEnabledLeagues(prev => {
                            const newSet = new Set(prev);
                            if (newSet.has(league)) {
                              newSet.delete(league);
                            } else {
                              newSet.add(league);
                            }
                            return newSet;
                          });
                        }
                      }}
                    >
                      {league}
                    </span>
                  );
                };

                // If 6 or more leagues, split into 2 rows with All badge
                if (sortedLeagues.length >= 6) {
                  const midpoint = Math.ceil(sortedLeagues.length / 2);
                  const row1 = sortedLeagues.slice(0, midpoint);
                  const row2 = sortedLeagues.slice(midpoint);

                  return (
                    <div className="d-flex flex-column align-items-center gap-1">
                      <div className="d-flex flex-wrap justify-content-center gap-1">
                        {renderBadge('All', true)}
                        {row1.map(league => renderBadge(league))}
                      </div>
                      <div className="d-flex flex-wrap justify-content-center gap-1">
                        {row2.map(league => renderBadge(league))}
                      </div>
                    </div>
                  );
                } else {
                  // Single row for fewer leagues
                  return (
                    <div className="d-flex flex-wrap justify-content-center gap-1">
                      {sortedLeagues.length > 1 && renderBadge('All', true)}
                      {sortedLeagues.map(league => renderBadge(league))}
                    </div>
                  );
                }
              })()
            ) : (
              <span className="text-muted" style={{ fontSize: '12px' }}>
                No games available
              </span>
            )}
          </div>
        )}
        
        {/* Analytics controls row - centered */}
        {analyticsMode && (
          <div className="d-flex flex-column justify-content-center align-items-center px-3" style={{ gap: '8px' }}>
            {/* Analytics Header and Odds Toggle */}
            <div className="d-flex align-items-center justify-content-center" style={{ gap: isMobile ? '12px' : '16px' }}>
              <div className="text-white" style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: 'bold' }}>
                Matchups
              </div>
              
              {/* Odds Toggle */}
              <div className="form-check form-switch mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="odds-toggle"
                  checked={false}
                  onChange={() => setAnalyticsMode(false)}
                />
                <label className="form-check-label text-white" htmlFor="odds-toggle" style={{ 
                  fontSize: isMobile ? '12px' : '14px',
                  opacity: 0.8,
                  marginLeft: '4px'
                }}>
                  Odds
                </label>
              </div>
            </div>
          </div>
        )}
        
        {/* NCAAF Week Navigator */}
        {selectedSport === 'ncaaf' && !analyticsMode && (
          <div className="d-flex flex-column justify-content-center align-items-center px-3" style={{ gap: '8px' }}>
            {calendarLoading && (
              <div className="text-white small">Loading weeks...</div>
            )}
            {calendarError && (
              <div className="text-danger small">Error loading calendar: {calendarError}</div>
            )}
            {calendarData && (
              <>
                {isMobile ? (
                  <>
                    <select
                      className="form-select form-select-sm"
                      value={selectedWeek}
                      onChange={(e) => setSelectedWeek(parseInt(e.target.value))}
                      style={{ maxWidth: '200px' }}
                    >
                      {calendarData.weeks.map(week => (
                        <option key={week.value} value={week.value}>
                          {week.label}
                        </option>
                      ))}
                    </select>
                    {getSelectedWeekData() && (
                      <div className="text-muted small text-center">
                        {getSelectedWeekData().detail}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="d-flex align-items-center justify-content-center" style={{ gap: '8px' }}>
                    <div className="text-muted small">Week:</div>
                    <select
                      className="form-select form-select-sm"
                      value={selectedWeek ?? ''}
                      onChange={(e) => setSelectedWeek(parseInt(e.target.value))}
                      style={{ maxWidth: '220px', width: 'auto' }}
                    >
                      {calendarData.weeks.map(week => (
                        <option key={week.value} value={week.value} title={week.detail}>
                          {week.label}
                        </option>
                      ))}
                    </select>
                    {getSelectedWeekData() && (
                      <div className="text-muted small">
                        {getSelectedWeekData().detail}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* NFL Week Navigator */}
        {selectedSport === 'nfl' && !analyticsMode && (
          <div className="d-flex flex-column justify-content-center align-items-center px-3" style={{ gap: '8px' }}>
            {nflCalendarLoading && (
              <div className="text-white small">Loading weeks...</div>
            )}
            {nflCalendarError && (
              <div className="text-danger small">Error loading calendar: {nflCalendarError}</div>
            )}
            {nflCalendarData && (
              <div className="d-flex align-items-center justify-content-center" style={{ marginBottom: '5px' }}>
                <select
                  className="form-select form-select-sm"
                  value={nflSelectedWeek}
                  onChange={(e) => {
                    const val = e.target.value;
                    // Handle both regular weeks (numbers) and playoff weeks (e.g., "playoff-1")
                    setNflSelectedWeek(val.startsWith('playoff-') ? val : parseInt(val));
                  }}
                  style={{
                    backgroundColor: '#2c3e50',
                    color: 'white',
                    border: '1px solid #495057',
                    fontSize: isMobile ? '12px' : '14px',
                    maxWidth: isMobile ? '120px' : '160px'
                  }}
                >
                  {nflCalendarData.weeks.map(week => (
                    <option key={week.value} value={week.value}>
                      {week.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}

        {/* Props controls row - centered (hidden for NCAAB) */}
        {!analyticsMode && selectedSport !== 'ncaab' && (
          <div className="d-flex flex-column justify-content-center align-items-center px-3" style={{ gap: '8px' }}>
            {/* Master Market Selection - centered */}
            <div className="d-flex align-items-center justify-content-center" style={{ gap: isMobile ? '8px' : '12px', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
            {!isMobile && (
              <div className="text-muted small me-2">Prop:</div>
            )}
            {(() => {
              const masterMarkets = availableMarkets.filter(m => !m.excludeFromMaster);
              return isMobile && masterMarkets.length >= 10 ? (
                <div className="d-flex flex-column" style={{ gap: '4px' }}>
                  {/* Split markets into two rows */}
                  {[0, Math.ceil(masterMarkets.length / 2)].map((startIndex, rowIndex) => (
                    <div key={rowIndex} role="group" className="btn-group" style={{ gap: '2px' }}>
                      {masterMarkets.slice(startIndex, rowIndex === 0 ? Math.ceil(masterMarkets.length / 2) : masterMarkets.length).map(market => (
                      <React.Fragment key={market.key}>
                        <input
                          className="btn-check"
                          type="radio"
                          name="master-market"
                          id={`master-${market.key}`}
                          value={market.key}
                          checked={masterMarket === market.key}
                          onChange={() => handleMasterMarketSelection(market.key)}
                          autoComplete="off"
                        />
                        <label
                          htmlFor={`master-${market.key}`}
                          className={`btn ${masterMarket === market.key ? 'btn-primary' : 'btn-outline-primary'}`}
                          title={market.name}
                          style={{ 
                            fontSize: '11px',
                            padding: '6px 8px'
                          }}
                        >
                          {market.label}
                        </label>
                      </React.Fragment>
                    ))}
                  </div>
                ))}
              </div>
            ) : (
            /* Single row with mainlines first, then props */
            <div role="group" className="btn-group flex-nowrap" style={{ gap: '2px' }}>
                {/* Mainline Markets first */}
                {availableMarkets.filter(m => m.isMainline && !m.excludeFromMaster).map(market => (
                  <React.Fragment key={market.key}>
                    <input
                      className="btn-check"
                      type="radio"
                      name="master-market"
                      id={`master-${market.key}`}
                      value={market.key}
                      checked={masterMarket === market.key}
                      onChange={() => handleMasterMarketSelection(market.key)}
                      autoComplete="off"
                    />
                    <label
                      htmlFor={`master-${market.key}`}
                      className={`btn ${masterMarket === market.key ? 'btn-primary' : 'btn-outline-primary'}`}
                      title={market.name}
                      style={{ 
                        fontSize: isMobile ? '11px' : '12px',
                        padding: isMobile ? '6px 8px' : '6px 10px',
                        minWidth: isMobile ? 'auto' : '45px'
                      }}
                    >
                      {market.label}
                    </label>
                  </React.Fragment>
                ))}
                
                {/* Then Player Markets */}
                {availableMarkets.filter(m => !m.isMainline).map(market => (
                  <React.Fragment key={market.key}>
                    <input
                      className="btn-check"
                      type="radio"
                      name="master-market"
                      id={`master-${market.key}`}
                      value={market.key}
                      checked={masterMarket === market.key}
                      onChange={() => handleMasterMarketSelection(market.key)}
                      autoComplete="off"
                    />
                    <label
                      htmlFor={`master-${market.key}`}
                      className={`btn ${masterMarket === market.key ? 'btn-primary' : 'btn-outline-primary'}`}
                      title={market.name}
                      style={{ 
                        fontSize: isMobile ? '11px' : '12px',
                        padding: isMobile ? '6px 8px' : '6px 8px'
                      }}
                    >
                      {market.label}
                    </label>
                  </React.Fragment>
                ))}

                {/* NFL Tier Controls - inline with markets (desktop only) */}
                {selectedSport === 'nfl' && !isMobile && masterMarket && shouldShowTiers('nfl', masterMarket) && getAvailableTiersForMasterMarket().length > 0 && (
                  <>
                    {/* Tier Filter Dropdown */}
                    <Form.Select
                      value={masterTierFilter !== null ? masterTierFilter : '0.5'}
                      onChange={(e) => handleMasterTierFilter(e.target.value)}
                      size="sm"
                      style={{
                        width: 'auto',
                        minWidth: '80px',
                        fontSize: '12px',
                        marginLeft: '8px'
                      }}
                    >
                      {getAvailableTiersForMasterMarket().map(tier => (
                        <option key={tier} value={tier}>
                          {tier}
                        </option>
                      ))}
                    </Form.Select>

                    {/* Tier Toggle Switch */}
                    <div className="form-check form-switch" style={{ marginLeft: '8px', marginBottom: '0' }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="nfl-tier-toggle"
                        checked={masterShowTiers}
                        onChange={(e) => handleMasterTierToggle(e.target.checked)}
                        style={{ fontSize: '12px' }}
                      />
                      <label className="form-check-label text-muted" htmlFor="nfl-tier-toggle" style={{
                        fontSize: '11px',
                        opacity: 0.8,
                        marginLeft: '4px'
                      }}>
                        Tiers
                      </label>
                    </div>
                  </>
                )}

                {/* NHL Tier Controls - inline with markets (desktop only) */}
                {selectedSport === 'nhl' && !isMobile && masterMarket && shouldShowTiers('nhl', masterMarket) && getAvailableTiersForMasterMarket().length > 0 && (
                  <>
                    {/* Tier Filter Dropdown */}
                    <Form.Select
                      value={masterTierFilter !== null ? masterTierFilter : '0.5'}
                      onChange={(e) => handleMasterTierFilter(e.target.value)}
                      size="sm"
                      style={{
                        width: 'auto',
                        minWidth: '80px',
                        fontSize: '12px',
                        marginLeft: '8px'
                      }}
                    >
                      {getAvailableTiersForMasterMarket().map(tier => (
                        <option key={tier} value={tier}>
                          {tier}
                        </option>
                      ))}
                    </Form.Select>

                    {/* Tier Toggle Switch */}
                    <div className="form-check form-switch" style={{ marginLeft: '8px', marginBottom: '0' }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="nhl-tier-toggle"
                        checked={masterShowTiers}
                        onChange={(e) => handleMasterTierToggle(e.target.checked)}
                        style={{ fontSize: '12px' }}
                      />
                      <label className="form-check-label text-muted" htmlFor="nhl-tier-toggle" style={{
                        fontSize: '11px',
                        opacity: 0.8,
                        marginLeft: '4px'
                      }}>
                        Tiers
                      </label>
                    </div>
                  </>
                )}

                {/* Soccer Tier Controls - inline with markets (desktop only) */}
                {selectedSport === 'soccer' && !isMobile && masterMarket && (masterMarket === 'player_shots' || masterMarket === 'player_shots_on_target') && getAvailableTiersForMasterMarket().length > 0 && (
                  <>
                    {/* Tier Filter Dropdown */}
                    <Form.Select
                      value={masterTierFilter !== null ? masterTierFilter : '0.5'}
                      onChange={(e) => handleMasterTierFilter(e.target.value)}
                      size="sm"
                      style={{
                        width: 'auto',
                        minWidth: '80px',
                        fontSize: '12px',
                        marginLeft: '8px'
                      }}
                    >
                      {getAvailableTiersForMasterMarket().map(tier => (
                        <option key={tier} value={tier}>
                          {tier}
                        </option>
                      ))}
                    </Form.Select>

                    {/* Tier Toggle Switch */}
                    <div className="form-check form-switch" style={{ marginLeft: '8px', marginBottom: '0' }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="soccer-tier-toggle"
                        checked={masterShowTiers}
                        onChange={(e) => handleMasterTierToggle(e.target.checked)}
                        style={{ fontSize: '12px' }}
                      />
                      <label className="form-check-label text-muted" htmlFor="soccer-tier-toggle" style={{
                        fontSize: '11px',
                        opacity: 0.8,
                        marginLeft: '4px'
                      }}>
                        Tiers
                      </label>
                    </div>
                  </>
                )}

                {/* NBA/WNBA View Mode Toggle - inline with markets (desktop only) */}
                {/* Hide toggle for binary markets (first basket, scorer markets, etc.) and moneyline */}
                {(selectedSport === 'nba' || selectedSport === 'wnba') && !isMobile && !isBinaryMarket(masterMarket) && masterMarket !== 'h2h' && (() => {
                  console.log('Rendering NBA/WNBA toggle buttons (desktop)', { selectedSport, isMobile, nbaViewMode, masterMarket, isBinary: isBinaryMarket(masterMarket) });
                  return true;
                })() && (
                  <div className="btn-group btn-group-sm" role="group" style={{ marginLeft: '12px' }}>
                    <button
                      type="button"
                      className={`btn ${nbaViewMode === 'bestline' ? 'btn-warning' : 'btn-outline-warning'}`}
                      onClick={() => {
                        console.log('Desktop: Switching to Edge View mode');
                        setNbaViewMode('bestline');
                      }}
                      style={{ fontSize: '0.75rem', padding: '4px 12px' }}
                    >
                      Edge View
                    </button>
                    <button
                      type="button"
                      className={`btn ${nbaViewMode === 'ou' ? 'btn-warning' : 'btn-outline-warning'}`}
                      onClick={() => {
                        console.log('Desktop: Switching to O/U mode');
                        setNbaViewMode('ou');
                      }}
                      style={{ fontSize: '0.75rem', padding: '4px 12px' }}
                    >
                      O/U
                    </button>
                  </div>
                )}

                {/* Trends Button for NBA/WNBA (desktop only) - shows for all markets including binary */}
                {(selectedSport === 'nba' || selectedSport === 'wnba') && !isMobile && (
                  <button
                    type="button"
                    className={`btn btn-sm ${showIqOverlay ? 'btn-warning' : 'btn-outline-warning'}`}
                    onClick={() => { const next = !showIqOverlay; setShowIqOverlay(next); if (next) setShowLeadersOverlay(false); }}
                    style={{ fontSize: '0.75rem', padding: '4px 12px', marginLeft: '8px', fontWeight: 'bold' }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" stroke="currentColor" strokeWidth="0.5" viewBox="0 0 16 16" style={{ marginRight: '4px', verticalAlign: 'text-bottom' }}>
                      <path d="M0 0h1v15h15v1H0V0Zm14.817 3.113a.5.5 0 0 1 .07.704l-4.5 5.5a.5.5 0 0 1-.74.037L7.06 6.767l-3.656 5.027a.5.5 0 0 1-.808-.588l4-5.5a.5.5 0 0 1 .758-.06l2.609 2.61 4.15-5.073a.5.5 0 0 1 .704-.07Z"/>
                    </svg>
                    Trends
                  </button>
                )}

                {/* Leaders Button (desktop) for NBA + NFL — opens the daily
                    (NBA) or weekly (NFL) leaders page in the same fullscreen
                    iframe shell as expanded Trends. Icon sits inside a
                    circular outline to match Neg Corr. */}
                {(selectedSport === 'nba' || selectedSport === 'nfl') && !isMobile && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-warning circled-icon-btn"
                    onClick={() => { setShowLeadersOverlay(true); setShowNegCorrOverlay(false); }}
                    style={{ fontSize: '0.75rem', padding: '4px 12px', marginLeft: '8px', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                    title="Leaders"
                    aria-label="Leaders"
                  >
                    <span className="circled-icon-chip" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '22px', height: '22px', borderRadius: '50%', border: '1.5px solid #ffc107', backgroundColor: '#ffc107', color: '#000' }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M7 4h10v5a5 5 0 0 1-10 0V4Z" />
                        <path d="M7 6H4.5A1.5 1.5 0 0 0 3 7.5 3.5 3.5 0 0 0 6.5 11H7M17 6h2.5A1.5 1.5 0 0 1 21 7.5 3.5 3.5 0 0 1 17.5 11H17M12 14v3M8.5 20h7M10 17h4l.5 3h-5l.5-3Z" />
                      </svg>
                    </span>
                    Leaders
                  </button>
                )}

                {/* Matchups Button for MLB (desktop only) */}
                {selectedSport === 'mlb' && !isMobile && (
                  <button
                    type="button"
                    className={`btn btn-sm ${showMatchupsPanel ? 'btn-warning' : 'btn-outline-warning'}`}
                    onClick={() => setShowMatchupsPanel(!showMatchupsPanel)}
                    style={{ fontSize: '0.75rem', padding: '4px 12px', marginLeft: '8px', fontWeight: 'bold' }}
                  >
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '4px', verticalAlign: 'text-bottom' }}>
                      <path d="M8 1L14.5 8L8 15L1.5 8Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" fill="none"/>
                      <rect x="7" y="0" width="2" height="2" rx="0.3" fill="currentColor" transform="rotate(0 8 1)"/>
                      <rect x="13.5" y="7" width="2" height="2" rx="0.3" fill="currentColor"/>
                      <rect x="7" y="14" width="2" height="2" rx="0.3" fill="currentColor"/>
                      <rect x="0.5" y="7" width="2" height="2" rx="0.3" fill="currentColor"/>
                    </svg>
                    Matchups
                  </button>
                )}

                {/* Betslip Button (desktop) */}
                {(selectedSport === 'nba' || selectedSport === 'nhl' || selectedSport === 'ncaab' || selectedSport === 'mlb' || selectedSport === 'nfl') && !isMobile && (
                  <button
                    type="button"
                    className={`btn btn-sm ${showBetslipPanel ? 'btn-info' : 'btn-outline-info'}`}
                    onClick={() => { const next = !showBetslipPanel; setShowBetslipPanel(next); if (next) setActiveRightPanel('betslip'); setShowMobileTrends(false); }}
                    style={{ fontSize: '0.75rem', padding: '4px 12px', marginLeft: '8px', fontWeight: 'bold' }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '4px', verticalAlign: 'text-bottom' }}>
                      <path d="M4 4h16v4.5c-1.1 0-2 .9-2 2s.9 2 2 2V20H4v-7.5c1.1 0 2-.9 2-2s-.9-2-2-2V4z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                    </svg>
                    Betslip{betslipItems.length > 0 ? ` (${betslipItems.length})` : ''}
                  </button>
                )}

                {/* NFL Negative Correlation (desktop) — sits beside the
                    Betslip button. Opens the server-side correlation page
                    for the current NFL week in a fullscreen iframe
                    overlay. Icon sits inside a circular outline. */}
                {selectedSport === 'nfl' && !isMobile && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-warning circled-icon-btn"
                    onClick={() => { setShowNegCorrOverlay(true); setShowLeadersOverlay(false); }}
                    style={{ fontSize: '0.75rem', padding: '4px 12px', marginLeft: '8px', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                    title="Negative Correlation"
                    aria-label="Negative Correlation"
                  >
                    <span className="circled-icon-chip" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '22px', height: '22px', borderRadius: '50%', border: '1.5px solid #ffc107', backgroundColor: '#ffc107', color: '#000' }}>
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <line x1="4" y1="2.5" x2="4" y2="13.5" />
                        <polyline points="1.5 5 4 2.5 6.5 5" />
                        <line x1="12" y1="2.5" x2="12" y2="13.5" />
                        <polyline points="9.5 11 12 13.5 14.5 11" />
                      </svg>
                    </span>
                    Neg Corr
                  </button>
                )}

                {/* Soccer Auto Parlays (desktop) — sits beside the Betslip
                    button, mirrors the NFL Neg Corr pattern. Opens the
                    server-side auto-parlays page in a fullscreen iframe. */}
                {selectedSport === 'soccer' && !isMobile && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-warning"
                    onClick={() => setShowAutoParlaysOverlay(true)}
                    style={{ fontSize: '0.75rem', padding: '4px 12px', marginLeft: '8px', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                    title="Auto Parlays"
                    aria-label="Auto Parlays"
                  >
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <rect x="2.5" y="2.5" width="8" height="5" rx="1" />
                      <rect x="5.5" y="5.5" width="8" height="5" rx="1" />
                      <rect x="2.5" y="8.5" width="8" height="5" rx="1" />
                    </svg>
                    Auto Parlays
                  </button>
                )}

                {/* Best Bets Panel Button (desktop only) - temporarily hidden */}
                {false && !isMobile && (
                  <button
                    type="button"
                    className={`btn btn-sm ${showBestBetsPanel ? 'btn-success' : 'btn-outline-success'}`}
                    onClick={() => setShowBestBetsPanel(!showBestBetsPanel)}
                    style={{ fontSize: '0.75rem', padding: '4px 12px', marginLeft: '8px', fontWeight: 'bold' }}
                  >
                    ⭐ Best Bets ({bestBetsPanelData.length})
                  </button>
                )}

                {/* NFL View Mode Toggle - inline with markets (desktop only) */}
                {/* Show toggle for specific yard markets: PaYds, RuYds, RuAtt, Rec, ReYds */}
                {selectedSport === 'nfl' && !isMobile && ['player_pass_yds', 'player_rush_yds', 'player_rush_attempts', 'player_receptions', 'player_reception_yds', 'player_rush_reception_yds', 'player_field_goals'].includes(masterMarket) && (
                  <div className="btn-group btn-group-sm" role="group" style={{ marginLeft: '12px' }}>
                    <button
                      type="button"
                      className={`btn ${nflViewMode === 'bestline' ? 'btn-warning' : 'btn-outline-warning'}`}
                      onClick={() => setNflViewMode('bestline')}
                      style={{ fontSize: '0.75rem', padding: '4px 12px' }}
                    >
                      Edge View
                    </button>
                    <button
                      type="button"
                      className={`btn ${nflViewMode === 'ou' ? 'btn-warning' : 'btn-outline-warning'}`}
                      onClick={() => setNflViewMode('ou')}
                      style={{ fontSize: '0.75rem', padding: '4px 12px' }}
                    >
                      O/U
                    </button>
                    <button
                      type="button"
                      className={`btn ${nflViewMode === 'middles' ? 'btn-warning' : 'btn-outline-warning'}`}
                      onClick={() => setNflViewMode('middles')}
                      style={{ fontSize: '0.75rem', padding: '4px 12px' }}
                    >
                      Middles
                    </button>
                  </div>
                )}
              </div>
            );
            })()}
          </div>


          {/* Tier controls on separate line for mobile (MLB only) */}
          {isMobile && selectedSport === 'mlb' && masterMarket && (getAvailableTiersForMasterMarket().length > 0 || masterTierFilter === null) && !masterMarket.includes('first_basket') && masterMarket !== 'h2h' && (
            <div className="d-flex align-items-center" style={{ width: '100%' }}>
              {/* Collapse/Expand buttons - left side */}
              <div className="d-flex align-items-center" style={{ marginRight: '20px', gap: '8px' }}>
                <button
                  onClick={handleCollapseAll}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '4px',
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '18px',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease',
                    minWidth: '32px',
                    height: '32px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.target.style.color = 'white';
                    e.target.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.target.style.color = 'rgba(255, 255, 255, 0.8)';
                    e.target.style.transform = 'scale(1)';
                  }}
                >
                  −
                </button>
                <button
                  onClick={handleExpandAll}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '4px',
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '18px',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease',
                    minWidth: '32px',
                    height: '32px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.target.style.color = 'white';
                    e.target.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.target.style.color = 'rgba(255, 255, 255, 0.8)';
                    e.target.style.transform = 'scale(1)';
                  }}
                >
                  +
                </button>
              </div>

              {/* Center tier controls */}
              <div className="d-flex align-items-center flex-grow-1 justify-content-center" style={{ gap: '12px' }}>
                {/* Master Tier Filter */}
                {getAvailableTiersForMasterMarket().length > 0 && (
                  <Form.Select
                    value={masterTierFilter || ''}
                    onChange={(e) => handleMasterTierFilter(e.target.value || null)}
                    size="sm"
                    style={{
                      width: 'auto',
                      minWidth: '80px',
                      fontSize: '11px'
                    }}
                  >
                    <option value="">Tiers</option>
                    {getAvailableTiersForMasterMarket().map(tier => (
                      <option key={tier} value={tier}>
                        {tier}
                      </option>
                    ))}
                  </Form.Select>
                )}

                {/* Master Tier Toggle */}
                <div className="form-check form-switch mb-0" style={{
                  paddingLeft: '0',
                  position: 'relative'
                }}>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="master-tier-toggle"
                    checked={masterMarket === 'pitcher_outs' ? true : masterShowTiers}
                    onChange={(e) => handleMasterTierToggle(e.target.checked)}
                    disabled={selectedSport === 'wnba' || masterMarket === 'pitcher_outs'}
                    style={{
                      width: '4em',
                      height: '1.5em',
                      minWidth: '4em'
                    }}
                  />
                  <label
                    className="form-check-label text-white"
                    htmlFor="master-tier-toggle"
                    style={{
                      position: 'absolute',
                      left: '2.5em',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      fontSize: '9px',
                      fontWeight: 'bold',
                      opacity: selectedSport === 'wnba' ? 0.3 : 0.8,
                      pointerEvents: 'none',
                      userSelect: 'none'
                    }}
                  >
                    All
                  </label>
                </div>

                {/* Matchups Button (mobile MLB) */}
                {selectedSport === 'mlb' && (
                  <button
                    type="button"
                    className={`btn btn-sm ${showMatchupsPanel ? 'btn-warning' : 'btn-outline-warning'}`}
                    onClick={() => setShowMatchupsPanel(!showMatchupsPanel)}
                    style={{ fontSize: '0.7rem', padding: '3px 10px', fontWeight: 'bold' }}
                  >
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '3px', verticalAlign: 'text-bottom' }}>
                      <path d="M8 1L14.5 8L8 15L1.5 8Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" fill="none"/>
                      <rect x="7" y="0" width="2" height="2" rx="0.3" fill="currentColor"/>
                      <rect x="13.5" y="7" width="2" height="2" rx="0.3" fill="currentColor"/>
                      <rect x="7" y="14" width="2" height="2" rx="0.3" fill="currentColor"/>
                      <rect x="0.5" y="7" width="2" height="2" rx="0.3" fill="currentColor"/>
                    </svg>
                    Matchups
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Collapse/Expand buttons and tier controls for NHL on mobile */}
          {isMobile && selectedSport === 'nhl' && (
            <div className="d-flex align-items-center" style={{ width: '100%', marginBottom: '8px' }}>
              {/* Collapse/Expand buttons - left side */}
              <div className="d-flex align-items-center" style={{ marginRight: '20px', gap: '8px' }}>
                <button
                  onClick={handleCollapseAll}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '4px',
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '18px',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease',
                    minWidth: '32px',
                    height: '32px'
                  }}
                >
                  −
                </button>
                <button
                  onClick={handleExpandAll}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '4px',
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '18px',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease',
                    minWidth: '32px',
                    height: '32px'
                  }}
                >
                  +
                </button>
              </div>

              {/* Tier controls - right side */}
              {masterMarket && shouldShowTiers('nhl', masterMarket) && getAvailableTiersForMasterMarket().length > 0 && (
                <div className="d-flex align-items-center" style={{ gap: '12px' }}>
                  {/* Tier Filter Dropdown */}
                  <Form.Select
                    value={masterTierFilter !== null ? masterTierFilter : '0.5'}
                    onChange={(e) => handleMasterTierFilter(e.target.value)}
                    size="sm"
                    style={{
                      width: 'auto',
                      minWidth: '80px',
                      fontSize: '11px'
                    }}
                  >
                    {getAvailableTiersForMasterMarket().map(tier => (
                      <option key={tier} value={tier}>
                        {tier}
                      </option>
                    ))}
                  </Form.Select>

                  {/* Tier Toggle Switch */}
                  <div className="form-check form-switch mb-0" style={{
                    paddingLeft: '0',
                    position: 'relative'
                  }}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="mobile-nhl-tier-toggle"
                      checked={masterShowTiers}
                      onChange={(e) => handleMasterTierToggle(e.target.checked)}
                      style={{
                        width: '4em',
                        height: '1.5em',
                        minWidth: '4em'
                      }}
                    />
                    <label
                      className="form-check-label text-white"
                      htmlFor="mobile-nhl-tier-toggle"
                      style={{
                        position: 'absolute',
                        left: '2.5em',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: '9px',
                        fontWeight: 'bold',
                        opacity: 0.8,
                        pointerEvents: 'none',
                        userSelect: 'none'
                      }}
                    >
                      All
                    </label>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Collapse/Expand buttons for NBA/WNBA binary markets and h2h on mobile */}
          {isMobile && (selectedSport === 'nba' || selectedSport === 'wnba') && (isBinaryMarket(masterMarket) || masterMarket === 'h2h') && (
            <div className="d-flex align-items-center justify-content-between" style={{ width: '100%', marginBottom: '8px' }}>
              <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                <button
                  onClick={handleCollapseAll}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '4px',
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '18px',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease',
                    minWidth: '32px',
                    height: '32px'
                  }}
                >
                  −
                </button>
                <button
                  onClick={handleExpandAll}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '4px',
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '18px',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease',
                    minWidth: '32px',
                    height: '32px'
                  }}
                >
                  +
                </button>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-warning"
                onClick={() => { setShowMobileTrends(true); setShowBetslipPanel(false); setShowLeadersOverlay(false); }}
                style={{ fontSize: '0.75rem', padding: '4px 12px', fontWeight: 'bold' }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" stroke="currentColor" strokeWidth="0.5" viewBox="0 0 16 16" style={{ marginRight: '4px', verticalAlign: 'text-bottom' }}>
                  <path d="M0 0h1v15h15v1H0V0Zm14.817 3.113a.5.5 0 0 1 .07.704l-4.5 5.5a.5.5 0 0 1-.74.037L7.06 6.767l-3.656 5.027a.5.5 0 0 1-.808-.588l4-5.5a.5.5 0 0 1 .758-.06l2.609 2.61 4.15-5.073a.5.5 0 0 1 .704-.07Z"/>
                </svg>
                Trends
              </button>
              {/* NBA Leaders Button (mobile, binary/h2h row) — icon-only. */}
              {selectedSport === 'nba' && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-warning circled-icon-btn"
                  onClick={() => { setShowLeadersOverlay(true); setShowNegCorrOverlay(false); }}
                  style={{ padding: '2px', marginLeft: '4px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                  title="Leaders"
                  aria-label="Leaders"
                >
                  <span className="circled-icon-chip" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '26px', height: '26px', borderRadius: '50%', border: '1.5px solid #ffc107' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M7 4h10v5a5 5 0 0 1-10 0V4Z" />
                      <path d="M7 6H4.5A1.5 1.5 0 0 0 3 7.5 3.5 3.5 0 0 0 6.5 11H7M17 6h2.5A1.5 1.5 0 0 1 21 7.5 3.5 3.5 0 0 1 17.5 11H17M12 14v3M8.5 20h7M10 17h4l.5 3h-5l.5-3Z" />
                    </svg>
                  </span>
                </button>
              )}
              {/* Betslip Button */}
              {(
                <button
                  type="button"
                  className={`btn btn-sm ${showBetslipPanel ? 'btn-info' : 'btn-outline-info'}`}
                  onClick={() => { const next = !showBetslipPanel; setShowBetslipPanel(next); if (next) setActiveRightPanel('betslip'); setShowMobileTrends(false); }}
                  style={{ fontSize: '0.75rem', padding: '4px 12px', marginLeft: '4px', fontWeight: 'bold' }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '4px', verticalAlign: 'text-bottom' }}>
                    <path d="M4 4h16v4.5c-1.1 0-2 .9-2 2s.9 2 2 2V20H4v-7.5c1.1 0 2-.9 2-2s-.9-2-2-2V4z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  </svg>
                  Betslip{betslipItems.length > 0 ? ` (${betslipItems.length})` : ''}
                </button>
              )}
            </div>
          )}

          {/* Collapse/Expand buttons and View Mode toggle for NBA/WNBA on mobile */}
          {/* Hide toggle for binary markets (first basket, scorer markets, etc.) and moneyline */}
          {isMobile && (selectedSport === 'nba' || selectedSport === 'wnba') && !isBinaryMarket(masterMarket) && masterMarket !== 'h2h' && (() => {
            console.log('Rendering NBA/WNBA toggle buttons (mobile)', { selectedSport, isMobile, nbaViewMode, masterMarket, isBinary: isBinaryMarket(masterMarket) });
            return true;
          })() && (
            <div className="d-flex align-items-center justify-content-between" style={{ width: '100%', marginBottom: '8px' }}>
              <div className="d-flex align-items-center" style={{ marginRight: '20px', gap: '8px' }}>
              <button
                onClick={handleCollapseAll}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '4px',
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: '18px',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  minWidth: '32px',
                  height: '32px'
                }}
              >
                −
              </button>
              <button
                onClick={handleExpandAll}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '4px',
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: '18px',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  minWidth: '32px',
                  height: '32px'
                }}
              >
                +
              </button>
              </div>
              {/* View Mode Toggle */}
              <div className="btn-group btn-group-sm" role="group">
                <button
                  type="button"
                  className={`btn ${nbaViewMode === 'bestline' ? 'btn-warning' : 'btn-outline-warning'}`}
                  onClick={() => {
                    console.log('Mobile: Switching to Edge View mode');
                    setNbaViewMode('bestline');
                  }}
                  style={{ fontSize: '0.75rem', padding: '4px 12px' }}
                >
                  Edge View
                </button>
                <button
                  type="button"
                  className={`btn ${nbaViewMode === 'ou' ? 'btn-warning' : 'btn-outline-warning'}`}
                  onClick={() => {
                    console.log('Mobile: Switching to O/U mode');
                    setNbaViewMode('ou');
                  }}
                  style={{ fontSize: '0.75rem', padding: '4px 12px' }}
                >
                  O/U
                </button>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-warning"
                onClick={() => { setShowMobileTrends(true); setShowBetslipPanel(false); setShowLeadersOverlay(false); }}
                style={{ fontSize: '0.75rem', padding: '4px 12px', fontWeight: 'bold', marginLeft: '8px' }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" stroke="currentColor" strokeWidth="0.5" viewBox="0 0 16 16" style={{ marginRight: '4px', verticalAlign: 'text-bottom' }}>
                  <path d="M0 0h1v15h15v1H0V0Zm14.817 3.113a.5.5 0 0 1 .07.704l-4.5 5.5a.5.5 0 0 1-.74.037L7.06 6.767l-3.656 5.027a.5.5 0 0 1-.808-.588l4-5.5a.5.5 0 0 1 .758-.06l2.609 2.61 4.15-5.073a.5.5 0 0 1 .704-.07Z"/>
                </svg>
                Trends
              </button>
              {/* NBA Leaders Button (mobile, prop markets row) — icon-only. */}
              {selectedSport === 'nba' && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-warning circled-icon-btn"
                  onClick={() => { setShowLeadersOverlay(true); setShowNegCorrOverlay(false); }}
                  style={{ padding: '2px', marginLeft: '4px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                  title="Leaders"
                  aria-label="Leaders"
                >
                  <span className="circled-icon-chip" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '26px', height: '26px', borderRadius: '50%', border: '1.5px solid #ffc107' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M7 4h10v5a5 5 0 0 1-10 0V4Z" />
                      <path d="M7 6H4.5A1.5 1.5 0 0 0 3 7.5 3.5 3.5 0 0 0 6.5 11H7M17 6h2.5A1.5 1.5 0 0 1 21 7.5 3.5 3.5 0 0 1 17.5 11H17M12 14v3M8.5 20h7M10 17h4l.5 3h-5l.5-3Z" />
                    </svg>
                  </span>
                </button>
              )}
              {/* Betslip Button */}
              {(
                <button
                  type="button"
                  className={`btn btn-sm ${showBetslipPanel ? 'btn-info' : 'btn-outline-info'}`}
                  onClick={() => { const next = !showBetslipPanel; setShowBetslipPanel(next); if (next) setActiveRightPanel('betslip'); setShowMobileTrends(false); }}
                  style={{ fontSize: '0.75rem', padding: '4px 12px', marginLeft: '4px', fontWeight: 'bold' }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '4px', verticalAlign: 'text-bottom' }}>
                    <path d="M4 4h16v4.5c-1.1 0-2 .9-2 2s.9 2 2 2V20H4v-7.5c1.1 0 2-.9 2-2s-.9-2-2-2V4z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  </svg>
                  Betslip{betslipItems.length > 0 ? ` (${betslipItems.length})` : ''}
                </button>
              )}
            </div>
          )}

          {/* Collapse/Expand buttons and View Mode toggle for NFL yard markets on mobile */}
          {isMobile && selectedSport === 'nfl' && ['player_pass_yds', 'player_rush_yds', 'player_rush_attempts', 'player_receptions', 'player_reception_yds', 'player_rush_reception_yds', 'player_field_goals'].includes(masterMarket) && (
            <div className="d-flex align-items-center justify-content-between" style={{ width: '100%', marginBottom: '8px' }}>
              <div className="d-flex align-items-center" style={{ marginRight: '20px', gap: '8px' }}>
              <button
                onClick={handleCollapseAll}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '4px',
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: '18px',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  minWidth: '32px',
                  height: '32px'
                }}
              >
                −
              </button>
              <button
                onClick={handleExpandAll}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '4px',
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: '18px',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  minWidth: '32px',
                  height: '32px'
                }}
              >
                +
              </button>
              </div>
              {/* View Mode Toggle */}
              <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                <div className="btn-group btn-group-sm" role="group">
                  <button
                    type="button"
                    className={`btn ${nflViewMode === 'bestline' ? 'btn-warning' : 'btn-outline-warning'}`}
                    onClick={() => setNflViewMode('bestline')}
                    style={{ fontSize: '0.75rem', padding: '4px 12px' }}
                  >
                    Edge View
                  </button>
                  <button
                    type="button"
                    className={`btn ${nflViewMode === 'ou' ? 'btn-warning' : 'btn-outline-warning'}`}
                    onClick={() => setNflViewMode('ou')}
                    style={{ fontSize: '0.75rem', padding: '4px 12px' }}
                  >
                    O/U
                  </button>
                  <button
                    type="button"
                    className={`btn ${nflViewMode === 'middles' ? 'btn-warning' : 'btn-outline-warning'}`}
                    onClick={() => setNflViewMode('middles')}
                    style={{ fontSize: '0.75rem', padding: '4px 12px' }}
                  >
                    Middles
                  </button>
                </div>
                {/* Neg Corr button (mobile, yard markets row) — icon-only. */}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-warning circled-icon-btn"
                  onClick={() => { setShowNegCorrOverlay(true); setShowLeadersOverlay(false); }}
                  style={{ padding: '2px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                  title="Negative Correlation"
                  aria-label="Negative Correlation"
                >
                  <span className="circled-icon-chip" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '26px', height: '26px', borderRadius: '50%', border: '1.5px solid #ffc107' }}>
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <line x1="4" y1="2.5" x2="4" y2="13.5" />
                      <polyline points="1.5 5 4 2.5 6.5 5" />
                      <line x1="12" y1="2.5" x2="12" y2="13.5" />
                      <polyline points="9.5 11 12 13.5 14.5 11" />
                    </svg>
                  </span>
                </button>
                {/* NFL Leaders Button (mobile, yard markets row) — icon-only. */}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-warning circled-icon-btn"
                  onClick={() => { setShowLeadersOverlay(true); setShowNegCorrOverlay(false); }}
                  style={{ padding: '2px', marginLeft: '4px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                  title="Leaders"
                  aria-label="Leaders"
                >
                  <span className="circled-icon-chip" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '26px', height: '26px', borderRadius: '50%', border: '1.5px solid #ffc107' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M7 4h10v5a5 5 0 0 1-10 0V4Z" />
                      <path d="M7 6H4.5A1.5 1.5 0 0 0 3 7.5 3.5 3.5 0 0 0 6.5 11H7M17 6h2.5A1.5 1.5 0 0 1 21 7.5 3.5 3.5 0 0 1 17.5 11H17M12 14v3M8.5 20h7M10 17h4l.5 3h-5l.5-3Z" />
                    </svg>
                  </span>
                </button>
              </div>
            </div>
          )}

          {/* Tier controls for other NFL prop markets on mobile (not yard markets) */}
          {isMobile && selectedSport === 'nfl' && masterMarket && masterMarket !== 'h2h' && masterMarket !== 'totals' && masterMarket !== 'spreads' && !['player_pass_yds', 'player_rush_yds', 'player_rush_attempts', 'player_receptions', 'player_reception_yds', 'player_rush_reception_yds', 'player_field_goals'].includes(masterMarket) && (
            <div className="d-flex align-items-center" style={{ width: '100%', marginBottom: '8px' }}>
              {/* Collapse/Expand buttons - left side */}
              <div className="d-flex align-items-center" style={{ marginRight: '20px', gap: '8px' }}>
                <button
                  onClick={handleCollapseAll}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '4px',
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '18px',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease',
                    minWidth: '32px',
                    height: '32px'
                  }}
                >
                  −
                </button>
                <button
                  onClick={handleExpandAll}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '4px',
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '18px',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease',
                    minWidth: '32px',
                    height: '32px'
                  }}
                >
                  +
                </button>
              </div>

              {/* Center tier controls */}
              <div className="d-flex align-items-center flex-grow-1 justify-content-center" style={{ gap: '12px' }}>
                {/* Master Tier Filter */}
                {getAvailableTiersForMasterMarket().length > 0 && (
                  <Form.Select
                    value={masterTierFilter !== null ? masterTierFilter : '0.5'}
                    onChange={(e) => handleMasterTierFilter(e.target.value)}
                    size="sm"
                    style={{
                      width: 'auto',
                      minWidth: '80px',
                      fontSize: '11px'
                    }}
                  >
                    {getAvailableTiersForMasterMarket().map(tier => (
                      <option key={tier} value={tier}>
                        {tier}
                      </option>
                    ))}
                  </Form.Select>
                )}

                {/* Master Tier Toggle */}
                <div className="form-check form-switch mb-0" style={{
                  paddingLeft: '0',
                  position: 'relative'
                }}>
                  <input
                    className="form-check-input"
                    type="checkbox"
                  role="switch"
                  id="mobile-nfl-tds-tiers-toggle"
                  checked={masterShowTiers}
                  onChange={(e) => handleMasterTierToggle(e.target.checked)}
                  style={{
                    width: '5em',
                    height: '1.5em',
                    minWidth: '5em'
                  }}
                />
                <label
                  className="form-check-label text-white"
                  htmlFor="mobile-nfl-tds-tiers-toggle"
                  style={{
                    position: 'absolute',
                    left: '3em',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '9px',
                    fontWeight: 'bold',
                    opacity: 0.8,
                    pointerEvents: 'none',
                    userSelect: 'none'
                  }}
                >
                  All
                </label>
              </div>
                {/* Neg Corr button (mobile, other-prop markets row) — icon-only. */}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-warning circled-icon-btn"
                  onClick={() => { setShowNegCorrOverlay(true); setShowLeadersOverlay(false); }}
                  style={{ padding: '2px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                  title="Negative Correlation"
                  aria-label="Negative Correlation"
                >
                  <span className="circled-icon-chip" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '26px', height: '26px', borderRadius: '50%', border: '1.5px solid #ffc107' }}>
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <line x1="4" y1="2.5" x2="4" y2="13.5" />
                      <polyline points="1.5 5 4 2.5 6.5 5" />
                      <line x1="12" y1="2.5" x2="12" y2="13.5" />
                      <polyline points="9.5 11 12 13.5 14.5 11" />
                    </svg>
                  </span>
                </button>
                {/* NFL Leaders Button (mobile, other-prop markets row) — icon-only. */}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-warning circled-icon-btn"
                  onClick={() => { setShowLeadersOverlay(true); setShowNegCorrOverlay(false); }}
                  style={{ padding: '2px', marginLeft: '4px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                  title="Leaders"
                  aria-label="Leaders"
                >
                  <span className="circled-icon-chip" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '26px', height: '26px', borderRadius: '50%', border: '1.5px solid #ffc107' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M7 4h10v5a5 5 0 0 1-10 0V4Z" />
                      <path d="M7 6H4.5A1.5 1.5 0 0 0 3 7.5 3.5 3.5 0 0 0 6.5 11H7M17 6h2.5A1.5 1.5 0 0 1 21 7.5 3.5 3.5 0 0 1 17.5 11H17M12 14v3M8.5 20h7M10 17h4l.5 3h-5l.5-3Z" />
                    </svg>
                  </span>
                </button>
              </div>
            </div>
          )}

          {/* Soccer mobile collapse/expand controls */}
          {isMobile && selectedSport === 'soccer' && (
            <div className="d-flex align-items-center" style={{ width: '100%', marginBottom: '8px' }}>
              {/* Collapse/Expand buttons */}
              <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                <button
                  onClick={handleCollapseAll}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '4px',
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '18px',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease',
                    minWidth: '32px',
                    height: '32px'
                  }}
                >
                  −
                </button>
                <button
                  onClick={handleExpandAll}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '4px',
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '18px',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease',
                    minWidth: '32px',
                    height: '32px'
                  }}
                >
                  +
                </button>
                {/* Compact Auto Parlays button on mobile — mirrors the NFL
                    Neg Corr mobile pattern. */}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-warning"
                  onClick={() => setShowAutoParlaysOverlay(true)}
                  style={{ fontSize: '0.75rem', padding: '4px 8px', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                  title="Auto Parlays"
                  aria-label="Auto Parlays"
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="2.5" y="2.5" width="8" height="5" rx="1" />
                    <rect x="5.5" y="5.5" width="8" height="5" rx="1" />
                    <rect x="2.5" y="8.5" width="8" height="5" rx="1" />
                  </svg>
                  Parlays
                </button>
              </div>

              {/* Tier controls for shots markets */}
              {(masterMarket === 'player_shots' || masterMarket === 'player_shots_on_target') && getAvailableTiersForMasterMarket().length > 0 && (
                <div className="d-flex align-items-center flex-grow-1 justify-content-center" style={{ gap: '12px' }}>
                  <Form.Select
                    value={masterTierFilter !== null ? masterTierFilter : '0.5'}
                    onChange={(e) => handleMasterTierFilter(e.target.value)}
                    size="sm"
                    style={{
                      width: 'auto',
                      minWidth: '80px',
                      fontSize: '11px'
                    }}
                  >
                    {getAvailableTiersForMasterMarket().map(tier => (
                      <option key={tier} value={tier}>
                        {tier}
                      </option>
                    ))}
                  </Form.Select>

                  <div className="form-check form-switch mb-0" style={{
                    paddingLeft: '0',
                    position: 'relative'
                  }}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="mobile-soccer-tiers-toggle"
                      checked={masterShowTiers}
                      onChange={(e) => handleMasterTierToggle(e.target.checked)}
                      style={{
                        marginLeft: '8px',
                        cursor: 'pointer'
                      }}
                    />
                    <label
                      htmlFor="mobile-soccer-tiers-toggle"
                      className="text-white"
                      style={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: '9px',
                        fontWeight: 'bold',
                        opacity: 0.8,
                        pointerEvents: 'none',
                        userSelect: 'none'
                      }}
                    >
                      All
                    </label>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Desktop tier controls (MLB only - NFL has inline controls) */}
          {!isMobile && selectedSport === 'mlb' && masterMarket && getAvailableTiersForMasterMarket().length > 0 && !masterMarket.includes('first_basket') && masterMarket !== 'h2h' && (
            <div className="d-flex align-items-center" style={{ gap: '12px' }}>
              <Form.Select
                value={masterTierFilter || ''}
                onChange={(e) => handleMasterTierFilter(e.target.value || null)}
                size="sm"
                style={{ 
                  width: 'auto', 
                  minWidth: '100px',
                  fontSize: '12px'
                }}
              >
                <option value="">All</option>
                {getAvailableTiersForMasterMarket().map(tier => (
                  <option key={tier} value={tier}>
                    {tier}
                  </option>
                ))}
              </Form.Select>
              
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="master-tier-toggle"
                  checked={masterMarket === 'pitcher_outs' ? true : masterShowTiers}
                  onChange={(e) => handleMasterTierToggle(e.target.checked)}
                  disabled={selectedSport === 'wnba' || masterMarket === 'pitcher_outs'}
                  style={{ fontSize: '12px' }}
                />
                <label className="form-check-label text-muted" htmlFor="master-tier-toggle" style={{
                  opacity: (selectedSport === 'wnba' || masterMarket === 'pitcher_outs') ? 0.3 : 0.8,
                  fontSize: '10px'
                }}>
                  Alt tiers
                </label>
              </div>
            </div>
          )}
          
        </div>
          )}

      </div>

      <div className="container" style={{ paddingTop: '8rem', marginTop: '3rem', paddingBottom: isMobile ? '80px' : '0' }}>

        {/* Show error message if API failed */}
        {getCurrentGamesMetadata()?.error && (
          <div className="alert alert-warning mb-4" role="alert">
            <h5>⚠️ Unable to load games</h5>
            <p>{getCurrentGamesMetadata().error}</p>
            <button className="btn btn-outline-primary btn-sm" onClick={selectedSport === 'ncaaf' ? refetchNcaafGames : selectedSport === 'nfl' ? refetchNflGames : refetchGames}>
              🔄 Try Again
            </button>
          </div>
        )}

        {/* Show debug info in development */}
        {process.env.NODE_ENV === 'development_' && getCurrentGamesMetadata() && (
          <div className="alert alert-info mb-4" role="alert">
            <small>
              Debug: {getCurrentGamesMetadata().format} format •
              {getCurrentGamesMetadata().totalGames || getCurrentGamesMetadata().total} games •
              Sport: {getCurrentGamesMetadata().sport || selectedSport}
              {getCurrentGamesMetadata().timestamp && (
                <> • Updated: {new Date(getCurrentGamesMetadata().timestamp).toLocaleTimeString()}</>
              )}
            </small>
          </div>
        )}

        {/* Loading message while auth/redirect resolves. Gift users have
            access without a subscription — don't show the "Loading..." here
            for them since they'd otherwise see it every page load. */}
        {((!hasActiveSubscription && !gift) || checkoutStatus === 'processing') && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
            padding: '40px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: '48px',
                height: '48px',
                border: '4px solid rgba(59, 130, 246, 0.3)',
                borderTopColor: '#3b82f6',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 20px'
              }}></div>
              <div style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#fff',
                marginBottom: '8px'
              }}>
                Loading...
              </div>
            </div>
          </div>
        )}

        {/* Subscription Required Banner (hidden - kept for reference) */}
        {false && (!hasActiveSubscription || checkoutStatus === 'processing') && (
          <div id="subscription-banner" style={{
            background: 'linear-gradient(135deg, rgba(255, 98, 67, 0.05) 0%, rgba(59, 130, 246, 0.05) 100%)',
            border: '1px solid #495057',
            borderRadius: '8px',
            padding: isMobile ? '16px' : '20px',
            marginBottom: '20px',
            display: isMobile ? 'flex' : 'grid',
            flexDirection: isMobile ? 'column' : undefined,
            gridTemplateColumns: isMobile ? undefined : '1fr 1fr',
            gap: isMobile ? '0' : '32px'
          }}>
            {/* Left Column - Login/Signup */}
            <div
              ref={(el) => {
                if (el && isMobile && !isAuthenticated) {
                  el.id = 'login-section';
                }
              }}
              style={{
                position: 'relative',
                order: isMobile && !isAuthenticated ? 2 : undefined
              }}
            >
              {/* Heading with EdgeBlocks logo */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                marginBottom: '16px'
              }}>
                <h2 style={{
                  fontSize: '32px',
                  fontWeight: '700',
                  color: 'white',
                  letterSpacing: '-0.02em',
                  margin: 0
                }}>
                  simpleOdds
                </h2>
                <EdgeBlocks size={32} />
              </div>
              <p style={{
                fontSize: '18px',
                fontWeight: '600',
                color: 'rgba(255, 255, 255, 0.9)',
                marginBottom: '24px',
                marginTop: '8px',
                textAlign: 'center'
              }}>
                A simple tool designed to easily compare odds across major sportsbooks and uncover edges
              </p>

              {/* Step 1 - Authentication */}
              {!isAuthenticated ? (
                /* Not authenticated - Show login */
                <div style={{
                  marginBottom: '20px',
                  padding: '16px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  position: 'relative'
                }}>
                  {/* Step 1 Header with Circle Badge */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '16px'
                  }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      border: '2px solid #f59e0b',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                      flexShrink: 0
                    }}>
                      1
                    </div>
                    <div style={{
                      fontSize: '16px',
                      fontWeight: '700',
                      color: '#fff',
                      textAlign: 'left'
                    }}>
                      Login to get started
                    </div>
                  </div>

                  {/* Message */}
                  <p style={{
                    fontSize: '14px',
                    color: '#9ca3af',
                    marginBottom: '16px',
                    lineHeight: '1.6',
                    textAlign: 'left'
                  }}>
                    An account is required to access real-time odds.<br />We partner with Whop.com to provide authentication and checkout. <span style={{ fontWeight: '600', color: '#2ecc71' }}>Free tier available!</span>
                  </p>

                  {/* Login Button - Whop styled */}
                  <button
                    onClick={() => {
                      if (isMobile) {
                        setAboutSectionExpanded(false);
                      }
                      login();
                    }}
                    style={{
                      width: '100%',
                      padding: '16px 24px',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: 'white',
                      background: '#FF6243',
                      border: 'none',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '12px',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 4px 12px rgba(255, 98, 67, 0.3)',
                      marginBottom: '16px'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#ff7456';
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.boxShadow = '0 6px 16px rgba(255, 98, 67, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = '#FF6243';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 12px rgba(255, 98, 67, 0.3)';
                    }}
                  >
                    <svg
                      width="48"
                      height="24"
                      viewBox="0 0 36 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ display: 'inline-block' }}
                    >
                      <path
                        clipRule="evenodd"
                        d="M5.73835 0.0180664C3.37553 0.0180664 1.75344 1.02402 0.515773 2.16716C0.515773 2.16716 0.0188309 2.62441 0.0282072 2.6427L5.20391 7.69078L10.3796 2.6427C9.3951 1.32581 7.54797 0.0180664 5.73835 0.0180664ZM18.5182 0.0180664C16.1554 0.0180664 14.5333 1.03317 13.2956 2.16716C13.2956 2.16716 12.8455 2.61527 12.8174 2.6427L6.42279 8.87964L11.5891 13.9186L23.15 2.6427C22.1655 1.32581 20.3184 0.0180664 18.5182 0.0180664ZM26.1036 2.16716C27.3413 1.03317 28.9634 0.0180664 31.3262 0.0180664C33.1358 0.0180664 34.9829 1.32581 35.9581 2.6427H35.9393L21.7905 16.4335C19.6902 18.4728 16.2585 18.4728 14.1676 16.4335L12.8174 15.1166L25.616 2.6427C25.6348 2.61527 26.1036 2.16716 26.1036 2.16716Z"
                        fill="white"
                        fillRule="evenodd"
                      />
                    </svg>
                    Continue with Whop
                  </button>

                  <p style={{
                    fontSize: '13px',
                    color: 'rgba(255, 255, 255, 0.7)',
                    marginBottom: '12px',
                    textAlign: 'center'
                  }}>
                    Includes support for
                  </p>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '16px',
                    flexWrap: 'wrap'
                  }}>
                    {/* Google */}
                    <div style={{
                      width: '40px',
                      height: '40px',
                      background: 'white',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '8px'
                    }}>
                      <svg viewBox="0 0 24 24" width="24" height="24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                    </div>

                    {/* Discord */}
                    <div style={{
                      width: '40px',
                      height: '40px',
                      background: '#5865F2',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '8px'
                    }}>
                      <svg viewBox="0 0 24 24" width="24" height="24" fill="white">
                        <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                      </svg>
                    </div>

                    {/* Email/Password */}
                    <div style={{
                      width: '40px',
                      height: '40px',
                      background: 'white',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '8px'
                    }}>
                      <svg viewBox="0 0 24 24" width="24" height="24" fill="#6b7280">
                        <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                      </svg>
                    </div>
                  </div>
                </div>
              ) : (
                /* Authenticated - Show completed step 1 */
                <div style={{
                  marginBottom: '20px',
                  padding: '16px',
                  background: 'rgba(46, 204, 113, 0.1)',
                  borderRadius: '12px',
                  border: '2px solid #2ecc71'
                }}>
                  {/* Step 1 Header with Checkmark Badge */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      border: '2px solid #f59e0b',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                      flexShrink: 0
                    }}>
                      ✓
                    </div>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#cbd5e1'
                    }}>
                      Logged in as {user?.email || user?.name || user?.username || 'User'}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2 - Plan Selection */}
              {!isAuthenticated ? (
                /* Not authenticated - Show greyed out step 2 */
                <div style={{
                  padding: '16px',
                  background: '#0f172a',
                  border: '2px solid #334155',
                  borderRadius: '12px',
                  opacity: 0.5,
                  pointerEvents: 'none'
                }}>
                {/* Step 2 Header with Circle Badge */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: '#475569',
                    color: '#94a3b8',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    border: '2px solid #64748b',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                    flexShrink: 0
                  }}>
                    2
                  </div>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '700',
                    color: '#cbd5e1',
                    textAlign: 'left'
                  }}>
                    Choose a Plan
                  </div>
                </div>

                {/* Plan Preview - Show options */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '8px',
                  marginBottom: '12px'
                }}>
                  {/* Preview Access Badge */}
                  <div style={{
                    padding: '12px',
                    background: '#1e293b',
                    border: '1px solid #34D399',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#cbd5e1',
                      marginBottom: '4px'
                    }}>
                      Preview
                    </div>
                    <div style={{
                      fontSize: '20px',
                      fontWeight: '700',
                      color: '#34D399'
                    }}>
                      FREE
                    </div>
                  </div>

                  {/* Full Access Badge */}
                  <div style={{
                    padding: '12px',
                    background: '#1e293b',
                    border: '1px solid #FF6243',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#cbd5e1',
                      marginBottom: '4px'
                    }}>
                      Full Access
                    </div>
                    <div style={{
                      fontSize: '20px',
                      fontWeight: '700',
                      color: '#FF6243'
                    }}>
                      ${availablePlans?.paid?.cost || 15}/mo
                    </div>
                  </div>
                </div>

                <div style={{
                  fontSize: '13px',
                  color: '#64748b',
                  textAlign: 'center',
                  padding: '12px',
                  background: 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '8px'
                }}>
                  Complete Step 1 to continue
                </div>
              </div>
              ) : (
                /* Authenticated - Show active plan selection */
                <div style={{
                  marginBottom: '20px',
                  padding: '16px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  {/* Step 2 Header with Circle Badge */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '16px'
                  }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #ff9f43 0%, #ff8c00 100%)',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      border: '2px solid #ffd700',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                      flexShrink: 0
                    }}>
                      2
                    </div>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#2ecc71',
                        marginBottom: '4px'
                      }}>
                        You're almost there!
                      </div>
                      <div style={{
                        fontSize: '16px',
                        fontWeight: '700',
                        color: '#fff'
                      }}>
                        Choose a Plan
                      </div>
                    </div>
                  </div>

                  {/* Checkout Status Banner */}
                  {checkoutStatus && (
                    <div style={{
                      padding: '16px',
                      borderRadius: '8px',
                      marginBottom: '16px',
                      background: checkoutStatus === 'success' ? '#10b981' :
                                  checkoutStatus === 'timeout' ? '#f59e0b' : '#3b82f6',
                      color: 'white',
                      textAlign: 'center',
                      fontSize: '14px',
                      fontWeight: '600',
                      border: checkoutStatus === 'success' ? '2px solid #059669' :
                              checkoutStatus === 'timeout' ? '2px solid #d97706' : '2px solid #2563eb'
                    }}>
                      {checkoutStatus === 'processing' && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                          <span>🎉 Checkout complete! Activating your subscription...</span>
                          <span style={{
                            display: 'inline-block',
                            width: '16px',
                            height: '16px',
                            border: '2px solid rgba(255,255,255,0.3)',
                            borderTopColor: 'white',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }}></span>
                        </div>
                      )}
                      {checkoutStatus === 'success' && (
                        <div>✓ Success! Your Preview Access is now active.</div>
                      )}
                      {checkoutStatus === 'timeout' && (
                        <div>
                          <div style={{ marginBottom: '8px' }}>
                            ⚠️ Your payment was successful! Your subscription is being processed and will activate shortly.
                          </div>
                          <button
                            onClick={() => window.location.reload()}
                            style={{
                              background: 'white',
                              color: '#f59e0b',
                              border: 'none',
                              padding: '8px 16px',
                              borderRadius: '6px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              fontSize: '13px'
                            }}
                          >
                            Refresh Page
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Plan Selection or Checkout */}
                  {!showCheckout && !checkoutStatus ? (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr',
                      gap: '12px'
                    }}>
                      {/* Preview Access - Free */}
                      <div style={{
                        padding: '16px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '12px',
                        border: '2px solid rgba(255, 255, 255, 0.1)',
                        textAlign: 'center',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#34D399';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 16px rgba(52, 211, 153, 0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      onClick={() => {
                        setSelectedPlan('free');
                        setShowCheckout(true);
                      }}
                      >
                      <h3 style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: 'white',
                        marginBottom: '6px'
                      }}>
                        Preview Access
                      </h3>
                      <div style={{
                        fontSize: '32px',
                        fontWeight: '700',
                        color: '#34D399',
                        marginBottom: '10px',
                        lineHeight: '1.1'
                      }}>
                        FREE
                        <div style={{
                          fontSize: '16px',
                          fontWeight: '500',
                          marginTop: '4px'
                        }}>
                          for 30 days, no payment info required
                        </div>
                      </div>
                      <ul style={{
                        fontSize: '12px',
                        color: 'rgba(255, 255, 255, 0.7)',
                        lineHeight: '1.6',
                        margin: '0 0 12px 0',
                        padding: '0',
                        textAlign: 'left',
                        listStyle: 'none'
                      }}>
                        <li style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          marginBottom: '4px'
                        }}>
                          <span style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            background: '#34D399',
                            color: 'white',
                            fontSize: '10px',
                            fontWeight: '700',
                            marginRight: '6px',
                            flexShrink: 0,
                            boxShadow: '0 2px 4px rgba(52, 211, 153, 0.4)'
                          }}>✓</span>
                          <span>One game/day each for MLB, NBA, NHL</span>
                        </li>
                        <li style={{
                          display: 'flex',
                          alignItems: 'flex-start'
                        }}>
                          <span style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            background: '#34D399',
                            color: 'white',
                            fontSize: '10px',
                            fontWeight: '700',
                            marginRight: '6px',
                            flexShrink: 0,
                            boxShadow: '0 2px 4px rgba(52, 211, 153, 0.4)'
                          }}>✓</span>
                          <span>One game/week: NFL</span>
                        </li>
                      </ul>

                      {/* Terms Acceptance Checkbox */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '12px',
                          padding: '0 4px'
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          id="terms-checkbox"
                          checked={termsAccepted}
                          onChange={(e) => {
                            e.stopPropagation();
                            setTermsAccepted(e.target.checked);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            cursor: 'pointer',
                            width: '16px',
                            height: '16px',
                            minWidth: '16px',
                            flexShrink: 0,
                            margin: 0
                          }}
                        />
                        <label
                          htmlFor="terms-checkbox"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            fontSize: '11px',
                            color: 'rgba(255, 255, 255, 0.8)',
                            lineHeight: '1.5',
                            cursor: 'pointer',
                            userSelect: 'none',
                            margin: 0
                          }}
                        >
                          I confirm I am 18+ and agree to the{' '}
                          <a
                            href="#"
                            style={{
                              color: '#34D399',
                              textDecoration: 'underline',
                              cursor: 'pointer'
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setLegalDocument('terms');
                            }}
                          >
                            Terms of Service
                          </a>
                          {' '}and{' '}
                          <a
                            href="#"
                            style={{
                              color: '#34D399',
                              textDecoration: 'underline',
                              cursor: 'pointer'
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setLegalDocument('privacy');
                            }}
                          >
                            Privacy Policy
                          </a>
                        </label>
                      </div>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!termsAccepted) return;
                          const planId = availablePlans?.free?.plan;
                          if (planId) {
                            window.location.assign(`https://whop.com/checkout/${planId}/`);
                          }
                        }}
                        disabled={!termsAccepted}
                        style={{
                          width: '100%',
                          padding: '10px 16px',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: 'white',
                          background: termsAccepted ? '#34D399' : '#6c757d',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: termsAccepted ? 'pointer' : 'not-allowed',
                          transition: 'all 0.2s ease',
                          opacity: termsAccepted ? 1 : 0.6
                        }}
                        onMouseEnter={(e) => {
                          if (termsAccepted) e.target.style.background = '#2aa87d';
                        }}
                        onMouseLeave={(e) => {
                          if (termsAccepted) e.target.style.background = '#34D399';
                        }}
                      >
                        Start with Free
                      </button>
                    </div>

                    {/* Full Access - $15/mo */}
                    <div style={{
                      padding: '16px',
                      background: 'linear-gradient(135deg, rgba(255, 98, 67, 0.15) 0%, rgba(59, 130, 246, 0.15) 100%)',
                      borderRadius: '12px',
                      border: '2px solid #FF6243',
                      textAlign: 'center',
                      position: 'relative',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(255, 98, 67, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}>
                      <h3 style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: 'white',
                        marginBottom: '6px'
                      }}>
                        Full Access
                      </h3>
                      <div style={{
                        fontSize: '32px',
                        fontWeight: '700',
                        color: '#FF6243',
                        marginBottom: '10px'
                      }}>
                        ${availablePlans?.paid?.cost || 15}/mo
                      </div>
                      <p style={{
                        fontSize: '12px',
                        color: 'rgba(255, 255, 255, 0.7)',
                        lineHeight: '1.4',
                        marginBottom: '12px'
                      }}>
                        Unlimited access to all games and all site features
                      </p>
                      <button
                        onClick={() => {
                          const planId = availablePlans?.paid?.plan;
                          if (planId) {
                            window.location.assign(`https://whop.com/checkout/${planId}/`);
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 16px',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: 'white',
                          background: 'linear-gradient(135deg, #FF6243 0%, #ff7456 100%)',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'scale(1.02)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'scale(1)';
                        }}
                      >
                        Get Started
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Show Checkout Embed */
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '12px',
                    padding: '16px',
                    border: '1px solid #495057'
                  }}>
                    {/* Header */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '16px',
                      paddingBottom: '16px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <h3 style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: 'white',
                        margin: 0
                      }}>
                        {selectedPlan === 'free' ? 'Confirm Free Plan' : 'Complete Your Purchase'}
                      </h3>
                      <button
                        onClick={() => {
                          setShowCheckout(false);
                          setSelectedPlan(null);
                          setTermsAccepted(false);
                        }}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#cbd5e1',
                          fontSize: '24px',
                          cursor: 'pointer',
                          padding: '0 4px'
                        }}
                      >
                        ×
                      </button>
                    </div>

                    {/* Checkout Embed */}
                    {!availablePlans ? (
                      <div style={{ color: '#cbd5e1', padding: '40px 20px', textAlign: 'center' }}>
                        Loading checkout...
                      </div>
                    ) : (
                      <WhopCheckoutEmbed
                        planId={selectedPlan === 'free' ? availablePlans?.free?.plan : availablePlans?.paid?.plan}
                        theme="dark"
                        prefill={{
                          email: user?.email || ''
                        }}
                      onComplete={async (planId, receiptId) => {
                        console.log('Checkout completed:', planId, receiptId);

                        // Send checkout completion to backend and wait for it to complete
                        try {
                          const response = await fetch('/api/auth/checkout', {
                            method: 'POST',
                            credentials: 'include',
                            headers: {
                              'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({ planId, receiptId })
                          });

                          if (!response.ok) {
                            console.error('Checkout completion API failed:', await response.text());
                          } else {
                            // Wait for the response to complete
                            await response.json();
                          }
                        } catch (error) {
                          console.error('Error sending checkout completion:', error);
                        }

                        // Set flag for post-refresh loading screen
                        localStorage.setItem('checkout_in_progress', 'true');

                        // Show processing status
                        setCheckoutStatus('processing');
                        setShowCheckout(false);

                        // Poll for updated subscription status
                        let attempts = 0;
                        const maxAttempts = 10;
                        const pollInterval = 2000; // 2 seconds

                        const pollForSubscription = async () => {
                          attempts++;
                          console.log(`Polling for subscription (attempt ${attempts}/${maxAttempts})`);

                          await checkAuth();

                          // Check if user now has active subscription
                          // Note: checkAuth updates the activeSub object in context
                          // We need to check it after a brief delay
                          setTimeout(() => {
                            if (activeSub?.valid && (activeSub?.status === 'active' || activeSub?.status === 'trialing' || activeSub?.status === 'completed' || activeSub?.status === 'drafted')) {
                              console.log('Subscription activated!');

                              // Clear checkout status immediately for smooth transition to games
                              setCheckoutStatus(null);
                              setSelectedPlan(null);
                              return;
                            }

                            if (attempts < maxAttempts) {
                              setTimeout(pollForSubscription, pollInterval);
                            } else {
                              console.warn('Subscription not activated after polling');
                              setCheckoutStatus('timeout');
                            }
                          }, 500);
                        };

                        // Start polling after a brief delay (give webhook time to process)
                        setTimeout(pollForSubscription, 2000);
                      }}
                      fallback={<div style={{ color: '#cbd5e1', padding: '20px', textAlign: 'center' }}>Loading checkout...</div>}
                    />
                    )}
                  </div>
                )}
                </div>
              )}
            </div>

            {/* Right Column - Features - Collapsible on mobile */}
            <div
              style={{
                marginBottom: isMobile ? '20px' : '0',
                order: isMobile && !isAuthenticated ? 1 : undefined,
                cursor: isMobile && !isAuthenticated ? 'pointer' : 'default'
              }}
              onClick={() => {
                if (isMobile && !isAuthenticated) {
                  setAboutSectionExpanded(!aboutSectionExpanded);
                }
              }}
            >
              {/* Collapsed state - show "New here? Read this first" */}
              {isMobile && !isAuthenticated && !aboutSectionExpanded && (
                <h4 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#fff',
                  marginBottom: '0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '12px',
                  background: 'rgba(59, 130, 246, 0.1)',
                  borderRadius: '8px',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  position: 'relative'
                }}>
                  📰 New here? Read this first
                  <span style={{
                    fontSize: '14px',
                    transition: 'transform 0.3s ease',
                    position: 'absolute',
                    right: '12px'
                  }}>
                    ▼
                  </span>
                </h4>
              )}

              {/* Expanded state - show full content */}
              {(aboutSectionExpanded || !isMobile || isAuthenticated) && (
                <div style={{
                  maxHeight: aboutSectionExpanded || !isMobile || isAuthenticated ? '3000px' : '0',
                  overflow: 'hidden',
                  transition: 'max-height 0.5s ease-out, opacity 0.3s ease-out',
                  opacity: aboutSectionExpanded || !isMobile || isAuthenticated ? 1 : 0
                }}>
                  {/* About section */}
                  <div style={{ marginBottom: '16px' }}>
                    <h4 style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#fff',
                      marginBottom: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative'
                    }}>
                      Hi There!
                      {isMobile && !isAuthenticated && (
                        <span style={{
                          fontSize: '14px',
                          transition: 'transform 0.3s ease',
                          transform: 'rotate(180deg)',
                          position: 'absolute',
                          right: '0'
                        }}>
                          ▼
                        </span>
                      )}
                    </h4>
                    <p style={{
                      fontSize: '14px',
                      color: '#cbd5e1',
                      lineHeight: '1.6',
                      marginBottom: '16px'
                    }}>
                      I'm Mike, a software engineer. I started building this app because I wanted a simpler, faster way to view odds the way I like to see them. What began as a personal MLB doubles and homers screener has since grown to include more props and all major sports. Maybe it'll work for you too.
                    </p>
                  </div>

            {/* Features Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr',
              gap: '8px',
              marginBottom: '12px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px',
                background: '#0f172a',
                border: '1px solid #334155',
                borderRadius: '8px',
                fontSize: '13px',
                color: '#cbd5e1'
              }}>
                <span style={{ fontSize: '1.2rem' }}>⚡</span>
                <span>High-frequency updates</span>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px',
                background: '#0f172a',
                border: '1px solid #334155',
                borderRadius: '8px',
                fontSize: '13px',
                color: '#cbd5e1'
              }}>
                <span style={{ fontSize: '1.2rem' }}>💻📱</span>
                <span>Desktop & Mobile</span>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px',
                background: '#0f172a',
                border: '1px solid #334155',
                borderRadius: '8px',
                fontSize: '13px',
                color: '#cbd5e1'
              }}>
                <span style={{ fontSize: '1.2rem' }}>🎯</span>
                <span>Easily find outliers</span>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px',
                background: '#0f172a',
                border: '1px solid #334155',
                borderRadius: '8px',
                fontSize: '13px',
                color: '#cbd5e1'
              }}>
                <span style={{ fontSize: '1.2rem' }}>🏆</span>
                <span>MLB, NFL, NBA, NHL & more</span>
              </div>
            </div>

            {/* View all players feature */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '10px',
              background: '#0f172a',
              border: '1px solid #334155',
              borderRadius: '8px',
              fontSize: '13px',
              color: '#cbd5e1',
              marginBottom: '12px'
            }}>
              <span style={{ fontSize: '1.2rem' }}>👀</span>
              <span>View odds for a prop for all players across all games.</span>
            </div>

            {/* Color coding info with blocks */}
            <div style={{
              padding: '12px',
              background: '#0f172a',
              borderRadius: '8px',
              border: '1px solid #334155',
              marginBottom: '8px'
            }}>
              <p style={{
                fontSize: '13px',
                color: '#cbd5e1',
                lineHeight: '1.5',
                marginBottom: '8px'
              }}>
                <span style={{ fontSize: '1rem', marginRight: '6px' }}>🎨</span>
                <span style={{ fontWeight: '600', color: '#fff' }}>Color-coded odds:</span> We use color to help you quickly identify betting value across different sportsbooks.
              </p>
              <div style={{
                display: 'flex',
                gap: '6px',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <div style={{
                  minWidth: '50px',
                  height: '24px',
                  borderRadius: '4px',
                  background: 'green',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '11px',
                  fontWeight: '700',
                  color: 'yellow'
                }}>Best</div>
                <div style={{
                  minWidth: '50px',
                  height: '24px',
                  borderRadius: '4px',
                  background: '#F59E0B',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '11px',
                  fontWeight: '700',
                  color: '#1E40AF'
                }}>Better</div>
                <div style={{
                  minWidth: '50px',
                  height: '24px',
                  borderRadius: '4px',
                  background: '#3B82F6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '11px',
                  fontWeight: '700',
                  color: 'white'
                }}>Good</div>
              </div>
            </div>

            {/* Bottom 2 features in quadrants */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: '8px',
              marginBottom: '16px'
            }}>

              {/* Best Bets */}
              <div style={{
                padding: '12px',
                background: '#0f172a',
                borderRadius: '8px',
                border: '1px solid #334155'
              }}>
                <p style={{
                  fontSize: '13px',
                  color: '#cbd5e1',
                  lineHeight: '1.5',
                  margin: 0
                }}>
                  <span style={{ fontSize: '1rem', marginRight: '6px' }}>⭐</span>
                  <span style={{ fontWeight: '600', color: '#fff' }}>Best Bets:</span> Click the Best Bets button to see props with the strongest real-time edges.
                </p>
              </div>

              {/* Player details */}
              <div style={{
                padding: '12px',
                background: '#0f172a',
                borderRadius: '8px',
                border: '1px solid #334155'
              }}>
                <p style={{
                  fontSize: '13px',
                  color: '#cbd5e1',
                  lineHeight: '1.5',
                  margin: 0
                }}>
                  <span style={{ fontSize: '1rem', marginRight: '6px' }}>👤</span>
                  <span style={{ fontWeight: '600', color: '#fff' }}>Player Details:</span> Click player names to see all odds/props for that player.
                </p>
              </div>
            </div>

            {/* Simple and Affordable Section */}
            <div style={{
              padding: '12px',
              background: '#0f172a',
              borderRadius: '8px',
              border: '1px solid #334155'
            }}>
              <h4 style={{
                fontSize: '16px',
                fontWeight: '700',
                color: '#fff',
                marginBottom: '10px'
              }}>
                Simple and Affordable
              </h4>
              <p style={{
                fontSize: '13px',
                color: '#cbd5e1',
                lineHeight: '1.6',
                marginBottom: '12px'
              }}>
                It's definitely still evolving — there will be some bumps along the way — but that's part of the process. I want to keep it simple, affordable, and accessible for everyone.
              </p>

              {/* Screenshot thumbnails */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '6px',
                marginTop: '12px'
              }}>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num, index) => (
                  <img
                    key={num}
                    src={`/images/screenshot${num}.jpg`}
                    alt={`Screenshot ${num}`}
                    style={{
                      width: '100%',
                      height: '80px',
                      objectFit: 'cover',
                      borderRadius: '4px',
                      border: '1px solid #334155',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                    onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                    onClick={() => {
                      setLightboxImageIndex(index);
                      setLightboxOpen(true);
                    }}
                  />
                ))}
              </div>
            </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Loading state when the user has access but games haven't loaded yet */}
        {hasAccess && getCurrentGamesData().length === 0 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
            padding: '40px'
          }}>
            <div style={{
              textAlign: 'center'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                border: '4px solid rgba(59, 130, 246, 0.3)',
                borderTopColor: '#3b82f6',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 20px'
              }}></div>
              <div style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#fff',
                marginBottom: '8px'
              }}>
                Loading games...
              </div>
              <div style={{
                fontSize: '14px',
                color: '#cbd5e1'
              }}>
                Please wait while we load the latest odds
              </div>
            </div>
          </div>
        )}

        {/* Games container with mobile top padding */}
        <div style={{ marginTop: isMobile ? '50px' : '0' }}>
        {(() => {
          // =============================================================================
          // PERFORMANCE OPTIMIZATION: Use pre-filtered games from useMemo
          // Previously filtering happened here on every render
          // To revert: change to getCurrentGamesData() and restore inline filtering
          // =============================================================================
          const currentGames = filteredGamesForRender;

          return currentGames.map((game, index) => {
            // Use gameIDmlb as key (locked games will have unique IDs like "locked_xyz")
            const uniqueKey = game.gameIDmlb;

            return (
              <React.Fragment key={uniqueKey}>
                {index === 0 && (() => {
                  const promoBySport = {
                    mlb: 'DINGERS',
                    nfl: 'TUDDY',
                    nba: 'BUCKETS',
                    nhl: 'SLAPSHOT',
                  };
                  const code = promoBySport[selectedSport];
                  if (!code) return null;
                  const gatePass = !gift
                    && activeSub?.tier === 'free'
                    && parseInt(localStorage.getItem(`${selectedSport}_visit_days`) || '0') >= 2;
                  if (!gatePass) return null;
                  const planId = availablePlans?.paid?.plan;
                  const href = planId
                    ? `https://whop.com/checkout/${planId}/?promoCode=${code}`
                    : `https://whop.com/hub/simpleodds/?promoCode=${code}`;
                  return (
                    <a
                      href={href}
                      style={{ display: 'block', background: 'linear-gradient(135deg, #1a1d21 0%, #2a2d31 100%)', border: '1px solid #ffc107', borderRadius: '8px', padding: '12px 16px', marginBottom: isMobile ? '16px' : '12px', textAlign: 'center', textDecoration: 'none', cursor: 'pointer' }}
                    >
                      <span style={{ color: '#ffc107', fontWeight: 700, fontSize: isMobile ? '0.9rem' : '1rem' }}>Get 50% off your first month with promo code: </span>
                      <span style={{ color: '#000', fontWeight: 700, fontSize: isMobile ? '1rem' : '1.1rem', backgroundColor: '#ffc107', padding: '2px 8px', borderRadius: '4px', marginLeft: '4px' }}>{code}</span>
                    </a>
                  );
                })()}
                {/* Game card */}
                <div
                  key={uniqueKey}
                  id={`game-${uniqueKey}`}
                  data-game-id={uniqueKey}
                  ref={(el) => registerGameElement(uniqueKey, el)}
                  className={`${analyticsMode && analyticsType === 'matchups' && isMobile ? 'mb-4' : isMobile ? 'mb-4' : 'mb-3'}`}
                >
                {analyticsMode ? (
                  <AnalyticsDataTable 
                    analyticsByGameId={analyticsByGameId}
                    analyticsLoadingGameIds={analyticsLoadingGameIds}
                    gameTime={game.gameTime} 
                    gameStatus={game.status}
                    gameDate={getCurrentGamesMetadata()?.date || selectedDate}
                    game={game}
                    formatPlayerName={formatPlayerNameWrapper}
                    getTeamLogoUrl={createTeamLogoGetter(game)}
                    isMobile={isMobile}
                    isCollapsed={gameCollapseStates[game.gameIDmlb] || false}
                    onCollapseToggle={(isCollapsed) => handleGameCollapseToggle(game.gameIDmlb, isCollapsed)}
                  />
                ) : (selectedSport === 'ncaaf' && masterMarket === 'h2h') ? (
                    // NCAAF H2H view - use H2HDataTable for mainline markets
                    <H2HDataTable
                      data={playersByGameIdFiltered[game.gameIDmlb]?.players}
                      gameTime={game.gameTime}
                      gameStatus={game.status}
                      gameDate={getCurrentGamesMetadata()?.date || selectedDate}
                      lastUpdate={playersByGameId[game.gameIDmlb]?.last_update}
                      game={{...game, sport: 'ncaaf'}}
                      formatPlayerName={formatPlayerNameWrapper}
                      getTeamLogoUrl={createTeamLogoGetter(game)}
                      getBookLogoUrl={getBookLogoUrl}
                      isMobile={isMobile}
                      bookmakers={getEnabledBooks()}
                      isLoading={loadingGameIds.has(game.gameIDmlb)}
                      isCollapsed={gameCollapseStates[game.gameIDmlb] || false}
                      onCollapseToggle={(isCollapsed) => handleGameCollapseToggle(game.gameIDmlb, isCollapsed)}
                      onOddsClick={handleOddsClick}
                      onPlayerContextMenu={handlePlayerContextMenu}
                      onGameClick={!isMobile ? (gameId) => { setGamePanelGameId(gameId); setShowGamePanel(true); setShowPlayerPanel(false); } : undefined}
                    />
                ) : (selectedSport === 'nfl' && masterMarket === 'h2h') ? (
                    // NFL H2H view - use H2HDataTable for mainline markets
                    <H2HDataTable
                      data={playersByGameIdFiltered[game.gameIDmlb]?.players}
                      gameTime={game.gameTime}
                      gameStatus={game.status}
                      gameDate={getCurrentGamesMetadata()?.date || selectedDate}
                      lastUpdate={playersByGameId[game.gameIDmlb]?.last_update}
                      game={{...game, sport: 'nfl'}}
                      formatPlayerName={formatPlayerNameWrapper}
                      getTeamLogoUrl={createTeamLogoGetter(game)}
                      getBookLogoUrl={getBookLogoUrl}
                      isMobile={isMobile}
                      bookmakers={getEnabledBooks()}
                      isLoading={loadingGameIds.has(game.gameIDmlb)}
                      isCollapsed={gameCollapseStates[game.gameIDmlb] || false}
                      onCollapseToggle={(isCollapsed) => handleGameCollapseToggle(game.gameIDmlb, isCollapsed)}
                      onOddsClick={handleOddsClick}
                      onPlayerContextMenu={handlePlayerContextMenu}
                      onGameClick={!isMobile ? (gameId) => { setGamePanelGameId(gameId); setShowGamePanel(true); setShowPlayerPanel(false); } : undefined}
                    />
                ) : selectedSport === 'nfl' ? (
                    isMobile ? (
                      <MobileNFLDataTable
                        data={playersByGameIdFiltered[game.gameIDmlb]?.players}
                        gameTime={game.gameTime}
                        gameStatus={game.status}
                        gameDate={getCurrentGamesMetadata()?.date || selectedDate}
                        lastUpdate={playersByGameId[game.gameIDmlb]?.last_update}
                        game={game}
                        formatPlayerName={formatPlayerNameWrapper}
                        getTeamLogoUrl={createTeamLogoGetter(game)}
                        getBookLogoUrl={getBookLogoUrl}
                        selectedMarket={getSelectedMarketForGame(game.gameIDmlb)}
                        selectedMarketName={availableMarkets.find(m => m.key === getSelectedMarketForGame(game.gameIDmlb))?.name || 'Touchdowns'}
                        bookmakers={getEnabledBooks()}
                        isLoading={loadingGameIds.has(game.gameIDmlb)}
                        availableMarkets={availableMarkets}
                        nflViewMode={nflViewMode}
                        onMarketSelection={(gameId, marketKey) => {
                          // Track if this is a user-specific selection (different from master)
                          if (marketKey !== masterMarket) {
                            setUserSelectedMarkets(prev => ({ ...prev, [gameId]: true }));
                          } else {
                            // If user selects the same as master, remove the user-specific flag
                            setUserSelectedMarkets(prev => {
                              const newState = { ...prev };
                              delete newState[gameId];
                              return newState;
                            });
                          }

                          handleMarketSelection(gameId, marketKey);
                        }}
                        showTiers={getShowTiersForGame(game.gameIDmlb)}
                        onTierToggle={(show) => handleGameTierToggle(game.gameIDmlb, show)}
                        masterTierFilter={masterTierFilter}
                        onPlayerClick={handlePlayerClick}
                        onOddsClick={handleOddsClick}
                        onPlayerContextMenu={handlePlayerContextMenu}
                      onPlayerContextMenu={handlePlayerContextMenu}
                        clickedCellId={clickedCellId}
                        isCollapsed={gameCollapseStates[game.gameIDmlb] || false}
                        onCollapseToggle={(isCollapsed) => handleGameCollapseToggle(game.gameIDmlb, isCollapsed)}
                        onSettingsClick={() => setShowSettings(true)}
                      />
                    ) : (
                      <NFLDataTable
                        data={playersByGameIdFiltered[game.gameIDmlb]?.players}
                        gameTime={game.gameTime}
                        gameStatus={game.status}
                        gameDate={getCurrentGamesMetadata()?.date || selectedDate}
                        lastUpdate={playersByGameId[game.gameIDmlb]?.last_update}
                        game={game}
                        formatPlayerName={formatPlayerNameWrapper}
                        getTeamLogoUrl={createTeamLogoGetter(game)}
                        getBookLogoUrl={getBookLogoUrl}
                        isMobile={isMobile}
                        selectedMarket={getSelectedMarketForGame(game.gameIDmlb)}
                        selectedMarketName={availableMarkets.find(m => m.key === getSelectedMarketForGame(game.gameIDmlb))?.name || 'Touchdowns'}
                        bookmakers={getEnabledBooks()}
                        isLoading={loadingGameIds.has(game.gameIDmlb)}
                        availableMarkets={availableMarkets}
                        nflViewMode={nflViewMode}
                        onMarketSelection={(gameId, marketKey) => {
                          // Track if this is a user-specific selection (different from master)
                          if (marketKey !== masterMarket) {
                            setUserSelectedMarkets(prev => ({ ...prev, [gameId]: true }));
                          } else {
                            // If user selects the same as master, remove the user-specific flag
                            setUserSelectedMarkets(prev => {
                              const newState = { ...prev };
                              delete newState[gameId];
                              return newState;
                            });
                          }

                          handleMarketSelection(gameId, marketKey);
                        }}
                        showTiers={getShowTiersForGame(game.gameIDmlb)}
                        onTierToggle={(show) => handleGameTierToggle(game.gameIDmlb, show)}
                        masterTierFilter={masterTierFilter}
                        onPlayerClick={handlePlayerClick}
                        onOddsClick={handleOddsClick}
                        onPlayerContextMenu={handlePlayerContextMenu}
                      onPlayerContextMenu={handlePlayerContextMenu}
                        clickedCellId={clickedCellId}
                        isCollapsed={gameCollapseStates[game.gameIDmlb] || false}
                        onCollapseToggle={(isCollapsed) => handleGameCollapseToggle(game.gameIDmlb, isCollapsed)}
                        onSettingsClick={() => setShowSettings(true)}
                      />
                    )
                ) : (selectedSport === 'nhl' && masterMarket === 'h2h') ? (
                    // NHL H2H view - use H2HDataTable for mainline markets
                    <H2HDataTable
                      data={playersByGameIdFiltered[game.gameIDmlb]?.players}
                      gameTime={game.gameTime}
                      gameStatus={game.status}
                      gameDate={getCurrentGamesMetadata()?.date || selectedDate}
                      lastUpdate={playersByGameId[game.gameIDmlb]?.last_update}
                      game={{...game, sport: 'nhl'}}
                      formatPlayerName={formatPlayerNameWrapper}
                      getTeamLogoUrl={createTeamLogoGetter(game)}
                      getBookLogoUrl={getBookLogoUrl}
                      isMobile={isMobile}
                      bookmakers={getEnabledBooks()}
                      isLoading={loadingGameIds.has(game.gameIDmlb)}
                      isCollapsed={gameCollapseStates[game.gameIDmlb] || false}
                      onCollapseToggle={(isCollapsed) => handleGameCollapseToggle(game.gameIDmlb, isCollapsed)}
                      onOddsClick={handleOddsClick}
                      onPlayerContextMenu={handlePlayerContextMenu}
                      onGameClick={!isMobile ? (gameId) => { setGamePanelGameId(gameId); setShowGamePanel(true); setShowPlayerPanel(false); } : undefined}
                    />
                ) : selectedSport === 'nhl' ? (
                    <NHLDataTable
                      data={playersByGameIdFiltered[game.gameIDmlb]?.players}
                      gameTime={game.gameTime}
                      gameStatus={game.status}
                      gameDate={getCurrentGamesMetadata()?.date || selectedDate}
                      lastUpdate={playersByGameId[game.gameIDmlb]?.last_update}
                      game={game}
                      formatPlayerName={formatPlayerNameWrapper}
                      getTeamLogoUrl={createTeamLogoGetter(game)}
                      getBookLogoUrl={getBookLogoUrl}
                      isMobile={isMobile}
                      selectedMarket={getSelectedMarketForGame(game.gameIDmlb)}
                      selectedMarketName={availableMarkets.find(m => m.key === getSelectedMarketForGame(game.gameIDmlb))?.name || 'Goals'}
                      bookmakers={getEnabledBooks()}
                      isLoading={loadingGameIds.has(game.gameIDmlb)}
                      isCollapsed={gameCollapseStates[game.gameIDmlb] || false}
                      onCollapseToggle={(isCollapsed) => handleGameCollapseToggle(game.gameIDmlb, isCollapsed)}
                      availableMarkets={availableMarkets}
                      onMarketSelection={(gameId, marketKey) => {
                        // Track if this is a user-specific selection (different from master)
                        if (marketKey !== masterMarket) {
                          setUserSelectedMarkets(prev => ({ ...prev, [gameId]: true }));
                        } else {
                          // If user selects the same as master, remove the user-specific flag
                          setUserSelectedMarkets(prev => {
                            const newState = { ...prev };
                            delete newState[gameId];
                            return newState;
                          });
                        }

                        handleMarketSelection(gameId, marketKey);
                      }}
                      showTiers={getShowTiersForGame(game.gameIDmlb)}
                      onTierToggle={(show) => handleGameTierToggle(game.gameIDmlb, show)}
                      masterTierFilter={masterTierFilter}
                      onPlayerClick={handlePlayerClick}
                      onOddsClick={handleOddsClick}
                      onPlayerContextMenu={handlePlayerContextMenu}
                      clickedCellId={clickedCellId}
                      isCollapsed={gameCollapseStates[game.gameIDmlb] || false}
                      onCollapseToggle={(isCollapsed) => handleGameCollapseToggle(game.gameIDmlb, isCollapsed)}
                      onSettingsClick={() => setShowSettings(true)}
                    />
                ) : selectedSport === 'ncaab' ? (
                    isMobile ? (
                      <H2HDataTable
                        data={playersByGameIdFiltered[game.gameIDmlb]?.players}
                        gameTime={game.gameTime}
                        gameStatus={game.status}
                        gameDate={getCurrentGamesMetadata()?.date || selectedDate}
                        lastUpdate={playersByGameId[game.gameIDmlb]?.last_update}
                        game={game}
                        formatPlayerName={formatPlayerNameWrapper}
                        getTeamLogoUrl={createTeamLogoGetter(game)}
                        getBookLogoUrl={getBookLogoUrl}
                        isMobile={isMobile}
                        selectedMarket={getSelectedMarketForGame(game.gameIDmlb)}
                        selectedMarketName={availableMarkets.find(m => m.key === getSelectedMarketForGame(game.gameIDmlb))?.name || 'Points'}
                        bookmakers={getEnabledBooks()}
                        isLoading={loadingGameIds.has(game.gameIDmlb)}
                        availableMarkets={availableMarkets}
                        onMarketSelection={(gameId, marketKey) => {
                          if (marketKey !== masterMarket) {
                            setUserSelectedMarkets(prev => ({ ...prev, [gameId]: true }));
                          } else {
                            setUserSelectedMarkets(prev => {
                              const newState = { ...prev };
                              delete newState[gameId];
                              return newState;
                            });
                          }
                          handleMarketSelection(gameId, marketKey);
                        }}
                        showTiers={getShowTiersForGame(game.gameIDmlb)}
                        onTierToggle={(show) => handleGameTierToggle(game.gameIDmlb, show)}
                        masterTierFilter={masterTierFilter}
                        onPlayerClick={handlePlayerClick}
                        onOddsClick={handleOddsClick}
                        onPlayerContextMenu={handlePlayerContextMenu}
                      onPlayerContextMenu={handlePlayerContextMenu}
                        clickedCellId={clickedCellId}
                        isCollapsed={gameCollapseStates[game.gameIDmlb] || false}
                        onCollapseToggle={(isCollapsed) => handleGameCollapseToggle(game.gameIDmlb, isCollapsed)}
                      />
                    ) : (
                      <NCAABDataTable
                        data={playersByGameIdFiltered[game.gameIDmlb]?.players}
                        gameTime={game.gameTime}
                        gameStatus={game.status}
                        gameDate={getCurrentGamesMetadata()?.date || selectedDate}
                        lastUpdate={playersByGameId[game.gameIDmlb]?.last_update}
                        game={game}
                        formatPlayerName={formatPlayerNameWrapper}
                        getTeamLogoUrl={createTeamLogoGetter(game)}
                        getBookLogoUrl={getBookLogoUrl}
                        isMobile={isMobile}
                        selectedMarket={getSelectedMarketForGame(game.gameIDmlb)}
                        selectedMarketName={availableMarkets.find(m => m.key === getSelectedMarketForGame(game.gameIDmlb))?.name || 'Points'}
                        bookmakers={getEnabledBooks()}
                        isLoading={loadingGameIds.has(game.gameIDmlb)}
                        availableMarkets={availableMarkets}
                        onMarketSelection={(gameId, marketKey) => {
                          if (marketKey !== masterMarket) {
                            setUserSelectedMarkets(prev => ({ ...prev, [gameId]: true }));
                          } else {
                            setUserSelectedMarkets(prev => {
                              const newState = { ...prev };
                              delete newState[gameId];
                              return newState;
                            });
                          }
                          handleMarketSelection(gameId, marketKey);
                        }}
                        showTiers={getShowTiersForGame(game.gameIDmlb)}
                        onTierToggle={(show) => handleGameTierToggle(game.gameIDmlb, show)}
                        masterTierFilter={masterTierFilter}
                        onPlayerClick={handlePlayerClick}
                        onOddsClick={handleOddsClick}
                        onPlayerContextMenu={handlePlayerContextMenu}
                      onPlayerContextMenu={handlePlayerContextMenu}
                        onGameClick={(gameId) => { setGamePanelGameId(gameId); setShowGamePanel(true); setShowPlayerPanel(false); }}
                        clickedCellId={clickedCellId}
                        isCollapsed={gameCollapseStates[game.gameIDmlb] || false}
                        onCollapseToggle={(isCollapsed) => handleGameCollapseToggle(game.gameIDmlb, isCollapsed)}
                      />
                    )
                ) : ((selectedSport === 'nba' || selectedSport === 'wnba') && masterMarket === 'h2h') ? (
                    // NBA/WNBA H2H view - use H2HDataTable for mainline markets
                    <H2HDataTable
                      data={playersByGameIdFiltered[game.gameIDmlb]?.players}
                      gameTime={game.gameTime}
                      gameStatus={game.status}
                      gameDate={getCurrentGamesMetadata()?.date || selectedDate}
                      lastUpdate={playersByGameId[game.gameIDmlb]?.last_update}
                      game={game}
                      formatPlayerName={formatPlayerNameWrapper}
                      getTeamLogoUrl={createTeamLogoGetter(game)}
                      getBookLogoUrl={getBookLogoUrl}
                      isMobile={isMobile}
                      selectedMarket={getSelectedMarketForGame(game.gameIDmlb)}
                      selectedMarketName={availableMarkets.find(m => m.key === getSelectedMarketForGame(game.gameIDmlb))?.name || 'Moneyline'}
                      bookmakers={getEnabledBooks()}
                      isLoading={loadingGameIds.has(game.gameIDmlb)}
                      availableMarkets={availableMarkets}
                      onMarketSelection={(gameId, marketKey) => {
                        if (marketKey !== masterMarket) {
                          setUserSelectedMarkets(prev => ({ ...prev, [gameId]: true }));
                        } else {
                          setUserSelectedMarkets(prev => {
                            const newState = { ...prev };
                            delete newState[gameId];
                            return newState;
                          });
                        }
                        handleMarketSelection(gameId, marketKey);
                      }}
                      showTiers={getShowTiersForGame(game.gameIDmlb)}
                      onTierToggle={(show) => handleGameTierToggle(game.gameIDmlb, show)}
                      masterTierFilter={masterTierFilter}
                      onPlayerClick={handlePlayerClick}
                      onOddsClick={handleOddsClick}
                      onPlayerContextMenu={handlePlayerContextMenu}
                      onGameClick={!isMobile ? (gameId) => { setGamePanelGameId(gameId); setShowGamePanel(true); setShowPlayerPanel(false); } : undefined}
                      clickedCellId={clickedCellId}
                      isCollapsed={gameCollapseStates[game.gameIDmlb] || false}
                      onCollapseToggle={(isCollapsed) => handleGameCollapseToggle(game.gameIDmlb, isCollapsed)}
                    />
                ) : (selectedSport === 'wnba' || selectedSport === 'nba') ? (
                    isMobile ? (
                      <MobileBasketballDataTable
                        data={playersByGameIdFiltered[game.gameIDmlb]?.players}
                        gameTime={game.gameTime}
                        gameStatus={game.status}
                        gameDate={getCurrentGamesMetadata()?.date || selectedDate}
                        lastUpdate={playersByGameId[game.gameIDmlb]?.last_update}
                        game={game}
                        formatPlayerName={formatPlayerNameWrapper}
                        getTeamLogoUrl={createTeamLogoGetter(game)}
                        getBookLogoUrl={getBookLogoUrl}
                        selectedMarket={getSelectedMarketForGame(game.gameIDmlb)}
                        selectedMarketName={availableMarkets.find(m => m.key === getSelectedMarketForGame(game.gameIDmlb))?.name || 'Points'}
                        bookmakers={getEnabledBooks()}
                        isLoading={loadingGameIds.has(game.gameIDmlb)}
                        availableMarkets={availableMarkets}
                        nbaViewMode={nbaViewMode}
                        onMarketSelection={(gameId, marketKey) => {
                          // Track if this is a user-specific selection (different from master)
                          if (marketKey !== masterMarket) {
                            setUserSelectedMarkets(prev => ({ ...prev, [gameId]: true }));
                          } else {
                            // If user selects the same as master, remove the user-specific flag
                            setUserSelectedMarkets(prev => {
                              const newState = { ...prev };
                              delete newState[gameId];
                              return newState;
                            });
                          }

                          handleMarketSelection(gameId, marketKey);
                        }}
                        showTiers={getShowTiersForGame(game.gameIDmlb)}
                        onTierToggle={(show) => handleGameTierToggle(game.gameIDmlb, show)}
                        masterTierFilter={masterTierFilter}
                        onPlayerClick={handlePlayerClick}
                        onOddsClick={handleOddsClick}
                        onPlayerContextMenu={handlePlayerContextMenu}
                      onPlayerContextMenu={handlePlayerContextMenu}
                        onOddsContextMenu={handleOddsContextMenu}
                        clickedCellId={clickedCellId}
                        isCollapsed={gameCollapseStates[game.gameIDmlb] || false}
                        onCollapseToggle={(isCollapsed) => handleGameCollapseToggle(game.gameIDmlb, isCollapsed)}
                        onSettingsClick={() => setShowSettings(true)}
                      />
                    ) : (
                      <BasketballDataTable
                        data={playersByGameIdFiltered[game.gameIDmlb]?.players}
                        gameTime={game.gameTime}
                        gameStatus={game.status}
                        gameDate={getCurrentGamesMetadata()?.date || selectedDate}
                        lastUpdate={playersByGameId[game.gameIDmlb]?.last_update}
                        game={game}
                        formatPlayerName={formatPlayerNameWrapper}
                        getTeamLogoUrl={createTeamLogoGetter(game)}
                        getBookLogoUrl={getBookLogoUrl}
                        isMobile={isMobile}
                        isAuthenticated={isAuthenticated}
                        selectedMarket={getSelectedMarketForGame(game.gameIDmlb)}
                        selectedMarketName={availableMarkets.find(m => m.key === getSelectedMarketForGame(game.gameIDmlb))?.name || 'Points'}
                        bookmakers={getEnabledBooks()}
                        isLoading={loadingGameIds.has(game.gameIDmlb)}
                        availableMarkets={availableMarkets}
                        nbaViewMode={nbaViewMode}
                        onMarketSelection={(gameId, marketKey) => {
                          // Track if this is a user-specific selection (different from master)
                          if (marketKey !== masterMarket) {
                            setUserSelectedMarkets(prev => ({ ...prev, [gameId]: true }));
                          } else {
                            // If user selects the same as master, remove the user-specific flag
                            setUserSelectedMarkets(prev => {
                              const newState = { ...prev };
                              delete newState[gameId];
                              return newState;
                            });
                          }

                          handleMarketSelection(gameId, marketKey);
                        }}
                        showTiers={getShowTiersForGame(game.gameIDmlb)}
                        onTierToggle={(show) => handleGameTierToggle(game.gameIDmlb, show)}
                        masterTierFilter={masterTierFilter}
                        onPlayerClick={handlePlayerClick}
                        onOddsClick={handleOddsClick}
                        onPlayerContextMenu={handlePlayerContextMenu}
                      onPlayerContextMenu={handlePlayerContextMenu}
                        onOddsContextMenu={handleOddsContextMenu}
                        clickedCellId={clickedCellId}
                        isCollapsed={gameCollapseStates[game.gameIDmlb] || false}
                        onCollapseToggle={(isCollapsed) => handleGameCollapseToggle(game.gameIDmlb, isCollapsed)}
                        onSettingsClick={() => setShowSettings(true)}
                        recalcEdgeOdds={recalcEdgeOdds}
                      />
                    )
                ) : selectedSport === 'soccer' ? (
                    <SoccerDataTable
                      data={playersByGameIdFiltered[game.gameIDmlb]?.players}
                      gameTime={game.gameTime}
                      gameStatus={game.status}
                      gameDate={getCurrentGamesMetadata()?.date || selectedDate}
                      lastUpdate={playersByGameId[game.gameIDmlb]?.last_update}
                      game={game}
                      formatPlayerName={formatPlayerNameWrapper}
                      getTeamLogoUrl={createTeamLogoGetter(game)}
                      getBookLogoUrl={getBookLogoUrl}
                      isMobile={isMobile}
                      selectedMarket={getSelectedMarketForGame(game.gameIDmlb)}
                      selectedMarketName={availableMarkets.find(m => m.key === getSelectedMarketForGame(game.gameIDmlb))?.name || 'Goal Scorer'}
                      bookmakers={getEnabledBooks()}
                      isLoading={loadingGameIds.has(game.gameIDmlb)}
                      availableMarkets={availableMarkets}
                      onMarketSelection={(gameId, marketKey) => {
                        if (marketKey !== masterMarket) {
                          setUserSelectedMarkets(prev => ({ ...prev, [gameId]: true }));
                        } else {
                          setUserSelectedMarkets(prev => {
                            const newState = { ...prev };
                            delete newState[gameId];
                            return newState;
                          });
                        }
                        handleMarketSelection(gameId, marketKey);
                      }}
                      showTiers={getShowTiersForGame(game.gameIDmlb)}
                      onTierToggle={(show) => handleGameTierToggle(game.gameIDmlb, show)}
                      masterTierFilter={masterTierFilter}
                      onPlayerClick={handlePlayerClick}
                      onOddsClick={handleOddsClick}
                      onRemoveBet={handleRemoveBet}
                      betslipItems={betslipItems}
                      onGameClick={!isMobile ? (gameId) => { setGamePanelGameId(gameId); setShowGamePanel(true); setShowPlayerPanel(false); } : undefined}
                      onPlayerContextMenu={handlePlayerContextMenu}
                      clickedCellId={clickedCellId}
                      isCollapsed={gameCollapseStates[game.gameIDmlb] || false}
                      onCollapseToggle={(isCollapsed) => handleGameCollapseToggle(game.gameIDmlb, isCollapsed)}
                      onSettingsClick={() => setShowSettings(true)}
                    />
                ) : (selectedSport === 'mlb' && masterMarket === 'h2h') ? (
                    // MLB H2H view - use H2HDataTable for mainline markets
                    <H2HDataTable
                      data={playersByGameIdFiltered[game.gameIDmlb]?.players}
                      gameTime={game.gameTime}
                      gameStatus={game.status}
                      gameDate={getCurrentGamesMetadata()?.date || selectedDate}
                      lastUpdate={playersByGameId[game.gameIDmlb]?.last_update}
                      game={{...game, sport: 'mlb'}}
                      formatPlayerName={formatPlayerNameWrapper}
                      getTeamLogoUrl={createTeamLogoGetter(game)}
                      getBookLogoUrl={getBookLogoUrl}
                      isMobile={isMobile}
                      bookmakers={getEnabledBooks()}
                      isLoading={loadingGameIds.has(game.gameIDmlb)}
                      isCollapsed={gameCollapseStates[game.gameIDmlb] || false}
                      onCollapseToggle={(isCollapsed) => handleGameCollapseToggle(game.gameIDmlb, isCollapsed)}
                      onOddsClick={handleOddsClick}
                      onPlayerContextMenu={handlePlayerContextMenu}
                      onGameClick={!isMobile ? (gameId) => { setGamePanelGameId(gameId); setShowGamePanel(true); setShowPlayerPanel(false); } : undefined}
                    />
                ) : (
                    <DataTable
                      data={playersByGameIdFiltered[game.gameIDmlb]?.players}
                      gameTime={game.gameTime}
                      gameStatus={game.status}
                      gameDate={getCurrentGamesMetadata()?.date || selectedDate}
                      lastUpdate={playersByGameId[game.gameIDmlb]?.last_update}
                      game={game}
                      formatPlayerName={formatPlayerNameWrapper}
                      getTeamLogoUrl={createTeamLogoGetter(game)}
                      getBookLogoUrl={getBookLogoUrl}
                      isMobile={isMobile}
                      selectedMarket={getSelectedMarketForGame(game.gameIDmlb)}
                      selectedMarketName={availableMarkets.find(m => m.key === getSelectedMarketForGame(game.gameIDmlb))?.name || 'Doubles'}
                      bookmakers={getEnabledBooks()}
                      isLoading={loadingGameIds.has(game.gameIDmlb)}
                      availableMarkets={availableMarkets}
                      onMarketSelection={(gameId, marketKey) => {
                        // Track if this is a user-specific selection (different from master)
                        if (marketKey !== masterMarket) {
                          setUserSelectedMarkets(prev => ({ ...prev, [gameId]: true }));
                        } else {
                          // If user selects the same as master, remove the user-specific flag
                          setUserSelectedMarkets(prev => {
                            const newState = { ...prev };
                            delete newState[gameId];
                            return newState;
                          });
                        }

                        // Auto-enable tiers for totals market, disable for yards markets and others
                        if (marketKey === 'totals') {
                          handleGameTierToggle(gameId, true);
                        } else if (['player_pass_yds', 'player_rush_yds', 'player_reception_yds', 'player_rush_reception_yds', 'player_field_goals'].includes(marketKey)) {
                          // Yards markets default to tiers off to show main O/U line
                          handleGameTierToggle(gameId, false);
                        } else {
                          handleGameTierToggle(gameId, false);
                        }

                        handleMarketSelection(gameId, marketKey);
                      }}
                      showTiers={getShowTiersForGame(game.gameIDmlb)}
                      onTierToggle={(show) => handleGameTierToggle(game.gameIDmlb, show)}
                      masterTierFilter={masterTierFilter}
                      onPlayerClick={handlePlayerClick}
                      onOddsClick={handleOddsClick}
                      onPlayerContextMenu={handlePlayerContextMenu}
                      clickedCellId={clickedCellId}
                      isCollapsed={gameCollapseStates[game.gameIDmlb] || false}
                      onCollapseToggle={(isCollapsed) => handleGameCollapseToggle(game.gameIDmlb, isCollapsed)}
                      onSettingsClick={() => setShowSettings(true)}
                    />
                )}
              </div>
              </React.Fragment>
            );
          });
        })()}
        </div>
      </div>

      {/* Best Bets Panel (desktop only) */}
      {showBestBetsPanel && !isMobile && (
        <BestBetsPanel
          bestBets={bestBetsPanelData}
          onClose={() => setShowBestBetsPanel(false)}
          getTeamLogoUrl={getTeamLogoUrlForSport}
          selectedSport={selectedSport}
          topOffset={marketBannerBottom}
          onPlayerClick={handlePlayerClick}
          onOddsClick={handleOddsClick}
          halfWidth={showPlayerPanel || showGamePanel || (showIqOverlay && (selectedSport === 'nba' || selectedSport === 'wnba') && (showBetslipPanel || showScratchpadPanel))}
          rightOffset={
            showGamePanel ? 'calc(55% + 1px)'
              : showPlayerPanel ? 'calc(50% + 1px)'
              : (showIqOverlay && (selectedSport === 'nba' || selectedSport === 'wnba') && (showBetslipPanel || showScratchpadPanel)) ? '60%'
              : (showIqOverlay && (selectedSport === 'nba' || selectedSport === 'wnba')) ? '585px'
              : (showBetslipPanel || showScratchpadPanel) ? '410px'
              : '5px'
          }
          compact={!showPlayerPanel && !showGamePanel && !(showIqOverlay && (selectedSport === 'nba' || selectedSport === 'wnba') && (showBetslipPanel || showScratchpadPanel)) && ((showIqOverlay && (selectedSport === 'nba' || selectedSport === 'wnba')) || showBetslipPanel || showScratchpadPanel)}
          onGameClick={(gameId) => {
            setGamePanelGameId(gameId);
            setShowGamePanel(true);
            setShowPlayerPanel(false);
          }}
          onGamePanelClick={(gameId) => {
            setGamePanelGameId(gameId);
            setShowGamePanel(true);
            setShowPlayerPanel(false);
          }}
          onTrendsClick={(gameId) => {
            setVisibleGameId(gameId);
            setShowIqOverlay(true);
            setShowLeadersOverlay(false);
            setShowPlayerPanel(false);
            setShowGamePanel(false);
          }}
        />
      )}

      {/* Matchups Panel (MLB) */}
      {showMatchupsPanel && selectedSport === 'mlb' && (
        <MatchupsPanel
          visibleGameId={visibleGameId}
          visibleGame={filteredGamesForRender.find(g => g.gameIDmlb === visibleGameId)}
          games={filteredGamesForRender}
          playersByGameIdRef={playersByGameIdRef}
          isMobile={isMobile}
          showBetslipPanel={showBetslipPanel}
          marketBannerBottom={marketBannerBottom}
          onClose={() => setShowMatchupsPanel(false)}
          onPlayerClick={handlePlayerClick}
          onPlayerContextMenu={handlePlayerContextMenu}
        />
      )}

      {/* IQ Overlay for NBA/WNBA */}
      {showIqOverlay && (selectedSport === 'nba' || selectedSport === 'wnba') && !isMobile && (
        <div
          className="dark-panel"
          style={{
            position: 'fixed',
            top: `${marketBannerBottom}px`,
            right: (showBestBetsPanel && showBetslipPanel) ? 'calc(25% + 2px)' : showBetslipPanel ? '410px' : '5px',
            left: (showBestBetsPanel && showBetslipPanel) ? 'calc(40% + 2px)' : undefined,
            width: (showBestBetsPanel && showBetslipPanel) ? undefined : showBetslipPanel ? 'calc(33.33% - 200px)' : '33.33%',
            minWidth: (showBestBetsPanel && showBetslipPanel) ? undefined : showBetslipPanel ? '450px' : '575px',
            bottom: 0,
            backgroundColor: '#000',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            padding: '0',
            borderLeft: '1px solid #333',
            borderRadius: '12px',
            transition: 'right 0.2s ease, width 0.2s ease'
          }}
        >
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid #333', flexShrink: 0, width: '100%' }}>
            <span style={{ color: '#fff', fontFamily: "'Oswald', sans-serif", fontSize: '1rem', fontWeight: '600' }}>TRENDS</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div className="btn-group btn-group-sm" role="group">
                <button
                  type="button"
                  className={`btn ${trendsYear === '2026' ? 'btn-warning' : 'btn-outline-warning'}`}
                  onClick={() => setTrendsYear('2026')}
                  style={{ fontSize: '0.7rem', padding: '2px 8px' }}
                >
                  2026
                </button>
                <button
                  type="button"
                  className={`btn ${trendsYear === '2025' ? 'btn-warning' : 'btn-outline-warning'}`}
                  onClick={() => setTrendsYear('2025')}
                  style={{ fontSize: '0.7rem', padding: '2px 8px' }}
                >
                  2025
                </button>
              </div>
              <button
                onClick={() => setShowTrendsExpanded(true)}
                title="Expand"
                style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', padding: '0 4px', display: 'flex', alignItems: 'center' }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2 6V2h4M14 6V2h-4M2 10v4h4M14 10v4h-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button onClick={() => setShowIqOverlay(false)} style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '20px', cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}>×</button>
            </div>
          </div>
          <div style={{ flex: 1, overflow: 'auto', padding: '16px', width: '100%' }}>
          {(() => {
            const visibleGame = filteredGamesForRender.find(g => g.gameIDmlb === visibleGameId);
            const teamsLabel = visibleGame ? `${visibleGame.away} @ ${visibleGame.home}` : '';
            const gameOddsData = visibleGameId ? playersByGameId[visibleGameId] : null;
            const trendsRosterMap = buildTrendsRosterMap(trendsTodayGames, visibleGame);

            const handleReportPlayerClick = (playerId, playerName, playerTeam) => {
              handlePlayerClick(visibleGameId, playerId, playerName, masterMarket, visibleGame?.away, visibleGame?.home, playerTeam, null, null);
            };

            // First basket uses TrendsFirstBasketReport
            if (masterMarket === 'player_first_basket') {
              return (
                <TrendsFirstBasketReport
                  data={iqData}
                  teams={teamsLabel}
                  loading={iqLoading}
                  oddsData={gameOddsData}
                  onPlayerClick={handleReportPlayerClick}
                  isPastYear={trendsYear !== String(new Date().getFullYear())}
                  sport={selectedSport}
                  currentTeamByPlayerId={trendsRosterMap}
                />
              );
            }

            // Double double and triple double use TrendsDoubleDoubleReport
            if (masterMarket === 'player_double_double' || masterMarket === 'player_triple_double') {
              return (
                <TrendsDoubleDoubleReport
                  data={iqData}
                  teams={teamsLabel}
                  loading={iqLoading}
                  oddsData={gameOddsData}
                  market={masterMarket}
                  onPlayerClick={handleReportPlayerClick}
                  isPastYear={trendsYear !== String(new Date().getFullYear())}
                  sport={selectedSport}
                  currentTeamByPlayerId={trendsRosterMap}
                />
              );
            }

            // Non-binary markets use TrendsPropReport
            const propMarkets = [
              'player_points', 'player_rebounds', 'player_assists', 'player_threes',
              'player_steals', 'player_blocks', 'player_turnovers',
              'player_points_rebounds_assists', 'player_points_rebounds', 'player_points_assists', 'player_rebounds_assists'
            ];

            if (propMarkets.includes(masterMarket)) {
              return (
                <TrendsPropReport
                  data={iqData}
                  teams={teamsLabel}
                  market={masterMarket}
                  loading={iqLoading}
                  oddsData={gameOddsData}
                  onPlayerClick={handleReportPlayerClick}
                  isPastYear={trendsYear !== String(new Date().getFullYear())}
                  sport={selectedSport}
                  currentTeamByPlayerId={trendsRosterMap}
                />
              );
            }

            // Default display for unknown markets
            return (
              <>
                <h3 style={{ color: '#fff', marginBottom: '10px' }}>IQ</h3>
                {visibleGame && (
                  <div style={{ color: '#17a2b8', fontSize: '0.9rem', marginBottom: '15px' }}>
                    {teamsLabel}
                  </div>
                )}
                {iqLoading && (
                  <div style={{ color: '#fff' }}>Loading...</div>
                )}
                {!iqLoading && iqData && (
                  <div style={{ color: '#fff', width: '100%', overflowY: 'auto' }}>
                    <pre style={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {JSON.stringify(iqData, null, 2)}
                    </pre>
                  </div>
                )}
                {!iqLoading && !iqData && (
                  <div style={{ color: '#888' }}>No data available</div>
                )}
              </>
            );
          })()}
          </div>
        </div>
      )}

      {/* Mobile Trends Overlay */}
      {showMobileTrends && (selectedSport === 'nba' || selectedSport === 'wnba') && (
        <div
          style={{
            position: 'fixed',
            top: `${marketBannerBottom}px`, // Precisely below the markets banner
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            zIndex: 1100,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px'
          }}
        >
          {/* Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 16px',
            borderBottom: '1px solid #333'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="#ffc107" stroke="#ffc107" strokeWidth="0.5" viewBox="0 0 16 16">
                <path d="M0 0h1v15h15v1H0V0Zm14.817 3.113a.5.5 0 0 1 .07.704l-4.5 5.5a.5.5 0 0 1-.74.037L7.06 6.767l-3.656 5.027a.5.5 0 0 1-.808-.588l4-5.5a.5.5 0 0 1 .758-.06l2.609 2.61 4.15-5.073a.5.5 0 0 1 .704-.07Z"/>
              </svg>
              <span style={{ color: '#ffc107', fontFamily: "'Oswald', sans-serif", fontSize: '1.1em', fontWeight: '600' }}>TRENDS</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div className="btn-group btn-group-sm" role="group">
                <button
                  type="button"
                  className={`btn ${trendsYear === '2026' ? 'btn-warning' : 'btn-outline-warning'}`}
                  onClick={() => setTrendsYear('2026')}
                  style={{ fontSize: '0.7rem', padding: '2px 8px' }}
                >
                  2026
                </button>
                <button
                  type="button"
                  className={`btn ${trendsYear === '2025' ? 'btn-warning' : 'btn-outline-warning'}`}
                  onClick={() => setTrendsYear('2025')}
                  style={{ fontSize: '0.7rem', padding: '2px 8px' }}
                >
                  2025
                </button>
              </div>
              <button
                onClick={() => setShowMobileTrends(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#fff',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0 8px'
                }}
              >
                ×
              </button>
            </div>
          </div>

          {/* Content */}
          <div style={{
            flex: 1,
            overflow: 'auto',
            padding: '16px'
          }}>
            {(() => {
              const visibleGame = filteredGamesForRender.find(g => g.gameIDmlb === visibleGameId);
              const teamsLabel = visibleGame ? `${visibleGame.away} @ ${visibleGame.home}` : '';
              const gameOddsData = visibleGameId ? playersByGameId[visibleGameId] : null;
              const trendsRosterMap = buildTrendsRosterMap(trendsTodayGames, visibleGame);

              const handleReportPlayerClick = (playerId, playerName, playerTeam) => {
                handlePlayerClick(visibleGameId, playerId, playerName, masterMarket, visibleGame?.away, visibleGame?.home, playerTeam, null, null);
              };

              // First basket uses TrendsFirstBasketReport
              if (masterMarket === 'player_first_basket') {
                return (
                  <TrendsFirstBasketReport
                    data={iqData}
                    teams={teamsLabel}
                    loading={iqLoading}
                    oddsData={gameOddsData}
                    onPlayerClick={handleReportPlayerClick}
                    isPastYear={trendsYear !== String(new Date().getFullYear())}
                  sport={selectedSport}
                  currentTeamByPlayerId={trendsRosterMap}
                  />
                );
              }

              // Double double and triple double use TrendsDoubleDoubleReport
              if (masterMarket === 'player_double_double' || masterMarket === 'player_triple_double') {
                return (
                  <TrendsDoubleDoubleReport
                    data={iqData}
                    teams={teamsLabel}
                    loading={iqLoading}
                    oddsData={gameOddsData}
                    market={masterMarket}
                    onPlayerClick={handleReportPlayerClick}
                    isPastYear={trendsYear !== String(new Date().getFullYear())}
                  sport={selectedSport}
                  currentTeamByPlayerId={trendsRosterMap}
                  />
                );
              }

              // Non-binary markets use TrendsPropReport
              const propMarkets = [
                'player_points', 'player_rebounds', 'player_assists', 'player_threes',
                'player_steals', 'player_blocks', 'player_turnovers',
                'player_points_rebounds_assists', 'player_points_rebounds', 'player_points_assists', 'player_rebounds_assists'
              ];

              if (propMarkets.includes(masterMarket)) {
                return (
                  <TrendsPropReport
                    data={iqData}
                    teams={teamsLabel}
                    market={masterMarket}
                    loading={iqLoading}
                    oddsData={gameOddsData}
                    onPlayerClick={handleReportPlayerClick}
                    isPastYear={trendsYear !== String(new Date().getFullYear())}
                  sport={selectedSport}
                  currentTeamByPlayerId={trendsRosterMap}
                  />
                );
              }

              // Default - loading or no data
              if (iqLoading) {
                return <div style={{ color: '#fff', textAlign: 'center', padding: '40px' }}>Loading...</div>;
              }

              return <div style={{ color: '#888', textAlign: 'center', padding: '40px' }}>Select a supported market to view trends</div>;
            })()}
          </div>
        </div>
      )}

      {/* Expanded Trends Panel (iframe) */}
      {showTrendsExpanded && (selectedSport === 'nba' || selectedSport === 'wnba') && (
        <div
          style={{
            position: 'fixed',
            top: isMobile ? 0 : `${marketBannerBottom}px`,
            left: isMobile ? 0 : '5px',
            right: isMobile ? 0 : '5px',
            bottom: 0,
            backgroundColor: '#000',
            zIndex: 1200,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: isMobile ? 0 : '12px',
            border: isMobile ? 'none' : '1px solid #333',
            overflow: 'hidden'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '10px 16px', borderBottom: '1px solid #333', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <button
                onClick={() => setShowTrendsExpanded(false)}
                title="Minimize"
                style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', padding: '0 8px', display: 'flex', alignItems: 'center' }}
              >
                <svg width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 12h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                </svg>
              </button>
              <button
                onClick={() => {
                  setShowTrendsExpanded(false);
                  setShowIqOverlay(false);
                  setShowMobileTrends(false);
                }}
                title="Close"
                style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '24px', cursor: 'pointer', padding: '0 8px', lineHeight: 1 }}
              >
                ×
              </button>
            </div>
          </div>
          <iframe
            src={`/${selectedSport}/trends.html?embed=true&season=${trendsYear}`}
            style={{ flex: 1, border: 'none', backgroundColor: '#000' }}
            title="Trends"
          />
        </div>
      )}

      {/* Leaders overlay (NBA + NFL) — reuses the expanded Trends shell,
          iframes the per-sport leaders page. Header shows close controls
          only. */}
      {showLeadersOverlay && (selectedSport === 'nba' || selectedSport === 'nfl') && (() => {
        const leadersSrc = selectedSport === 'nfl'
          ? `/nfl/leaders/week.html?embed=true`
          : `/nba/leaders/daily.html?embed=true`;
        const leadersTitle = selectedSport === 'nfl' ? 'NFL Leaders' : 'NBA Leaders';
        return (
          <div
            style={{
              position: 'fixed',
              // Mobile: sit directly under the market banner (matches the
              // Neg Corr overlay) so the sport nav + banner stay visible.
              // Desktop: side-docked with 5 px gutters, and if the Betslip
              // panel is also open, pull in from the right so the two sit
              // side-by-side.
              top: `${marketBannerBottom}px`,
              left: isMobile ? 0 : '5px',
              right: isMobile ? 0 : (showBetslipPanel ? 'calc(410px + 2px)' : '5px'),
              bottom: 0,
              backgroundColor: isMobile ? 'rgba(0, 0, 0, 0.95)' : '#000',
              zIndex: isMobile ? 1100 : 1200,
              display: 'flex',
              flexDirection: 'column',
              borderTopLeftRadius: '12px',
              borderTopRightRadius: '12px',
              borderBottomLeftRadius: isMobile ? 0 : '12px',
              borderBottomRightRadius: isMobile ? 0 : '12px',
              border: isMobile ? 'none' : '1px solid #333',
              overflow: 'hidden',
              transition: 'right 0.2s ease'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '10px 16px', borderBottom: '1px solid #333', flexShrink: 0 }}>
              <button
                onClick={() => setShowLeadersOverlay(false)}
                title="Close"
                style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '24px', cursor: 'pointer', padding: '0 8px', lineHeight: 1 }}
              >
                ×
              </button>
            </div>
            <iframe
              src={leadersSrc}
              style={{ flex: 1, border: 'none', backgroundColor: '#000' }}
              title={leadersTitle}
            />
          </div>
        );
      })()}

      {/* NFL Negative Correlation overlay — iframes the backend page for
          the currently-selected NFL week. Same fullscreen chrome as the
          NBA/WNBA trends overlay above. Playoff weeks come through
          `playoff-N`, so strip the prefix for the URL. */}
      {showNegCorrOverlay && selectedSport === 'nfl' && (() => {
        const rawWeek = nflWeekForApi ?? nflSelectedWeek;
        const weekParam = typeof rawWeek === 'string' ? rawWeek.replace(/^playoff-/, '') : rawWeek;
        return (
          <div
            style={{
              position: 'fixed',
              // Mobile: sit directly under the market banner (matches the
              // WNBA/NBA mobile trends overlay) so the sport nav + banner
              // stay visible. Desktop: side-docked with 5 px gutters, and
              // if the Betslip panel is also open, pull in from the right
              // (410 px panel + 2 px gap) so the two sit side-by-side
              // instead of the Betslip hovering on top of the overlay.
              top: `${marketBannerBottom}px`,
              left: isMobile ? 0 : '5px',
              right: isMobile ? 0 : (showBetslipPanel ? 'calc(410px + 2px)' : '5px'),
              bottom: 0,
              backgroundColor: isMobile ? 'rgba(0, 0, 0, 0.95)' : '#000',
              zIndex: isMobile ? 1100 : 1200,
              display: 'flex',
              flexDirection: 'column',
              borderTopLeftRadius: '12px',
              borderTopRightRadius: '12px',
              borderBottomLeftRadius: isMobile ? 0 : '12px',
              borderBottomRightRadius: isMobile ? 0 : '12px',
              border: isMobile ? 'none' : '1px solid #333',
              overflow: 'hidden',
              transition: 'right 0.2s ease'
            }}
          >
            {/* 3-column header: empty left spacer | centered title | close ×.
                Spacers share `flex: 1` so the title stays optically centered
                in the header regardless of its own width. Title scales down
                a touch on mobile so the full phrase stays on one line. */}
            <div style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid #333', flexShrink: 0 }}>
              <div style={{ flex: 1 }} />
              <span style={{
                color: '#ffc107',
                fontFamily: '"Bebas Neue", sans-serif',
                fontSize: isMobile ? '1.1rem' : '1.75rem',
                fontWeight: 600,
                letterSpacing: '0.02em',
                textAlign: 'center',
                whiteSpace: 'nowrap'
              }}>
                NFL Negative Correlation Parlays
              </span>
              <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowNegCorrOverlay(false)}
                  title="Close"
                  style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '24px', cursor: 'pointer', padding: '0 8px', lineHeight: 1 }}
                >
                  ×
                </button>
              </div>
            </div>
            <iframe
              src={`/nfl/betslips/correlation/?week=${weekParam}&embed=true`}
              style={{ flex: 1, border: 'none', backgroundColor: '#000' }}
              title="NFL Negative Correlation"
            />
          </div>
        );
      })()}

      {/* Soccer Auto Parlays overlay — iframes /soccer/betslips/auto-parlays/.
          Same fullscreen chrome as the NFL Neg Corr overlay above. */}
      {showAutoParlaysOverlay && selectedSport === 'soccer' && (
        <div
          style={{
            position: 'fixed',
            top: `${marketBannerBottom}px`,
            left: isMobile ? 0 : '5px',
            right: isMobile ? 0 : (showBetslipPanel ? 'calc(410px + 2px)' : '5px'),
            bottom: 0,
            backgroundColor: isMobile ? 'rgba(0, 0, 0, 0.95)' : '#000',
            zIndex: isMobile ? 1100 : 1200,
            display: 'flex',
            flexDirection: 'column',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            borderBottomLeftRadius: isMobile ? 0 : '12px',
            borderBottomRightRadius: isMobile ? 0 : '12px',
            border: isMobile ? 'none' : '1px solid #333',
            overflow: 'hidden',
            transition: 'right 0.2s ease'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid #333', flexShrink: 0 }}>
            <div style={{ flex: 1 }} />
            <span style={{
              color: '#ffc107',
              fontFamily: '"Bebas Neue", sans-serif',
              fontSize: isMobile ? '1.1rem' : '1.75rem',
              fontWeight: 600,
              letterSpacing: '0.02em',
              textAlign: 'center',
              whiteSpace: 'nowrap'
            }}>
              Soccer Auto Parlays
            </span>
            <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowAutoParlaysOverlay(false)}
                title="Close"
                style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '24px', cursor: 'pointer', padding: '0 8px', lineHeight: 1 }}
              >
                ×
              </button>
            </div>
          </div>
          <iframe
            src={`/soccer/betslips/auto-parlays/?embed=true`}
            style={{ flex: 1, border: 'none', backgroundColor: '#000' }}
            title="Soccer Auto Parlays"
          />
        </div>
      )}

      {/* Desktop Player Detail Panel */}
      {showPlayerPanel && !isMobile && (() => {
        const isFull = playerPanelFull && !showBestBetsPanel;
        const effectiveLeft = showBestBetsPanel ? 'calc(50% + 1px)' : isFull ? '5px' : undefined;
        const effectiveRight = showBestBetsPanel ? '5px' : isFull ? '5px' : playerPanelRight;
        const effectiveWidth = showBestBetsPanel ? undefined : isFull ? undefined : '50%';
        const panelIsWide = isFull;
        return (
          <div
            className="dark-panel"
            style={{
              position: 'fixed',
              top: `${marketBannerBottom}px`,
              right: effectiveRight,
              left: effectiveLeft,
              width: effectiveWidth,
              bottom: 0,
              minWidth: '400px',
              backgroundColor: 'rgba(0, 0, 0, 0.95)',
              zIndex: 1000,
              display: 'flex',
              flexDirection: 'column',
              borderLeft: isFull ? 'none' : '1px solid #333',
              borderRadius: '12px',
              transition: 'right 0.2s ease, left 0.2s ease, width 0.2s ease'
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid #333', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {playerPanelData.playerId && (
                  <img
                    src={getPlayerHeadshotUrl(playerPanelData.playerId, selectedSport)}
                    alt={playerPanelData.playerName}
                    style={{ width: '28px', height: '28px', borderRadius: '50%', objectFit: 'cover', backgroundColor: '#333' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
                <span style={{ color: '#fff', fontFamily: "'Oswald', sans-serif", fontSize: '1rem', fontWeight: '600' }}>
                  {playerPanelData.playerName}
                </span>
                {selectedSport === 'mlb' && playerPanelHandInfo && (
                  <span style={{ color: '#adb5bd', fontSize: '0.75rem' }}>
                    {playerPanelHandInfo.isPitcher
                      ? `Throws ${playerPanelHandInfo.pitchHand === 'R' ? 'Right' : playerPanelHandInfo.pitchHand === 'L' ? 'Left' : playerPanelHandInfo.pitchHand}`
                      : `Bats ${playerPanelHandInfo.batSide === 'R' ? 'Right' : playerPanelHandInfo.batSide === 'L' ? 'Left' : playerPanelHandInfo.batSide === 'S' ? 'Switch' : playerPanelHandInfo.batSide}`}
                  </span>
                )}
                {(playerPanelData.playerTeam || playerPanelData.playerPosition) && (
                  <span style={{ color: '#6c757d', fontSize: '0.8rem' }}>
                    {playerPanelData.playerTeam}{playerPanelData.playerPosition ? ` (${playerPanelData.playerPosition})` : ''}
                  </span>
                )}
                <AddToScratchpadLink
                  playerData={playerPanelData}
                  onToggleScratchpad={handleToggleScratchpad}
                  isInScratchpad={isInScratchpad}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {!showBestBetsPanel && (
                  <div style={{ display: 'flex', borderRadius: '4px', overflow: 'hidden', border: '1px solid #4b5563' }}>
                    <button
                      onClick={() => setPlayerPanelFull(false)}
                      style={{
                        padding: '2px 8px', fontSize: '0.6rem', border: 'none', cursor: 'pointer',
                        backgroundColor: !playerPanelFull ? '#0d6efd' : 'transparent',
                        color: !playerPanelFull ? 'white' : '#9CA3AF'
                      }}
                    >Half</button>
                    <button
                      onClick={() => setPlayerPanelFull(true)}
                      style={{
                        padding: '2px 8px', fontSize: '0.6rem', border: 'none', cursor: 'pointer',
                        borderLeft: '1px solid #4b5563',
                        backgroundColor: playerPanelFull ? '#0d6efd' : 'transparent',
                        color: playerPanelFull ? 'white' : '#9CA3AF'
                      }}
                    >Full</button>
                  </div>
                )}
                <button onClick={() => setShowPlayerPanel(false)} style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '20px', cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}>×</button>
              </div>
            </div>

            {/* Content - responsive: row when wide, column when narrow */}
            <div style={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: panelIsWide ? 'row' : 'column',
              overflow: panelIsWide ? 'hidden' : 'auto',
              padding: '16px',
              gap: '16px'
            }}>
              {/* Left column: Odds Table + Line movement chart below */}
              <div style={{
                ...(panelIsWide
                  ? { width: '50%', maxWidth: '50%', flexShrink: 0, overflow: 'auto' }
                  : { flex: 'none' }
                )
              }}>
                <PlayerOddsTable
                  gameId={playerPanelData.gameId}
                  game={filteredGamesForRender.find(g => g.gameIDmlb === playerPanelData.gameId)}
                  playerId={playerPanelData.playerId}
                  playerName={playerPanelData.playerName}
                  playerTeam={playerPanelData.playerTeam}
                  playerPosition={playerPanelData.playerPosition}
                  awayTeam={playerPanelData.awayTeam}
                  homeTeam={playerPanelData.homeTeam}
                  market={playerPanelData.market}
                  sport={selectedSport}
                  opposingPitcher={playerPanelData.opposingPitcher}
                  marketBannerBottom={marketBannerBottom}
                  onOddsClick={handleOddsClick}
                  onChartClick={(marketKey, point, isOver) => {
                    setPlayerPanelChartParams({ marketKey, point, isOver });
                    setPlayerPanelView('chart');
                    setTimeout(() => {
                      if (playerPanelChartRef.current) {
                        playerPanelChartRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }, 100);
                  }}
                  onMarketChange={(marketKey) => setPlayerPanelActiveMarketKey(marketKey)}
                  onLineChange={(val) => setPlayerPanelLineValue(val)}
                  onPlayerInfo={handlePlayerPanelInfo}
                  gameLogTargetNode={panelIsWide && selectedSport === 'mlb' ? playerPanelGameLogNode : null}
                  chartElement={playerPanelView === 'chart' && playerPanelChartParams ? (
                    <div ref={playerPanelChartRef} style={{ marginTop: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ color: '#9CA3AF', fontSize: '0.75rem', fontWeight: 600 }}>Line Movement</span>
                      </div>
                      <div style={{ height: '350px' }}>
                        <PlayerOddsChart
                          gameId={playerPanelData.gameId}
                          playerId={playerPanelData.playerId}
                          marketKey={playerPanelChartParams.marketKey}
                          point={playerPanelChartParams.point}
                          isOver={playerPanelChartParams.isOver}
                          sport={selectedSport}
                          onBack={() => { setPlayerPanelView('table'); setPlayerPanelChartParams(null); }}
                        />
                      </div>
                    </div>
                  ) : null}
                />
              </div>

              {/* Right column: Game log for MLB in wide mode */}
              {panelIsWide && selectedSport === 'mlb' && (
                <div ref={setPlayerPanelGameLogNode} style={{ flex: 1, minWidth: 0, overflow: 'auto' }} />
              )}

              {/* Right column: Performance chart (only in wide mode) */}
              {panelIsWide && (selectedSport === 'nba' || selectedSport === 'wnba' || selectedSport === 'nhl' || selectedSport === 'nfl') && (
                <div style={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
                  <PlayerStatsChart
                    playerId={playerPanelData.playerId}
                    sport={selectedSport}
                    marketType={playerPanelActiveMarketKey || playerPanelData.market}
                    playerTeam={playerPanelData.playerTeam}
                    awayTeam={playerPanelData.awayTeam}
                    homeTeam={playerPanelData.homeTeam}
                    lineValue={playerPanelLineValue}
                  />
                </div>
              )}

              {/* Performance chart below when NOT wide */}
              {!panelIsWide && (selectedSport === 'nba' || selectedSport === 'wnba' || selectedSport === 'nhl' || selectedSport === 'nfl') && (
                <PlayerStatsChart
                  playerId={playerPanelData.playerId}
                  sport={selectedSport}
                  marketType={playerPanelActiveMarketKey || playerPanelData.market}
                  playerTeam={playerPanelData.playerTeam}
                  awayTeam={playerPanelData.awayTeam}
                  homeTeam={playerPanelData.homeTeam}
                  lineValue={playerPanelLineValue}
                />
              )}
            </div>
          </div>
        );
      })()}

      {/* Desktop Game Panel (all sports) */}
      {showGamePanel && !isMobile && (() => {
        const effectiveLeft = showBestBetsPanel ? 'calc(45% + 1px)' : '5px';
        const effectiveRight = showBestBetsPanel ? '5px' : ((showBetslipPanel || showScratchpadPanel) ? '410px' : '5px');
        // Look up the game from the full pre-filter list so a click coming
        // from Best Bets still resolves when a conference / Top-25 filter is
        // narrowing the on-screen games list (otherwise the game object is
        // undefined and awayLogo / homeLogo render blank).
        const allSportGames = getCurrentGamesData();
        const gamePanelGame = (allSportGames || filteredGamesForRender).find(g => g.gameIDmlb === gamePanelGameId)
          || filteredGamesForRender.find(g => g.gameIDmlb === gamePanelGameId);
        return (
          <div
            className="dark-panel"
            style={{
              position: 'fixed',
              top: `${marketBannerBottom}px`,
              right: effectiveRight,
              left: effectiveLeft,
              bottom: 0,
              minWidth: '400px',
              backgroundColor: 'rgba(0, 0, 0, 0.95)',
              zIndex: 1000,
              display: 'flex',
              flexDirection: 'column',
              borderLeft: '1px solid #333',
              borderRadius: '12px',
              transition: 'right 0.2s ease, left 0.2s ease'
            }}
          >
            <GamePanel
              gameId={gamePanelGameId}
              game={gamePanelGame}
              sport={selectedSport}
              bookmakers={bookOrder}
              onClose={() => setShowGamePanel(false)}
              onOddsClick={handleOddsClick}
            />
          </div>
        );
      })()}

      {/* Desktop Betslip Panel — when Scratchpad is also open, only render this
          panel if Betslip is the active tab. */}
      {showBetslipPanel && (!showScratchpadPanel || activeRightPanel === 'betslip') && (selectedSport === 'nba' || selectedSport === 'wnba' || selectedSport === 'nhl' || selectedSport === 'ncaab' || selectedSport === 'mlb' || selectedSport === 'nfl' || selectedSport === 'soccer' || selectedSport === 'ncaaf') && !isMobile && (
        <div
          className="dark-panel"
          style={{
            position: 'fixed',
            top: `${marketBannerBottom}px`,
            right: '5px',
            left: (showBestBetsPanel && showIqOverlay) ? 'calc(75% + 2px)' : undefined,
            width: (showBestBetsPanel && showIqOverlay) ? undefined : '400px',
            bottom: 0,
            backgroundColor: '#000',
            zIndex: showBestBetsPanel ? 1001 : 1000,
            display: 'flex',
            flexDirection: 'column',
            borderLeft: '1px solid #333',
            borderRadius: '12px',
            transition: 'left 0.2s ease, width 0.2s ease'
          }}
        >
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid #333', flexShrink: 0 }}>
            {showScratchpadPanel ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <button
                  onClick={() => setActiveRightPanel('betslip')}
                  style={{
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    color: '#17a2b8', fontFamily: "'Oswald', sans-serif", fontSize: '0.95rem', fontWeight: 600,
                    borderBottom: '2px solid #17a2b8', padding: '4px 8px'
                  }}
                >
                  BETSLIP{betslipItems.length > 0 ? ` (${betslipItems.length})` : ''}
                </button>
                <button
                  onClick={() => setActiveRightPanel('scratchpad')}
                  style={{
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    color: '#9ca3af', fontFamily: "'Oswald', sans-serif", fontSize: '0.95rem', fontWeight: 600,
                    borderBottom: '2px solid transparent', padding: '4px 8px'
                  }}
                >
                  SCRATCHPAD{scratchpadItems.length > 0 ? ` (${scratchpadItems.length})` : ''}
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 4h16v4.5c-1.1 0-2 .9-2 2s.9 2 2 2V20H4v-7.5c1.1 0 2-.9 2-2s-.9-2-2-2V4z" stroke="#17a2b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                </svg>
                <span style={{ color: '#17a2b8', fontFamily: "'Oswald', sans-serif", fontSize: '1rem', fontWeight: '600' }}>BETSLIP</span>
                <span style={{ fontSize: '0.55rem', fontWeight: 700, color: '#fff', backgroundColor: '#0d6efd', padding: '1px 5px', borderRadius: '3px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Beta</span>
                {betslipItems.length > 0 && (
                  <span style={{ color: '#6c757d', fontSize: '0.8rem' }}>({betslipItems.length})</span>
                )}
              </div>
            )}
            <button onClick={() => setShowBetslipPanel(false)} style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '20px', cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}>×</button>
          </div>

          {/* Content */}
          <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
            <BetslipModal
              show={false}
              onHide={() => {}}
              betslipItems={betslipItems}
              onRemoveBet={handleRemoveBet}
              onClearAll={handleClearBetslip}
              isDarkMode={true}
              getTeamLogoUrl={getTeamLogoUrlForSport}
              renderInline={true}
              showBetslipIframe={showBetslipIframe}
              onToggleBetFinder={() => setShowBetslipIframe(prev => !prev)}
              selectedSport={selectedSport}
              onPlayerClick={(bet) => handlePlayerClick(bet.gameId, bet.playerId, bet.playerName, bet.market, bet.awayTeam, bet.homeTeam, bet.team, null, bet.line)}
            />
          </div>
        </div>
      )}

      {/* Mobile Betslip Panel */}
      {showBetslipPanel && (selectedSport === 'nba' || selectedSport === 'wnba' || selectedSport === 'nhl' || selectedSport === 'ncaab' || selectedSport === 'mlb' || selectedSport === 'nfl' || selectedSport === 'soccer' || selectedSport === 'ncaaf') && isMobile && (
        <div
          style={{
            position: 'fixed',
            top: `${marketBannerBottom}px`,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            zIndex: 1100,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px'
          }}
        >
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid #333' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 4h16v4.5c-1.1 0-2 .9-2 2s.9 2 2 2V20H4v-7.5c1.1 0 2-.9 2-2s-.9-2-2-2V4z" stroke="#17a2b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
              <span style={{ color: '#17a2b8', fontFamily: "'Oswald', sans-serif", fontSize: '1.1em', fontWeight: '600' }}>BETSLIP</span>
              <span style={{ fontSize: '0.55rem', fontWeight: 700, color: '#fff', backgroundColor: '#0d6efd', padding: '1px 5px', borderRadius: '3px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Beta</span>
              {betslipItems.length > 0 && (
                <span style={{ color: '#6c757d', fontSize: '0.85rem' }}>({betslipItems.length})</span>
              )}
            </div>
            <button onClick={() => setShowBetslipPanel(false)} style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '24px', cursor: 'pointer', padding: '0 8px' }}>×</button>
          </div>

          {/* Content */}
          <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
            <BetslipModal
              show={false}
              onHide={() => {}}
              betslipItems={betslipItems}
              onRemoveBet={handleRemoveBet}
              onClearAll={handleClearBetslip}
              isDarkMode={true}
              getTeamLogoUrl={getTeamLogoUrlForSport}
              renderInline={true}
              showBetslipIframe={showBetslipIframe}
              onToggleBetFinder={() => setShowBetslipIframe(prev => !prev)}
              selectedSport={selectedSport}
              onPlayerClick={(bet) => handlePlayerClick(bet.gameId, bet.playerId, bet.playerName, bet.market, bet.awayTeam, bet.homeTeam, bet.team, null, bet.line)}
            />
          </div>
        </div>
      )}

      {/* Mobile Bottom Menu Bar — Best Bets in the middle, scratchpad on the
          left, betslip on the right. Replaces the floating FABs on mobile
          so nothing gets stuck behind lingering modal DOM. */}
      {isMobile && !analyticsMode && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: '#343a40',
          borderTop: '1px solid #495057',
          zIndex: 1000,
          padding: '8px 12px calc(8px + env(safe-area-inset-bottom))',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '8px'
        }}>
          {/* Scratchpad button (left) */}
          <button
            onClick={() => {
              setShowScratchpadPanel(prev => {
                const next = !prev;
                if (next) setActiveRightPanel('scratchpad');
                return next;
              });
            }}
            style={{
              position: 'relative',
              width: '44px', height: '44px',
              borderRadius: '50%',
              background: '#0d6efd',
              opacity: showScratchpadPanel ? 1 : 0.9,
              border: 'none',
              color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
              flex: '0 0 auto'
            }}
            title="Scratchpad"
            aria-label="Open scratchpad"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="9" y1="13" x2="15" y2="13"/>
              <line x1="9" y1="17" x2="13" y2="17"/>
            </svg>
            {scratchpadItems.length > 0 && (
              <span style={{
                position: 'absolute', top: '-4px', right: '-4px',
                background: '#dc3545', color: '#fff',
                fontSize: '0.65rem', fontWeight: 700,
                minWidth: '18px', height: '18px',
                borderRadius: '9px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '0 5px'
              }}>{scratchpadItems.length}</span>
            )}
          </button>

          {/* Best Bets button (center, flexible width) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: '1 1 auto', justifyContent: 'center', minWidth: 0 }}>
            <Button
              variant="success"
              size="sm"
              onClick={() => setShowBestBets(true)}
              className="fw-bold"
              style={{ width: '100%', maxWidth: '260px' }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                <span style={{ marginRight: '4px' }}>⭐</span>
                Best Bets ({bestBetsData.length})
                {isDevModeEnabled('allbestbets') && (
                  <span style={{ color: '#ff6b6b', fontSize: '10px', marginLeft: '4px' }}>ALL</span>
                )}
              </span>
            </Button>
            <button
              onClick={() => setShowBestBetsFaq(true)}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#94a3b8'
              }}
              title="Best Bets FAQ"
              aria-label="Best Bets FAQ"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </button>
          </div>

          {/* Betslip button (right) */}
          <button
            onClick={() => {
              const next = !showBetslipPanel;
              setShowBetslipPanel(next);
              if (next) setActiveRightPanel('betslip');
              setShowMobileTrends(false);
            }}
            style={{
              position: 'relative',
              width: '44px', height: '44px',
              borderRadius: '50%',
              background: '#0d6efd',
              opacity: showBetslipPanel ? 1 : 0.9,
              border: 'none',
              color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
              flex: '0 0 auto'
            }}
            title="Betslip"
            aria-label="Open betslip"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16v4.5c-1.1 0-2 .9-2 2s.9 2 2 2V20H4v-7.5c1.1 0 2-.9 2-2s-.9-2-2-2V4z"/>
            </svg>
            {betslipItems.length > 0 && (
              <span style={{
                position: 'absolute', top: '-4px', right: '-4px',
                background: '#dc3545', color: '#fff',
                fontSize: '0.65rem', fontWeight: 700,
                minWidth: '18px', height: '18px',
                borderRadius: '9px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '0 5px'
              }}>{betslipItems.length}</span>
            )}
          </button>
        </div>
      )}

      {/* Mobile Sport Selection Menu */}
      {isMobile && showMobileMenu && (
        <div className="mobile-menu-container" style={{
          position: 'fixed',
          top: '60px',
          left: '10px',
          backgroundColor: '#343a40',
          border: '1px solid #495057',
          borderRadius: '0 8px 8px 8px',
          zIndex: 1001,
          padding: '15px',
          minWidth: '200px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <div className="text-white mb-3" style={{ fontSize: '14px', fontWeight: 'bold' }}>
            Select Sport
          </div>
          <div className="d-flex flex-column gap-2">
            {/* Mobile sport buttons — order/labels driven by SPORTS_NAV. */}
            {SPORTS_NAV.map(({ key, label, clearAnalyticsOnSelect }) => (
              <Button
                key={key}
                variant={selectedSport === key ? 'warning' : 'outline-warning'}
                size="sm"
                onClick={() => {
                  setSelectedSport(key);
                  if (clearAnalyticsOnSelect && analyticsMode) setAnalyticsMode(false);
                  setShowMobileMenu(false);
                }}
                className="text-start"
              >
                {label}
              </Button>
            ))}

            {/* Logout button - only show if authenticated */}
            {isAuthenticated && (
              <>
                <hr style={{ margin: '12px 0', borderColor: '#495057' }} />
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => {
                    setShowMobileMenu(false);
                    logout();
                  }}
                  className="text-start"
                >
                  Log Out
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Best Bets Modal */}
      <BestBetsModal
        show={showBestBets}
        onHide={() => setShowBestBets(false)}
        bestBets={bestBetsData}
        getTeamLogoUrl={getTeamLogoUrlForSport}
        selectedSport={selectedSport}
      />

      {/* Best Bets FAQ Modal */}
      <BestBetsFaqModal show={showBestBetsFaq} onHide={() => setShowBestBetsFaq(false)} />

      {/* Player Modal - Use sport-specific modal for NFL/NHL/NBA, generic PlayerModal for other sports */}
      {selectedSport === 'nfl' ? (
        <NFLPlayerModal
          show={showPlayerModal}
          onHide={handleClosePlayerModal}
          playerName={playerModalData.playerName}
          playerTeam={playerModalData.playerTeam}
          playerPosition={playerModalData.playerPosition}
          playerId={playerModalData.playerId}
          gameId={playerModalData.gameId}
          market={playerModalData.market}
          awayTeam={playerModalData.awayTeam}
          homeTeam={playerModalData.homeTeam}
          getTeamLogoUrl={getTeamLogoUrlForSport}
          getBookLogoUrl={getBookLogoUrl}
          onOddsClick={handleOddsClick}
          onRemoveBet={handleRemoveBet}
          betslipItems={betslipItems}
          bookmakers={getEnabledBooks()}
          recalcEdgePlayer={recalcEdgePlayer}
        />
      ) : selectedSport === 'nhl' ? (
        <NHLPlayerModal
          show={showPlayerModal}
          onHide={handleClosePlayerModal}
          playerName={playerModalData.playerName}
          playerTeam={playerModalData.playerTeam}
          playerPosition={playerModalData.playerPosition}
          playerId={playerModalData.playerId}
          gameId={playerModalData.gameId}
          market={playerModalData.market}
          point={playerModalData.point}
          awayTeam={playerModalData.awayTeam}
          homeTeam={playerModalData.homeTeam}
          getTeamLogoUrl={getTeamLogoUrlForSport}
          getBookLogoUrl={getBookLogoUrl}
          onOddsClick={handleOddsClick}
          betslipItems={betslipItems}
          onRemoveBet={handleRemoveBet}
          bookmakers={getEnabledBooks()}
          recalcEdgePlayer={recalcEdgePlayer}
        />
      ) : selectedSport === 'nba' ? (
        <NBAPlayerModal
          show={showPlayerModal}
          onHide={handleClosePlayerModal}
          playerName={playerModalData.playerName}
          playerTeam={playerModalData.playerTeam}
          playerPosition={playerModalData.playerPosition}
          playerId={playerModalData.playerId}
          gameId={playerModalData.gameId}
          game={games.find(g => g.gameIDmlb === playerModalData.gameId)}
          getTeamLogoUrl={getTeamLogoUrlForSport}
          getBookLogoUrl={getBookLogoUrl}
          onOddsClick={handleOddsClick}
          betslipCount={betslipItems.length}
          betslipItems={betslipItems}
          onRemoveBet={handleRemoveBet}
          bookmakers={getEnabledBooks()}
          initialMarket={(() => {
            // Convert API market key to modal category name
            const marketKey = playerModalData.market;
            if (!marketKey) return 'points';

            const marketMap = {
              'player_points': 'points',
              'player_rebounds': 'rebounds',
              'player_assists': 'assists',
              'player_threes': 'threes',
              'player_points_rebounds_assists': 'pra',
              'player_points_rebounds': 'points_rebounds',
              'player_points_assists': 'points_assists',
              'player_rebounds_assists': 'rebounds_assists',
              'player_double_double': 'double_double',
              'player_triple_double': 'triple_double',
              'player_first_basket': 'first_basket'
            };

            return marketMap[marketKey] || 'points';
          })()}
          recalcEdgePlayer={recalcEdgePlayer}
        />
      ) : selectedSport === 'wnba' ? (
        <WNBAPlayerModal
          show={showPlayerModal}
          onHide={handleClosePlayerModal}
          playerName={playerModalData.playerName}
          playerTeam={playerModalData.playerTeam}
          playerPosition={playerModalData.playerPosition}
          playerId={playerModalData.playerId}
          gameId={playerModalData.gameId}
          game={games.find(g => g.gameIDmlb === playerModalData.gameId)}
          getTeamLogoUrl={getTeamLogoUrlForSport}
          getBookLogoUrl={getBookLogoUrl}
          onOddsClick={handleOddsClick}
          betslipCount={betslipItems.length}
          betslipItems={betslipItems}
          onRemoveBet={handleRemoveBet}
          bookmakers={getEnabledBooks()}
          initialMarket={(() => {
            const marketKey = playerModalData.market;
            if (!marketKey) return 'points';

            const marketMap = {
              'player_points': 'points',
              'player_rebounds': 'rebounds',
              'player_assists': 'assists',
              'player_threes': 'threes',
              'player_points_rebounds_assists': 'pra',
              'player_points_rebounds': 'points_rebounds',
              'player_points_assists': 'points_assists',
              'player_rebounds_assists': 'rebounds_assists',
              'player_double_double': 'double_double',
              'player_triple_double': 'triple_double',
              'player_first_basket': 'first_basket'
            };

            return marketMap[marketKey] || 'points';
          })()}
          recalcEdgePlayer={recalcEdgePlayer}
        />
      ) : selectedSport === 'soccer' ? (
        <SoccerPlayerModal
          show={showPlayerModal}
          onHide={handleClosePlayerModal}
          playerName={playerModalData.playerName}
          playerTeam={playerModalData.playerTeam}
          playerPosition={playerModalData.playerPosition}
          playerId={playerModalData.playerId}
          gameId={playerModalData.gameId}
          game={games.find(g => g.gameIDmlb === playerModalData.gameId)}
          market={playerModalData.market}
          point={playerModalData.point}
          getTeamLogoUrl={getTeamLogoUrlForSport}
          getBookLogoUrl={getBookLogoUrl}
          onOddsClick={handleOddsClick}
          onRemoveBet={handleRemoveBet}
          betslipItems={betslipItems}
          bookmakers={getEnabledBooks()}
        />
      ) : (
        <PlayerModal
          show={showPlayerModal}
          onHide={handleClosePlayerModal}
          gameId={playerModalData.gameId}
          playerId={playerModalData.playerId}
          playerName={playerModalData.playerName}
          market={playerModalData.market}
          point={playerModalData.point}
          awayTeam={playerModalData.awayTeam}
          homeTeam={playerModalData.homeTeam}
          getTeamLogoUrl={getTeamLogoUrlForSport}
          getBookLogoUrl={getBookLogoUrl}
          sport={selectedSport}
          game={filteredGamesForRender.find(g => g.gameIDmlb === playerModalData.gameId)}
          opposingPitcher={playerModalData.opposingPitcher}
          bookmakers={getEnabledBooks()}
          recalcEdgePlayer={recalcEdgePlayer}
        />
      )}

      {/* Mobile betslip toast — visual confirmation when an odds cell is
          tapped. Bottom-center, slides up, auto-dismisses after ~2s. */}
      {isMobile && betslipToast && (
        <div
          role="status"
          aria-live="polite"
          onClick={() => setBetslipToast(null)}
          style={{
            position: 'fixed',
            left: '50%',
            bottom: '84px',
            transform: 'translateX(-50%)',
            zIndex: 3000,
            minWidth: '220px',
            maxWidth: 'calc(100vw - 32px)',
            padding: '10px 14px',
            borderRadius: '10px',
            background: betslipToast.kind === 'dup' ? 'rgba(75, 85, 99, 0.95)' : 'rgba(16, 185, 129, 0.95)',
            color: '#fff',
            boxShadow: '0 6px 24px rgba(0, 0, 0, 0.35)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            animation: 'toastFadeUp 0.2s ease-out',
            cursor: 'pointer',
          }}
        >
          <span style={{ fontSize: '1.05rem', lineHeight: 1 }} aria-hidden="true">
            {betslipToast.kind === 'dup' ? '↺' : '✓'}
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <span style={{ fontWeight: 700, fontSize: '0.85rem' }}>{betslipToast.message}</span>
            {betslipToast.subtitle && (
              <span style={{ fontSize: '0.75rem', opacity: 0.9, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {betslipToast.subtitle}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Debug Overlay - Development Only */}
      <DebugOverlay
        debugStats={debugStats}
        authInfo={{
          isAuthenticated,
          user,
          hasActiveSubscription
        }}
      />
      
      {/* Settings Takeover */}
      <SettingsTakeover
        show={showSettings}
        onHide={() => setShowSettings(false)}
        onShowCheckout={(plan) => {
          setSelectedPlan(plan);
          setShowCheckout(true);
        }}
        checkoutPlans={availablePlans}
        topOffset={marketBannerBottom}
      />

      {/* Lightbox for screenshots */}
      {lightboxOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => setLightboxOpen(false)}
        >
          {/* Close button */}
          <button
            onClick={() => setLightboxOpen(false)}
            style={{
              position: 'absolute',
              top: '20px',
              right: '40px',
              fontSize: '40px',
              color: 'white',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '10px',
              lineHeight: 1,
              transition: 'color 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.color = '#f59e0b'}
            onMouseLeave={(e) => e.target.style.color = 'white'}
          >
            ×
          </button>

          {/* Previous button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setLightboxImageIndex((prev) => (prev === 0 ? 8 : prev - 1));
            }}
            style={{
              position: 'absolute',
              left: '20px',
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '60px',
              color: 'white',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '20px',
              userSelect: 'none',
              transition: 'color 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.color = '#f59e0b'}
            onMouseLeave={(e) => e.target.style.color = 'white'}
          >
            ‹
          </button>

          {/* Image */}
          <img
            src={`/images/screenshot${lightboxImageIndex + 1}.jpg`}
            alt={`Screenshot ${lightboxImageIndex + 1}`}
            style={{
              maxWidth: '90%',
              maxHeight: '90%',
              objectFit: 'contain'
            }}
            onClick={(e) => e.stopPropagation()}
          />

          {/* Next button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setLightboxImageIndex((prev) => (prev === 8 ? 0 : prev + 1));
            }}
            style={{
              position: 'absolute',
              right: '20px',
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '60px',
              color: 'white',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '20px',
              userSelect: 'none',
              transition: 'color 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.color = '#f59e0b'}
            onMouseLeave={(e) => e.target.style.color = 'white'}
          >
            ›
          </button>
        </div>
      )}
    </div>

      {/* Footer */}
      <footer style={{
        background: '#0f172a',
        borderTop: '1px solid #334155',
        padding: isMobile ? '20px 16px' : '24px 40px',
        marginTop: '40px',
        textAlign: 'center'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'center',
          alignItems: 'center',
          gap: isMobile ? '12px' : '24px',
          marginBottom: '16px',
          flexWrap: 'wrap'
        }}>
          <a
            href="/lets-go"
            style={{
              color: '#94a3b8',
              textDecoration: 'none',
              fontSize: '14px',
              transition: 'color 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.color = '#3b82f6'}
            onMouseLeave={(e) => e.target.style.color = '#94a3b8'}
          >
            Get Started
          </a>
          <a
            href="/terms-of-service"
            style={{
              color: '#94a3b8',
              textDecoration: 'none',
              fontSize: '14px',
              transition: 'color 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.color = '#3b82f6'}
            onMouseLeave={(e) => e.target.style.color = '#94a3b8'}
          >
            Terms of Service
          </a>
          <a
            href="/privacy-policy"
            style={{
              color: '#94a3b8',
              textDecoration: 'none',
              fontSize: '14px',
              transition: 'color 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.color = '#3b82f6'}
            onMouseLeave={(e) => e.target.style.color = '#94a3b8'}
          >
            Privacy Policy
          </a>
          <a
            href="/faq"
            style={{
              color: '#94a3b8',
              textDecoration: 'none',
              fontSize: '14px',
              transition: 'color 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.color = '#3b82f6'}
            onMouseLeave={(e) => e.target.style.color = '#94a3b8'}
          >
            FAQ
          </a>
          <a
            href="/whats-new"
            style={{
              color: '#94a3b8',
              textDecoration: 'none',
              fontSize: '14px',
              transition: 'color 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.color = '#3b82f6'}
            onMouseLeave={(e) => e.target.style.color = '#94a3b8'}
          >
            What's New
          </a>
        </div>

        <div style={{
          fontSize: '12px',
          color: '#64748b',
          marginBottom: '12px'
        }}>
          © {new Date().getFullYear()} SimpleOdds. All rights reserved.
        </div>

        <div style={{
          fontSize: '11px',
          color: '#64748b',
          lineHeight: '1.6',
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          <strong style={{ color: '#94a3b8' }}>18+ Only. Gamble Responsibly.</strong>
          <br />
          SimpleOdds is for informational and entertainment purposes only. We do not accept bets or facilitate gambling.
          <br />
          If you have a gambling problem, call 1-800-522-4700.
        </div>
      </footer>

      {/* Legal Document Modal */}
      <LegalDocument
        show={legalDocument !== null}
        onHide={() => setLegalDocument(null)}
        document={legalDocument}
      />

      {/* Contact Modal */}
      <ContactModal
        show={showContactModal}
        onHide={() => setShowContactModal(false)}
      />

      {/* Announcements Modal */}
      <AnnouncementsModal
        show={showAnnouncementsModal}
        onHide={() => setShowAnnouncementsModal(false)}
        announcements={announcements}
        isDarkMode={isDarkMode}
      />

      {/* Betslip / Scratchpad Floating Buttons — desktop only. On mobile
          these live inside the bottom bar next to the Best Bets button so
          they can't get intercepted by leftover modal DOM. */}
      {!isMobile && betslipItems.length > 0 && (
        <BetslipFloatingButton
          betCount={betslipItems.length}
          onClick={() => {
            const next = !showBetslipPanel;
            setShowBetslipPanel(next);
            if (next) setActiveRightPanel('betslip');
            setShowMobileTrends(false);
          }}
          isDarkMode={isDarkMode}
        />
      )}

      {!isMobile && (
        <ScratchpadFloatingButton
          count={scratchpadItems.length}
          onClick={() => {
            setShowScratchpadPanel(prev => {
              const next = !prev;
              if (next) setActiveRightPanel('scratchpad');
              return next;
            });
          }}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Betslip Modal */}
      <BetslipModal
        show={showBetslipModal}
        onHide={() => setShowBetslipModal(false)}
        betslipItems={betslipItems}
        onRemoveBet={handleRemoveBet}
        onClearAll={handleClearBetslip}
        isDarkMode={isDarkMode}
        getTeamLogoUrl={getTeamLogoUrlForSport}
        selectedSport={selectedSport}
        onPlayerClick={(bet) => handlePlayerClick(bet.gameId, bet.playerId, bet.playerName, bet.market, bet.awayTeam, bet.homeTeam, bet.team, null, bet.line)}
      />

      {/* Player Context Menu */}
      <PlayerContextMenu
        visible={contextMenu.visible}
        x={contextMenu.x}
        y={contextMenu.y}
        playerData={contextMenu.playerData}
        onAddToBetslip={handleContextMenuAddToBetslip}
        onToggleScratchpad={handleToggleScratchpad}
        isInScratchpad={isInScratchpad}
        onClose={() => setContextMenu(prev => ({ ...prev, visible: false }))}
      />

      {/* Scratchpad Panel — when Betslip is also open, only render when Scratchpad is the active tab. */}
      {showScratchpadPanel && (!showBetslipPanel || activeRightPanel === 'scratchpad') && (
        <ScratchpadPanel
          items={scratchpadItems}
          onRemove={handleRemoveFromScratchpad}
          onClearAll={() => setScratchpadItems([])}
          onClearDate={handleClearScratchpadDate}
          onClose={() => setShowScratchpadPanel(false)}
          getTeamLogoUrl={getTeamLogoUrlForSport}
          isMobile={isMobile}
          marketBannerBottom={marketBannerBottom}
          onPlayerClick={(item) => handlePlayerClick(item.gameId, item.playerId, item.name, masterMarket, undefined, undefined, item.teamAbbr, null, null)}
          betslipOpen={showBetslipPanel}
          betslipCount={betslipItems.length}
          onSwitchToBetslip={() => setActiveRightPanel('betslip')}
          filterEnabled={scratchpadFilterEnabled}
          onToggleFilter={setScratchpadFilterEnabled}
        />
      )}


    </AuthWrapper>
  );
}

export default App;